import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API, TOKEN_KEY, useAuth } from '../contexts/AuthContext';
import InfoFluxoAtendimento from '../components/InfoFluxoAtendimento';
import BannerAtivarAvisos from '../components/BannerAtivarAvisos';
import RespostasRapidas from '../components/RespostasRapidas';
import ChatInternoModal from '../components/ChatInternoModal';
import ConsultaClienteModal from '../components/ConsultaClienteModal';
import ConsultaProdutoModal from '../components/ConsultaProdutoModal';
import EnviarOrcamentoModal from '../components/EnviarOrcamentoModal';
import HistoricoClienteModal from '../components/HistoricoClienteModal';
import MenuTransferir from '../components/MenuTransferir';
import usePresenca from '../hooks/usePresenca';
import { MidiaRenderer } from '../components/MidiaMensagem';
import { playNotificationSound, unlockNotificationSound } from '../helpers/notificationSound';
import { usePrefs, getPrefs, somConfigDe, deveVibrar } from '../helpers/prefs';
import { resolverVariaveis } from '../helpers/respostasRapidas';
import {
  segundosEmJanela,
  dentroDaJanela,
  formatarCronometro,
} from '../helpers/janelaAtendimento';
import { alertarErro } from '../helpers/alerts';
import {
  faixaDoToque, usePosicaoAcoes, emojisPorGrupo, useEmojisRapidos, reagirEAnotar,
  TextoDaMensagem,
} from '../components/ChatShared';
import { dddDe, formatarTelefone } from '../helpers/numerosNoTexto';
import EditorImagem, { daPraEditar } from '../components/EditorImagem';
import { criarFilaDeEnvio } from '../helpers/filaDeEnvio';
import { reconciliarPendentes } from '../helpers/pendentes';
import { fecharNotificacoesDeConversas } from '../push/push';
import { TEMPO_DE_POLL_MS, telaVisivel } from '../helpers/rede';
import { baixarDaUrl, noApp } from '../helpers/appNativo';

// Voltar arrastando da borda esquerda, como o iPhone faz. A faixa é estreita
// de propósito: mais larga que isto e o gesto rouba o swipe-to-reply das
// mensagens que ficam encostadas na esquerda.
// Tipos que a entrada "Documento" declara. A lista existe pra o seletor do
// Android abrir os ARQUIVOS e não as fotos — ver o comentário do input.
//
// Tem extensão E mime type de propósito: fabricante que ignora um costuma
// respeitar o outro, e o Android converte extensão em mime quando precisa.
// `image/*` entra no fim porque mandar uma foto que está em Downloads (print,
// comprovante que chegou por e-mail) é caso real, e sem isso ela apareceria
// apagada no seletor de arquivos.
const ACEITA_DOCUMENTO = [
  '.pdf', 'application/pdf',
  '.doc', '.docx', 'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xls', '.xlsx', 'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.csv', 'text/csv', '.txt', 'text/plain',
  '.zip', 'application/zip',
  'image/*',
].join(',');

const ZONA_BORDA = 22;      // onde o dedo precisa começar
const BORDA_DISPARA = 90;   // quanto precisa andar pra confirmar a saída

function formatHora(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const hoje = new Date();
  const mesmoDia = d.toDateString() === hoje.toDateString();
  if (mesmoDia) {
    return hora;
  }
  const data = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  return `${data} ${hora}`;
}

function formatHoraCompleta(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

// Contador de espera: quanto tempo o cliente esta aguardando resposta.
function formatEspera(ms) {
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}min`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm ? `${h}h${rm}min` : `${h}h`;
}

// Cor de urgencia: quanto mais tempo esperando, mais quente. <1min neutro,
// 1-5min laranja, >5min vermelho.
function corEspera(ms) {
  const min = ms / 60000;
  if (min >= 5) return '#E5484D';
  if (min >= 1) return '#F5A623';
  return '#5E7A82';
}

function formatNumero(numero) {
  if (!numero) return '';
  const limpo = String(numero).replace(/\D/g, '');
  if (limpo.length === 13) {
    return `+${limpo.slice(0, 2)} (${limpo.slice(2, 4)}) ${limpo.slice(4, 9)}-${limpo.slice(9)}`;
  }
  if (limpo.length === 12) {
    return `+${limpo.slice(0, 2)} (${limpo.slice(2, 4)}) ${limpo.slice(4, 8)}-${limpo.slice(8)}`;
  }
  return numero;
}

// Preview curta da mensagem fixada, pra faixa no topo da conversa. Texto sai
// como está (o CSS trunca com ellipsis); mídia vira o rótulo do tipo, porque
// meter só o nome do arquivo (que pode ser "documento.pdf") não diz o que a
// pessoa fixou.
function previewMsgFixada(conversa) {
  if (!conversa) return '';
  const tipo = conversa.msgfix_tipo;
  const conteudo = String(conversa.msgfix_conteudo || '').trim();
  const rotulos = {
    imagem: 'Foto',
    video: 'Vídeo',
    audio: 'Áudio',
    documento: 'Documento',
    figurinha: 'Figurinha',
    contato: 'Contato',
    localizacao: 'Localização',
  };
  if (rotulos[tipo]) {
    return conteudo ? `${rotulos[tipo]}: ${conteudo}` : rotulos[tipo];
  }
  return conteudo || '(mensagem)';
}

function iniciaisDe(nome, numero) {
  const base = (nome || '').trim();
  if (base) {
    const partes = base.split(/\s+/).slice(0, 2);
    return partes.map((p) => p[0]?.toUpperCase()).join('');
  }
  return numero ? String(numero).slice(-2) : '?';
}

// Avatar do contato: mostra a foto do WhatsApp se existir; senão, as iniciais.
// Se a foto falhar ao carregar (URL expirada), cai nas iniciais também.
function Avatar({ nome, numero, fotoUrl, size = 42 }) {
  const [erro, setErro] = useState(false);
  const mostrarFoto = fotoUrl && !erro;
  return (
    <div
      style={{
        width: size, height: size, borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
        background: 'linear-gradient(135deg, #3D8B37 0%, #2E6A29 100%)',
        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 600, fontSize: size <= 40 ? '0.85rem' : '0.9rem',
      }}
    >
      {mostrarFoto ? (
        <img
          src={fotoUrl}
          alt=""
          onError={() => setErro(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : iniciaisDe(nome, numero)}
    </div>
  );
}

function MensagemStatus({ status }) {
  if (!status || status === 'erro') return null;

  // Enviando: relógio (igual WhatsApp), antes do servidor confirmar.
  if (status === 'enviando') {
    return (
      <i
        className="bi bi-clock"
        title="Enviando"
        aria-label="Enviando"
        style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', lineHeight: 1 }}
      />
    );
  }

  const lida = status === 'lida';
  const entregue = status === 'entregue';
  const icon = entregue || lida ? 'bi-check2-all' : 'bi-check2';
  const label = lida ? 'Lida' : entregue ? 'Entregue' : 'Enviada';

  return (
    <i
      className={`bi ${icon}`}
      title={label}
      aria-label={label}
      style={{
        color: lida ? '#22e3ff' : 'rgba(255,255,255,0.78)',
        fontSize: '1.05rem',
        lineHeight: 1,
      }}
    />
  );
}

function QuotedBlock({ mensagem, contatoNome, contatoNumero, onIr }) {
  const tipo = mensagem.quoted_tipo || 'texto';
  const iconeMap = {
    imagem: 'bi-image', audio: 'bi-mic-fill', video: 'bi-camera-video-fill',
    documento: 'bi-file-earmark', figurinha: 'bi-emoji-smile',
  };
  const icone = iconeMap[tipo];
  // Preview: usa o conteúdo linkado; se não temos a original (só o preview do
  // webhook), cai no quoted_preview. Assim o bloco citado sempre aparece.
  const preview = mensagem.quoted_conteudo
    ? String(mensagem.quoted_conteudo).slice(0, 120)
    : (mensagem.quoted_preview ? String(mensagem.quoted_preview).slice(0, 120) : `[${tipo}]`);
  // Só sabemos o remetente quando a original está linkada (quoted_direcao).
  const remetente = mensagem.quoted_direcao === 'out'
    ? (mensagem.quoted_autor || 'atendente')
    : (mensagem.quoted_direcao === 'in'
      ? (contatoNome || (contatoNumero ? formatNumero(contatoNumero) : null) || 'cliente')
      : null);
  // Tocar na citação leva até a mensagem original, como no WhatsApp. Só faz
  // sentido quando ela está LINKADA: quando só temos o preview do webhook, não
  // existe original nossa pra onde ir.
  const podeIr = Boolean(mensagem.quoted_id && onIr);
  return (
    <div
      onClick={podeIr ? (e) => { e.stopPropagation(); onIr(mensagem.quoted_id); } : undefined}
      role={podeIr ? 'button' : undefined}
      title={podeIr ? 'Ir até a mensagem citada' : undefined}
      style={{
        borderLeft: '3px solid rgba(255,255,255,0.45)',
        padding: '3px 8px',
        marginBottom: 6,
        borderRadius: '0 4px 4px 0',
        background: 'rgba(0,0,0,0.18)',
        fontSize: '0.78rem',
        cursor: podeIr ? 'pointer' : 'default',
      }}
    >
      {remetente && <div style={{ fontWeight: 700, opacity: 0.9, marginBottom: 1 }}>{remetente}</div>}
      <div style={{ opacity: 0.75, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {icone && tipo !== 'texto' && <i className={`bi ${icone} me-1`} />}
        {preview}
      </div>
    </div>
  );
}


// DESLIGADO DE PROPÓSITO (decisão do Lucas: "prefiro não ter foto do que ser
// banido"). Consultar foto de perfil é o que o WhatsApp limita e pune, e este
// era o único ponto que gerava consulta NOVA só porque alguém digitou na busca.
//
// As fotos continuam entrando, pelas fontes que não consultam nada:
//   - webhook contacts.update (o WhatsApp empurra; ~141/dia, URL fresca)
//   - ao ABRIR a conversa (1 consulta, com throttle de 24h — já existia)
// e agora cada foto obtida é BAIXADA e guardada, então não se perde mais.
//
// Pra religar: `AQUECER_FOTO_NA_BUSCA = true`. O endpoint /contatos/fotos
// continua existindo e já é sequencial, com teto de 5 e recuo em 2 vazios.
const AQUECER_FOTO_NA_BUSCA = false;

async function buscarFotosFaltando(lista, maximo = 5) {
  if (!AQUECER_FOTO_NA_BUSCA) return {};
  const ids = (lista || []).filter((c) => c && !c.profile_pic_url).slice(0, maximo).map((c) => c.id);
  if (!ids.length) return {};
  try {
    const r = await axios.post(`${API}/contatos/fotos`, { ids });
    const mapa = {};
    (r.data?.data || []).forEach((x) => { if (x.profile_pic_url) mapa[x.id] = x.profile_pic_url; });
    return mapa;
  } catch (e) {
    return {}; // best-effort: sem foto a tela mostra a inicial e segue
  }
}


// Seletor de contato da agenda (pra enviar um contato pro cliente).
function ContatoPickerModal({ aberto, onClose, onEscolher }) {
  const [busca, setBusca] = useState('');
  const [contatos, setContatos] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!aberto) return;
    setBusca('');
    setLoading(true);
    axios.get(`${API}/contatos`).then((r) => setContatos(r.data?.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [aberto]);
  const filtrados = useMemo(() => {
    if (!busca) return contatos;
    const q = busca.toLowerCase();
    return contatos.filter((c) => String(c.push_name || '').toLowerCase().includes(q) || String(c.numero || '').includes(q));
  }, [contatos, busca]);
  if (!aberto) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1300, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
      <div className="content-card" onClick={(e) => e.stopPropagation()} style={{ width: 'min(440px, 96vw)', height: 'min(78vh, 560px)', display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}>
        <header style={{ padding: '12px 14px', borderBottom: '1px solid var(--portal-border)', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <i className="bi bi-person-lines-fill" style={{ color: '#3D8B37' }} />
          <strong style={{ flex: 1 }}>Enviar contato</strong>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}><i className="bi bi-x-lg" /></button>
        </header>
        <div style={{ padding: '10px 14px', flexShrink: 0 }}>
          <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar na agenda..." autoFocus
            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--portal-border)', background: 'var(--portal-surface)', color: 'var(--portal-text)', fontSize: '0.9rem' }} />
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: 16, color: 'var(--portal-muted)', fontSize: '0.85rem' }}>Carregando...</div>
          ) : filtrados.length === 0 ? (
            <div style={{ padding: 16, color: 'var(--portal-muted)', fontSize: '0.85rem' }}>Nenhum contato encontrado.</div>
          ) : filtrados.map((c) => (
            <button key={c.id} type="button" onClick={() => onEscolher(c)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '10px 14px', background: 'transparent', border: 'none', borderBottom: '1px solid var(--portal-border)', cursor: 'pointer', color: 'var(--portal-text)', textAlign: 'left' }}>
              <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'rgba(61,139,55,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className="bi bi-person-fill" style={{ color: '#3D8B37' }} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.push_name || formatNumero(c.numero)}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--portal-muted)' }}>{formatNumero(c.numero)}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}


const acaoBolhaStyle = {
  background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--portal-text)',
  fontSize: '0.9rem', lineHeight: 1, padding: '4px 5px', borderRadius: 6,
};
const anexoItemStyle = {
  display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
  padding: '10px 14px', background: 'transparent', border: 'none', cursor: 'pointer',
  color: 'var(--portal-text)', fontSize: '0.9rem',
};

// Cartão de contato (vCard) — estilo WhatsApp.
function ContatoCard({ conteudo, out, onConversar, onSalvarNaAgenda, nomeNaAgenda }) {
  let d = {};
  try { d = JSON.parse(conteudo || '{}'); } catch (_) { d = {}; }
  const temNumero = Boolean(d.phoneNumber);
  const btn = {
    flex: 1, padding: '5px 8px', borderRadius: 6, cursor: 'pointer',
    fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap',
    background: out ? 'rgba(255,255,255,0.2)' : 'rgba(61, 139, 55, 0.12)',
    color: out ? '#fff' : '#2E6A29',
    border: out ? 'none' : '1px solid rgba(61, 139, 55, 0.4)',
  };
  return (
    <div style={{ minWidth: 200 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '2px 0' }}>
        <div style={{
          width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
          background: out ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.08)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <i className="bi bi-person-fill" style={{ fontSize: '1.2rem' }} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600 }}>{d.fullName || 'Contato'}</div>
          {temNumero && <div style={{ fontSize: '0.8rem', opacity: 0.85 }}>{formatNumero(d.phoneNumber)}</div>}
        </div>
      </div>
      {/* Igual ao WhatsApp: dá pra falar com esse número ou guardar na agenda
          sem sair da conversa. Já salvo = mostra de quem é, sem oferecer salvar
          de novo. `undefined` = ainda checando (não pisca botão à toa). */}
      {temNumero && (onConversar || onSalvarNaAgenda) && (
        <div style={{
          display: 'flex', gap: 6, marginTop: 6, paddingTop: 6, alignItems: 'center',
          borderTop: `1px solid ${out ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.1)'}`,
        }}>
          {onConversar && (
            <button type="button" style={btn} onClick={() => onConversar(d.phoneNumber, d.fullName)}>
              <i className="bi bi-chat-dots me-1" />Conversar
            </button>
          )}
          {nomeNaAgenda ? (
            <span
              title={`Já na agenda como "${nomeNaAgenda}"`}
              style={{
                flex: 1, fontSize: '0.74rem', opacity: 0.85, display: 'inline-flex',
                alignItems: 'center', gap: 4, justifyContent: 'center',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              <i className="bi bi-person-check-fill" />na agenda
            </span>
          ) : (nomeNaAgenda === null && onSalvarNaAgenda && (
            <button type="button" style={btn} onClick={() => onSalvarNaAgenda(d.phoneNumber, d.fullName)}>
              <i className="bi bi-person-plus me-1" />Salvar
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Cartão de localização — abre no Google Maps.
function LocalizacaoCard({ conteudo, out }) {
  let d = {};
  try { d = JSON.parse(conteudo || '{}'); } catch (_) { d = {}; }
  const url = `https://www.google.com/maps?q=${d.latitude},${d.longitude}`;
  return (
    <a href={url} target="_blank" rel="noreferrer" style={{ display: 'block', textDecoration: 'none', color: 'inherit', minWidth: 180 }}>
      <div style={{
        height: 88, borderRadius: 8, marginBottom: 4,
        background: out ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.06)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <i className="bi bi-geo-alt-fill" style={{ fontSize: '1.8rem' }} />
      </div>
      <div style={{ fontWeight: 600 }}>{d.name || 'Localização'}</div>
      {d.address && <div style={{ fontSize: '0.8rem', opacity: 0.85 }}>{d.address}</div>}
      <div style={{ fontSize: '0.78rem', opacity: 0.9, marginTop: 2 }}>
        <i className="bi bi-box-arrow-up-right me-1" />Abrir no mapa
      </div>
    </a>
  );
}

// Menu de ações no MOBILE/TABLET: overlay full-screen igual WhatsApp — escurece
// a tela, mostra a barra de reações + o menu, e some ao tocar fora. Um só por
// vez (estado no pai), então não empilha.
// Gêmeo do AcoesMensagemMobile do ChatShared, com "Encaminhar" a mais — que só
// existe na conversa 1:1. A CONTA de onde ele abre é a mesma nos dois, e mora
// num lugar só (`usePosicaoAcoes`): duas cópias dessa lógica já custaram um
// conserto que valeu só pros Grupos.
function AcoesMensagemMobile({ mensagem, ancora, onFechar, onReagir, onResponder, onEditar, onApagar, onCopiar, onEncaminhar, onFixar, onDesafixar, ehFixada }) {
  const [maisAberto, setMaisAberto] = useState(false);
  const rapidos = useEmojisRapidos();
  const { reacoesRef, menuRef, pos, ancoragem } = usePosicaoAcoes(mensagem, ancora, maisAberto);

  if (!mensagem) return null;
  const out = mensagem.direcao === 'out';
  const item = (icon, label, onClick, danger) => (
    <button type="button" onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 14, width: '100%', textAlign: 'left',
      padding: '13px 18px', background: 'transparent', border: 'none', cursor: 'pointer',
      color: danger ? '#e5484d' : 'var(--portal-text)', fontSize: '1rem',
      borderBottom: '1px solid var(--portal-border)',
    }}><i className={`bi ${icon}`} style={{ fontSize: '1.1rem', width: 22 }} />{label}</button>
  );
  return (
    <div
      onClick={onFechar}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)',
      }}
    >
      {/* COLUNA, não linha: com a régua e o painel lado a lado num flex-wrap, o
          painel virava item de flex e itens de flex crescem em vez de rolar
          (min-height:auto). O `overflow` existia e não funcionava. */}
      <div ref={reacoesRef} onClick={(e) => e.stopPropagation()} style={{ ...ancoragem(pos?.reacoes ?? 0, out), display: 'flex', flexDirection: 'column', maxWidth: 'min(92vw, 340px)', background: 'var(--portal-surface)', borderRadius: maisAberto ? 14 : 28, padding: '8px 12px', boxShadow: '0 6px 20px rgba(0,0,0,0.35)' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {rapidos.map((e) => (
          <button key={e} type="button" onClick={() => { reagirEAnotar(onReagir, mensagem, e); onFechar(); }}
            style={{ background: mensagem.reacao === e ? 'rgba(61,139,55,0.18)' : 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.7rem', lineHeight: 1, padding: '4px 6px', borderRadius: '50%' }}>{e}</button>
        ))}
        {!maisAberto && (
          <button
            type="button"
            onClick={() => setMaisAberto(true)}
            title="Todas as reações"
            aria-label="Todas as reações"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.4rem', lineHeight: 1, padding: '4px 8px', borderRadius: '50%', color: 'var(--portal-muted)' }}
          >
            +
          </button>
        )}
        </div>
        {/* Painel com TODOS: rola dentro de si, com teto de altura, pra não
            empurrar o menu de ações pra fora da tela num celular deitado.
            `minHeight: 0` é o que destrava a rolagem dentro de um flex. */}
        {maisAberto && (
          <div style={{
            width: 'min(88vw, 300px)', maxHeight: '40vh', minHeight: 0,
            overflowY: 'auto', WebkitOverflowScrolling: 'touch', touchAction: 'pan-y',
            overscrollBehavior: 'contain',
            marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--portal-border)',
          }}>
            {emojisPorGrupo().map((g) => (
              <div key={g.titulo}>
                <div style={{
                  position: 'sticky', top: 0, background: 'var(--portal-surface)',
                  fontSize: '0.68rem', fontWeight: 700, color: 'var(--portal-muted)',
                  padding: '4px 2px', textTransform: 'uppercase', letterSpacing: 0.5,
                }}>{g.titulo}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                  {g.itens.map((e) => (
                    <button key={e} type="button"
                      onClick={() => { reagirEAnotar(onReagir, mensagem, e); onFechar(); }}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.5rem', lineHeight: 1.1, padding: '2px 4px', borderRadius: '50%' }}>{e}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div ref={menuRef} onClick={(e) => e.stopPropagation()} style={{ ...ancoragem(pos?.menu ?? 0, out), background: 'var(--portal-surface)', borderRadius: 14, overflow: 'hidden', minWidth: 220, boxShadow: '0 6px 20px rgba(0,0,0,0.35)' }}>
        {mensagem.tipo !== 'sistema' && item('bi-reply-fill', 'Responder', () => { onResponder(mensagem); onFechar(); })}
        {onEncaminhar && mensagem.tipo !== 'sistema' && !mensagem.apagada && item('bi-forward-fill', 'Encaminhar', () => { onEncaminhar(mensagem); onFechar(); })}
        {mensagem.tipo !== 'sistema' && !mensagem.apagada && (ehFixada
          ? (onDesafixar && item('bi-pin-angle', 'Desafixar', () => { onDesafixar(); onFechar(); }))
          : (onFixar && item('bi-pin-angle-fill', 'Fixar', () => { onFixar(mensagem); onFechar(); }))
        )}
        {(mensagem.conteudo && !mensagem.apagada) && item('bi-copy', 'Copiar', () => { onCopiar(mensagem); onFechar(); })}
        {out && mensagem.tipo === 'texto' && !mensagem.apagada && item('bi-pencil', 'Editar', () => { onEditar(mensagem); onFechar(); })}
        {out && !mensagem.apagada && item('bi-trash', 'Apagar', () => { onApagar(mensagem); onFechar(); }, true)}
      </div>
    </div>
  );
}

function MessageBubble({
  mensagem, contatoNome, contatoNumero, onAbrirImagem, onResponder,
  onReagir, onApagar, onEditar, onEncaminhar, isMobile, onAbrirAcoes,
  onConversarContato, onSalvarContato, nomeNaAgenda,
  onIrParaMensagem, onTelefone,
  onFixar, onDesafixar, ehFixada,
}) {
  // DDD de quem está na conversa: serve de contexto pro número escrito sem ele
  // ("manda pro 99916-1234"), que é como se fala entre gente da mesma cidade.
  const dddDoContato = dddDe(contatoNumero);
  const [hovered, setHovered] = useState(false);
  const [emojisAbertos, setEmojisAbertos] = useState(false);
  // A mesma régua do toque longo, pra quem usa mouse: os emojis da PESSOA, não
  // os seis fixos. Duas réguas diferentes na mesma tela seria o pior dos dois.
  const rapidosDoMouse = useEmojisRapidos();
  const [editando, setEditando] = useState(false);
  const [textoEdit, setTextoEdit] = useState('');
  const [dragX, setDragX] = useState(0);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const longPressRef = useRef(null);
  const naBordaRef = useRef(false);

  // Mensagem interna (transferencia, ligacao, etc): nota central, nao e balao
  // de chat.
  if (mensagem.tipo === 'sistema') {
    // Ligação do cliente (ligacaoService): o id é "call:<id da ligação>". Pra
    // ela, a HORA é o dado principal ("ligou às 10:32") — nas outras notas não
    // faz falta, e continua sem.
    const ligacao = String(mensagem.evolution_id || '').startsWith('call:');
    return (
      <div style={{ display: 'flex', justifyContent: 'center', margin: '8px 0' }}>
        <div style={{
          maxWidth: '85%', textAlign: 'center',
          background: 'rgba(240,173,78,0.12)',
          border: '1px solid rgba(240,173,78,0.35)',
          color: 'var(--portal-text)',
          padding: '6px 12px', borderRadius: 10,
          fontSize: '0.78rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>
          <i className={`bi ${ligacao ? 'bi-telephone' : 'bi-arrow-left-right'} me-1`} style={{ color: '#f0ad4e' }} />
          {mensagem.conteudo}
          {ligacao && mensagem.criada_em && (
            <span style={{ color: 'var(--portal-muted)' }}> · {formatHora(mensagem.criada_em)}</span>
          )}
        </div>
      </div>
    );
  }

  const out = mensagem.direcao === 'out';
  const erro = mensagem.status === 'erro';
  const bg = out
    ? (erro ? '#b43737' : 'linear-gradient(135deg, #3D8B37 0%, #2E6A29 100%)')
    : 'var(--portal-bubble-in)';
  const align = out ? 'flex-end' : 'flex-start';
  const podeResponder = mensagem.tipo !== 'sistema' && onResponder;

  const remetente = out
    ? (mensagem.autor || (mensagem.tipo === 'sistema' ? 'sistema' : null))
    : (contatoNome || formatNumero(contatoNumero) || 'cliente');

  const ehMidia = ['imagem', 'audio', 'video', 'documento', 'figurinha'].includes(mensagem.tipo);
  const conteudoTexto = mensagem.conteudo || null;

  // Swipe-to-reply no mobile: arrastar a mensagem pra direita responde a ela
  // (gesto do WhatsApp). No desktop, o botão de resposta aparece no hover.
  const cancelarLongPress = () => {
    if (longPressRef.current) { clearTimeout(longPressRef.current); longPressRef.current = null; }
  };
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    // Arrasto que nasce colado na borda esquerda é "voltar" — quem trata é o
    // painel. Sem isto o mesmo gesto respondia a mensagem E saía da conversa.
    naBordaRef.current = e.touches[0].clientX <= ZONA_BORDA;
    // Long-press (mobile/tablet) abre o menu de ações em overlay — o pai controla,
    // então some ao tocar fora e nunca empilha.
    if (!mensagem.apagada && onAbrirAcoes) {
      // O retângulo é medido na HORA em que o menu abre, não no toque: entre um
      // e outro a lista pode ter rolado, e o menu abriria onde a bolha estava.
      const bolha = e.currentTarget;
      const toqueY = e.touches[0].clientY;
      longPressRef.current = setTimeout(() => {
        onAbrirAcoes(mensagem, faixaDoToque(bolha, toqueY));
      }, 420);
    }
  };
  const onTouchMove = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.touches[0].clientX - touchStartX.current;
    const dy = e.touches[0].clientY - touchStartY.current;
    if (Math.abs(dx) > 8 || Math.abs(dy) > 8) cancelarLongPress();
    if (naBordaRef.current) return;
    if (!podeResponder) return;
    // Enquanto não engatou, se o gesto é mais vertical que horizontal, deixa
    // o scroll da lista rolar normal (não sequestra pra responder).
    if (dragX === 0 && Math.abs(dy) > Math.abs(dx)) return;
    setDragX(dx > 0 ? Math.min(dx * 0.6, 72) : 0);
  };
  const onTouchEnd = () => {
    cancelarLongPress();
    if (dragX > 45 && podeResponder) onResponder(mensagem);
    setDragX(0);
    touchStartX.current = null;
    touchStartY.current = null;
  };
  return (
    <div
      // pan-y: o browser só cuida do scroll vertical; o arraste horizontal é
      // nosso (a bolha), então a tela não desliza junto pro lado.
      style={{ position: 'relative', touchAction: 'pan-y' }}
      // No dedo, o toque longo é NOSSO. Sem isto o Android entende "selecionar
      // texto" e sobe as alças de copiar por cima do menu; no iOS sobe o
      // callout. Copiar continua existindo — como item do menu.
      className={isMobile ? 'balao-toque' : undefined}
      onContextMenu={isMobile ? (e) => e.preventDefault() : undefined}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setEmojisAbertos(false); }}
    >
      {dragX > 4 && (
        <div style={{
          position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
          opacity: Math.min(dragX / 45, 1), color: '#3D8B37', fontSize: '1.2rem', zIndex: 0,
        }}>
          <i className="bi bi-reply-fill" />
        </div>
      )}
      <div
        style={{
          display: 'flex', justifyContent: align, alignItems: 'center', marginBottom: 6,
          transform: dragX ? `translateX(${dragX}px)` : undefined,
          transition: dragX ? 'none' : 'transform 0.15s ease',
        }}
      >
      <div
        style={{
          position: 'relative',
          maxWidth: '70%',
          background: bg,
          color: out ? '#fff' : 'var(--portal-text)',
          padding: '8px 12px',
          borderRadius: 12,
          borderTopRightRadius: out ? 4 : 12,
          borderTopLeftRadius: out ? 12 : 4,
          fontSize: '0.9rem',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
          // No mobile, não deixa selecionar texto (o long-press abre o menu e o
          // "Copiar" copia a msg inteira — igual WhatsApp). Evita o conflito com
          // o swipe/arraste que bagunçava a seleção.
          ...(isMobile ? { userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' } : null),
        }}
      >
        {hovered && !isMobile && !editando && !mensagem.apagada && (
          <div style={{
            position: 'absolute', zIndex: 20,
            // Desktop (hover): barra ao LADO da bolha, centralizada — sem o
            // "buraco" que fazia a barra fugir. (Mobile usa o overlay do pai.)
            top: '50%', transform: 'translateY(-50%)', [out ? 'right' : 'left']: 'calc(100% + 6px)',
            display: 'flex', alignItems: 'center', gap: 1, padding: '2px 4px',
            background: 'var(--portal-surface)', border: '1px solid var(--portal-border)',
            borderRadius: 18, boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
          }}>
            {emojisAbertos ? (
              rapidosDoMouse.map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => { if (onReagir) reagirEAnotar(onReagir, mensagem, e); setEmojisAbertos(false); }}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.05rem', lineHeight: 1, padding: '2px 3px' }}
                >{e}</button>
              ))
            ) : (
              <>
                <button type="button" title="Reagir" onClick={() => setEmojisAbertos(true)} style={acaoBolhaStyle}><i className="bi bi-emoji-smile" /></button>
                {podeResponder && <button type="button" title="Responder" onClick={() => onResponder(mensagem)} style={acaoBolhaStyle}><i className="bi bi-reply-fill" /></button>}
                {onEncaminhar && mensagem.tipo !== 'sistema' && !mensagem.apagada && (
                  <button type="button" title="Encaminhar" onClick={() => onEncaminhar(mensagem)} style={acaoBolhaStyle}><i className="bi bi-forward-fill" /></button>
                )}
                {mensagem.tipo !== 'sistema' && !mensagem.apagada && (ehFixada
                  ? (onDesafixar && (
                      <button type="button" title="Desafixar" onClick={() => onDesafixar()} style={acaoBolhaStyle}><i className="bi bi-pin-angle" /></button>
                    ))
                  : (onFixar && (
                      <button type="button" title="Fixar" onClick={() => onFixar(mensagem)} style={acaoBolhaStyle}><i className="bi bi-pin-angle-fill" /></button>
                    ))
                )}
                {out && mensagem.tipo === 'texto' && onEditar && (
                  <button type="button" title="Editar" onClick={() => { setTextoEdit(mensagem.conteudo || ''); setEditando(true); }} style={acaoBolhaStyle}><i className="bi bi-pencil" /></button>
                )}
                {out && onApagar && (
                  <button type="button" title="Apagar" onClick={() => onApagar(mensagem)} style={acaoBolhaStyle}><i className="bi bi-trash" /></button>
                )}
              </>
            )}
          </div>
        )}
        {(mensagem.quoted_id || mensagem.quoted_preview) && (
          <QuotedBlock mensagem={mensagem} contatoNome={contatoNome} contatoNumero={contatoNumero} onIr={onIrParaMensagem} />
        )}
        {remetente && !erro && (
          <div style={{ fontSize: '0.7rem', opacity: 0.75, marginBottom: 2, fontWeight: 600 }}>
            {remetente}
          </div>
        )}
        {editando ? (
          <div style={{ minWidth: 210 }}>
            <textarea
              value={textoEdit}
              onChange={(e) => setTextoEdit(e.target.value)}
              rows={2}
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (textoEdit.trim()) { onEditar?.(mensagem, textoEdit); setEditando(false); } } if (e.key === 'Escape') setEditando(false); }}
              style={{ width: '100%', resize: 'none', borderRadius: 8, border: 'none', padding: '6px 8px', fontSize: '0.9rem', color: '#222' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 4 }}>
              <button type="button" onClick={() => setEditando(false)} style={{ background: 'rgba(255,255,255,0.25)', border: 'none', color: '#fff', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: '0.8rem' }}>Cancelar</button>
              <button type="button" onClick={() => { if (textoEdit.trim()) { onEditar?.(mensagem, textoEdit); setEditando(false); } }} style={{ background: '#fff', border: 'none', color: '#2E6A29', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700 }}>Salvar</button>
            </div>
          </div>
        ) : mensagem.apagada ? (
          <div style={{ fontStyle: 'italic', opacity: 0.75, display: 'flex', alignItems: 'center', gap: 5 }}>
            <i className="bi bi-slash-circle" /> Mensagem apagada
          </div>
        ) : mensagem.tipo === 'contato' ? (
          <ContatoCard
            conteudo={conteudoTexto}
            out={out}
            onConversar={onConversarContato}
            onSalvarNaAgenda={onSalvarContato}
            nomeNaAgenda={nomeNaAgenda}
          />
        ) : mensagem.tipo === 'localizacao' ? (
          <LocalizacaoCard conteudo={conteudoTexto} out={out} />
        ) : (
          <>
            {ehMidia && <MidiaRenderer mensagem={mensagem} onAbrirImagem={onAbrirImagem} out={out} />}
            {ehMidia && conteudoTexto && (
              <div style={{ marginTop: 4 }}>
                <TextoDaMensagem
                  texto={conteudoTexto} ddd={dddDoContato} ignorar={contatoNumero}
                  onTelefone={onTelefone} corLink={out ? '#dff3dd' : '#3D8B37'}
                />
              </div>
            )}
            {!ehMidia && (
              <div>
                {conteudoTexto
                  ? (
                    <TextoDaMensagem
                      texto={conteudoTexto} ddd={dddDoContato} ignorar={contatoNumero}
                      onTelefone={onTelefone} corLink={out ? '#dff3dd' : '#3D8B37'}
                    />
                  )
                  : '[mensagem]'}
              </div>
            )}
          </>
        )}
        {mensagem.reacao && !mensagem.apagada && (
          <div style={{ marginTop: 3 }}>
            <span style={{
              background: out ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.06)',
              borderRadius: 12, padding: '1px 7px', fontSize: '0.95rem', lineHeight: 1.4,
            }}>{mensagem.reacao}</span>
          </div>
        )}
        <div style={{
          fontSize: '0.7rem', opacity: 0.7, marginTop: 4, textAlign: 'right',
          display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 6,
        }}>
          {mensagem.chamado_id && (
            <span style={{
              background: out ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.08)',
              padding: '0 6px', borderRadius: 8, fontWeight: 600,
            }}>
              #{mensagem.chamado_id}
            </span>
          )}
          {mensagem.editada && !mensagem.apagada && <span style={{ fontStyle: 'italic' }}>editada</span>}
          <span>{formatHora(mensagem.criada_em)}{erro && ' - falha ao enviar'}</span>
          {out && !erro && !mensagem.apagada && <MensagemStatus status={mensagem.status} />}
        </div>
      </div>
      </div>
    </div>
  );
}

const STATUS_CHAMADO_RESOLVIDO = new Set(['resolvido', 'fechado', 'cancelado']);

// Bolinha colorida de SLA: verde = folga, laranja = passou de 70% do limite,
// vermelho = estourou. Sem SLA configurado (ou sem prioridade) -> null.
// Texto na tag: tempo restante quando dentro do SLA, "+Xmin" quando estourou.
function SlaBolinha({ decorridoSegs, limiteMin, faseLabel }) {
  if (!limiteMin || decorridoSegs == null) return null;
  const limiteSegs = limiteMin * 60;
  const ratio = decorridoSegs / limiteSegs;
  let cor;
  let texto;
  if (ratio >= 1) {
    cor = '#dc3545';
    const estouroMin = Math.floor((decorridoSegs - limiteSegs) / 60);
    texto = `+${estouroMin}m`;
  } else if (ratio >= 0.7) {
    cor = '#F5A623';
    const restanteMin = Math.ceil((limiteSegs - decorridoSegs) / 60);
    texto = `${restanteMin}m`;
  } else {
    cor = '#28a745';
    const restanteMin = Math.ceil((limiteSegs - decorridoSegs) / 60);
    texto = `${restanteMin}m`;
  }
  const titulo = ratio >= 1
    ? `SLA ${faseLabel} estourado em ${Math.floor((decorridoSegs - limiteSegs) / 60)}min úteis`
    : `SLA ${faseLabel}: ${Math.ceil((limiteSegs - decorridoSegs) / 60)}min úteis restantes (limite ${limiteMin}min)`;
  return (
    <span
      title={titulo}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: '0.62rem', fontWeight: 700,
        background: `${cor}22`, color: cor,
        padding: '1px 6px', borderRadius: 8, border: `1px solid ${cor}55`,
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{
        width: 7, height: 7, borderRadius: '50%', background: cor,
        display: 'inline-block',
      }} />
      {texto}
    </span>
  );
}

// Cronometro de SLA do chamado vinculado a conversa.
// Tres fases:
//   1) Chamado pendente de primeira resposta -> conta TTFR (icone stopwatch).
//   2) Atendente ja respondeu mas chamado nao foi resolvido -> conta TTR
//      (icone hourglass), continuando a partir do mesmo created_at.
//   3) Chamado resolvido/fechado/cancelado OU sem chamado vinculado -> nada.
// Em horario fora da janela o numero para de subir.
function CronometroSLA({
  chamadoCreatedAt, primeiraRespostaEm, chamadoStatus,
  agora, horarios, horarioAtivo,
  status, prioridade, slaTtfrMap, slaTtrMap,
  compact = false,
}) {
  if (!chamadoCreatedAt) return null;
  if (status === 'encerrada') return null;
  if (chamadoStatus && STATUS_CHAMADO_RESOLVIDO.has(chamadoStatus)) return null;

  const fase = primeiraRespostaEm ? 'ttr' : 'ttfr';
  const segs = segundosEmJanela(chamadoCreatedAt, agora, horarios, horarioAtivo);
  if (segs == null) return null;

  const correndo = dentroDaJanela(agora, horarios, horarioAtivo);
  const cor = !correndo ? '#5E7A82' : (segs >= 1800 ? '#F5A623' : '#3D8B37');
  const tituloBase = fase === 'ttfr'
    ? 'Aguardando primeira resposta'
    : 'Aguardando solução do chamado';
  const titulo = correndo ? tituloBase : `${tituloBase} (fora do horário - cronômetro pausado)`;

  const limite = (prioridade && (fase === 'ttfr' ? slaTtfrMap : slaTtrMap))
    ? (fase === 'ttfr' ? slaTtfrMap[prioridade] : slaTtrMap[prioridade])
    : null;
  const faseLabel = fase === 'ttfr' ? '1ª resp.' : 'solução';
  const icone = !correndo
    ? 'bi-pause-circle'
    : (fase === 'ttfr' ? 'bi-stopwatch' : 'bi-hourglass-split');

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span
        title={titulo}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          fontSize: compact ? '0.65rem' : '0.72rem',
          fontWeight: 600,
          background: `${cor}22`, color: cor,
          padding: compact ? '1px 6px' : '2px 8px',
          borderRadius: 8, border: `1px solid ${cor}55`,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
      >
        <i className={`bi ${icone}`} />
        {formatarCronometro(segs)}
      </span>
      <SlaBolinha decorridoSegs={segs} limiteMin={limite} faseLabel={faseLabel} />
    </span>
  );
}

// O olhinho que fixa um grupo de filtro fora do painel. Só aparece com o
// painel ABERTO: é ali que a pessoa está decidindo o que quer ver sempre, e
// mostrar sempre viraria mais um ícone competindo com o filtro em si.
function OlhoFixar({ fixado, aoAlternar, painelAberto }) {
  if (!painelAberto) return null;
  return (
    <button
      type="button"
      onClick={aoAlternar}
      title={fixado ? 'Deixar de mostrar sempre (só dentro de Filtros)' : 'Mostrar sempre, mesmo com Filtros fechado'}
      style={{
        background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px',
        color: fixado ? '#3D8B37' : 'var(--portal-muted)', fontSize: '0.85rem',
      }}
    >
      <i className={`bi ${fixado ? 'bi-eye-fill' : 'bi-eye-slash'}`} />
    </button>
  );
}

function ConversaItem({
  conversa, ativa, onClick, agora, horarios, horarioAtivo, slaTtfrMap, slaTtrMap,
  selecionando = false, selecionada = false, onAlternarSelecao, onSegurar,
}) {
  const esperandoMs = conversa.aguardando_desde && conversa.status !== 'encerrada'
    ? Math.max(0, agora.getTime() - new Date(conversa.aguardando_desde).getTime())
    : null;
  const corEsp = esperandoMs != null ? corEspera(esperandoMs) : null;

  // Toque longo entra na seleção, como no WhatsApp. Existe também o botão
  // "Selecionar" no topo da lista: gesto escondido ninguém acha sozinho (foi o
  // motivo de a consulta de produto usar checkbox em vez de segurar).
  const seguraRef = useRef(null);
  const comecouRef = useRef(false);
  const iniciarSegurar = () => {
    if (selecionando) return;
    comecouRef.current = false;
    seguraRef.current = setTimeout(() => {
      comecouRef.current = true;
      onSegurar?.(conversa.id);
    }, 500);
  };
  const soltarSegurar = () => { clearTimeout(seguraRef.current); };
  useEffect(() => () => clearTimeout(seguraRef.current), []);

  return (
    <button
      type="button"
      onClick={() => {
        // O toque longo já resolveu: o clique que vem junto não pode abrir a
        // conversa por cima da seleção que acabou de aparecer.
        if (comecouRef.current) { comecouRef.current = false; return; }
        if (selecionando) { onAlternarSelecao?.(conversa.id); return; }
        onClick?.();
      }}
      onContextMenu={(e) => { if (selecionando) e.preventDefault(); }}
      onTouchStart={iniciarSegurar}
      onTouchEnd={soltarSegurar}
      onTouchMove={soltarSegurar}
      onMouseDown={iniciarSegurar}
      onMouseUp={soltarSegurar}
      onMouseLeave={soltarSegurar}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        width: '100%',
        textAlign: 'left',
        background: ativa ? 'rgba(61, 139, 55, 0.18)' : 'transparent',
        border: 'none',
        borderLeft: ativa ? '3px solid #3D8B37' : '3px solid transparent',
        borderBottom: '1px solid var(--portal-border)',
        color: 'var(--portal-text)',
        cursor: 'pointer',
      }}
    >
      {selecionando && (
        <span
          aria-hidden
          style={{
            width: 22, height: 22, flexShrink: 0, borderRadius: 6,
            border: `2px solid ${selecionada ? '#3D8B37' : 'var(--portal-border)'}`,
            background: selecionada ? '#3D8B37' : 'transparent',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: '0.8rem',
          }}
        >
          {selecionada && <i className="bi bi-check-lg" />}
        </span>
      )}
      <Avatar nome={conversa.push_name} numero={conversa.numero} fotoUrl={conversa.profile_pic_url} size={42} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <strong style={{
            fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {conversa.push_name || formatNumero(conversa.numero) || conversa.jid}
          </strong>
          <span style={{ fontSize: '0.7rem', color: 'var(--portal-muted)', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {/* Tag do lado da HORA, longe dos chips de fila e espera: são
                naturezas diferentes (o que EU marquei x o estado da conversa) e
                juntas viravam uma fileira que ninguém lê. */}
            {Array.isArray(conversa.tags) && conversa.tags.slice(0, 3).map((t) => (
              <span key={t.id} title={`Sua tag: ${t.nome}`} style={{
                fontSize: '0.62rem', fontWeight: 700, maxWidth: 92,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                background: t.cor || '#3D8B37', color: '#fff',
                padding: '1px 6px', borderRadius: 8,
              }}>{t.nome}</span>
            ))}
            {Array.isArray(conversa.tags) && conversa.tags.length > 3 && (
              <span title={conversa.tags.slice(3).map((t) => t.nome).join(', ')}
                style={{ fontSize: '0.62rem', fontWeight: 700, color: 'var(--portal-muted)' }}>
                +{conversa.tags.length - 3}
              </span>
            )}
            {conversa.fixada && <i className="bi bi-pin-angle-fill" title="Fixada" />}
            {formatHora(conversa.ultima_mensagem_em)}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 2 }}>
          <span style={{
            fontSize: '0.78rem', color: 'var(--portal-muted)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
          }}>
            {conversa.ultima_preview || '(sem mensagens)'}
          </span>
          {conversa.nao_lidas > 0 ? (
            <span style={{
              background: '#F5A623', color: '#fff', fontSize: '0.7rem', fontWeight: 700,
              padding: '1px 7px', borderRadius: 10, flexShrink: 0,
            }}>
              {conversa.nao_lidas}
            </span>
          ) : conversa.nao_lida_manual ? (
            <span title="Marcada como não-lida" style={{
              width: 10, height: 10, borderRadius: 5, background: '#F5A623', flexShrink: 0, alignSelf: 'center',
            }} />
          ) : null}
        </div>
        <div style={{
          marginTop: 4, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', gap: 6, flexWrap: 'wrap',
        }}>
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            {esperandoMs != null && (
              <span
                title="Tempo esperando resposta (desde a última mensagem do cliente)"
                style={{
                  fontSize: '0.65rem', fontWeight: 700,
                  background: `${corEsp}22`, color: corEsp,
                  padding: '1px 6px', borderRadius: 8, border: `1px solid ${corEsp}66`,
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}
              >
                <i className="bi bi-clock-history" />{formatEspera(esperandoMs)}
              </span>
            )}
            {/* Sem chip de status: a ABA já diz. Em "Minha fila" tudo está
                aberto, em "Encerradas" tudo está encerrado — repetir em cada
                linha só gastava espaço e cansava a vista. */}
            {conversa.setor_nome && (
              <span
                title={`Fila: ${conversa.setor_nome}`}
                style={{
                  fontSize: '0.65rem', fontWeight: 700,
                  background: 'rgba(245, 166, 35, 0.18)', color: '#F5A623',
                  padding: '1px 6px', borderRadius: 8,
                  border: '1px solid rgba(245, 166, 35, 0.5)',
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}
              >
                <i className="bi bi-inboxes" />{conversa.setor_nome}
              </span>
            )}
            {conversa.atendente && (
              <span
                title={`Em atendimento por ${conversa.atendente_nome || conversa.atendente}`}
                style={{
                  fontSize: '0.65rem', fontWeight: 600,
                  background: 'rgba(61, 139, 55, 0.18)', color: '#3D8B37',
                  padding: '1px 6px', borderRadius: 8,
                  border: '1px solid rgba(61, 139, 55, 0.45)',
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {/* O SELO mostra nome de gente. A comparação de dono, essa
                    continua no login (`atendente`) — são coisas diferentes e
                    misturar as duas foi o que fez o vendedor ver a própria
                    conversa como se fosse de outro. */}
                <i className="bi bi-person-check" />{conversa.atendente_nome || conversa.atendente}
              </span>
            )}
            {!conversa.atendente && parseBotDados(conversa.bot_dados)._respondido_externo && (
              <span
                title="Alguém já respondeu pelo WhatsApp do celular — cuidado pra não responder em dobro"
                style={{
                  fontSize: '0.65rem', fontWeight: 600,
                  background: 'rgba(245, 166, 35, 0.14)', color: '#F5A623',
                  padding: '1px 6px', borderRadius: 8,
                  border: '1px solid rgba(245, 166, 35, 0.4)',
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}
              >
                <i className="bi bi-phone" />pelo celular
              </span>
            )}
          </span>
          <CronometroSLA
            chamadoCreatedAt={conversa.chamado_created_at}
            primeiraRespostaEm={conversa.primeira_resposta_em}
            chamadoStatus={conversa.chamado_status}
            agora={agora}
            horarios={horarios}
            horarioAtivo={horarioAtivo}
            status={conversa.status}
            prioridade={conversa.chamado_prioridade}
            slaTtfrMap={slaTtfrMap}
            slaTtrMap={slaTtrMap}
            compact
          />
        </div>
      </div>
    </button>
  );
}

function parseBotDados(raw) {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return {}; }
}

function EncerrarModal({ aberto, modos, onClose, onConfirmar }) {
  const [modoId, setModoId] = useState('');
  const [comentario, setComentario] = useState('');
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    setModoId(modos[0] ? String(modos[0].id) : '');
    setComentario('');
    setEnviando(false);
  }, [aberto]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!aberto) return null;

  const modoSelecionado = modos.find((m) => String(m.id) === String(modoId)) || null;
  const podeConfirmar = !enviando && Boolean(modoId);

  const confirmar = async () => {
    if (!podeConfirmar) return;
    setEnviando(true);
    try {
      const payload = { modo_id: Number(modoId) };
      if (comentario.trim()) payload.comentario = comentario.trim();
      await onConfirmar(payload);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 520 }}>
        <div className="modal-content" style={{ background: 'var(--portal-surface)', color: 'var(--portal-text)' }}>
          <div className="modal-header" style={{ borderColor: 'var(--portal-border)' }}>
            <h5 className="modal-title">
              <i className="bi bi-check2-circle me-2" />
              Encerrar atendimento
            </h5>
            <button className="btn-close btn-close-white" onClick={onClose} disabled={enviando} />
          </div>

          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Modo de encerramento — obrigatorio */}
            <div>
              <label style={{ fontSize: '0.85rem', color: 'var(--portal-muted)', display: 'block', marginBottom: 6 }}>
                <i className="bi bi-flag me-1" /> Modo de encerramento <span style={{ color: '#dc3545' }}>*</span>
              </label>
              {modos.length === 0 ? (
                <div style={{ fontSize: '0.82rem', color: '#F5A623' }}>
                  Nenhum modo cadastrado. Peça a um administrador para cadastrar em /admin/atendimento.
                </div>
              ) : (
                <>
                  <select
                    className="form-select"
                    value={modoId}
                    onChange={(e) => setModoId(e.target.value)}
                  >
                    {modos.map((m) => (
                      <option key={m.id} value={String(m.id)}>{m.descricao}</option>
                    ))}
                  </select>
                  {modoSelecionado?.mensagem_cliente && (
                    <div style={{
                      marginTop: 8, padding: '8px 10px', borderRadius: 6,
                      background: 'rgba(61, 139, 55,0.08)', border: '1px solid rgba(61, 139, 55,0.2)',
                      fontSize: '0.78rem', color: 'var(--portal-muted)',
                    }}>
                      <i className="bi bi-chat-dots me-1" />
                      <em>{modoSelecionado.mensagem_cliente}</em>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Comentario */}
            <div>
              <label style={{ fontSize: '0.85rem', color: 'var(--portal-muted)', display: 'block', marginBottom: 6 }}>
                <i className="bi bi-pencil-square me-1" /> Comentário do atendente (opcional)
              </label>
              <textarea
                className="form-control"
                rows={3}
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
                placeholder="O que foi feito, observações, próximos passos..."
              />
            </div>

          </div>

          <div className="modal-footer" style={{ borderColor: 'var(--portal-border)' }}>
            <button className="btn btn-outline-secondary" onClick={onClose} disabled={enviando}>
              Cancelar
            </button>
            <button
              className="btn"
              onClick={confirmar}
              disabled={!podeConfirmar}
              style={{
                background: 'linear-gradient(135deg, #3D8B37 0%, #2E6A29 100%)',
                color: '#fff', fontWeight: 600, border: 'none', padding: '6px 18px',
              }}
            >
              {enviando ? 'Encerrando...' : 'Encerrar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Lightbox simples para visualizar imagens das conversas sem abrir nova
// aba do navegador. ESC ou botao fechar encerram. Usa portal-surface escura
// pra contraste e mantem aspect ratio da imagem.
const ZOOM_STEP = 0.2;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 5;

// Visualizador de mídia em tela cheia. Aceita string (imagem, como sempre) ou
// { url, tipo, nome } — hoje 'pdf' também abre aqui, em vez de jogar o usuário
// numa aba. Mesma lógica pro usuário: vê, imprime ou baixa sem sair da conversa.
export function LightboxImagem({ url: entrada, onClose }) {
  const alvo = typeof entrada === 'string' ? { url: entrada, tipo: 'imagem' } : (entrada || {});
  const url = alvo.url || null;
  const ehPdf = alvo.tipo === 'pdf';
  const nomeArquivo = alvo.nome || (ehPdf ? 'documento.pdf' : 'imagem');
  // Sem a extensão o Android baixa um arquivo que ele não sabe abrir — e o
  // nome que vem do storage costuma ser só o UUID.
  const nomePdf = /\.pdf$/i.test(nomeArquivo) ? nomeArquivo : `${nomeArquivo}.pdf`;

  // O Chrome do Android não tem visualizador de PDF embutido: um <iframe> com o
  // documento vira um ícone com "Abrir" que não abre nada, porque a origem é
  // `blob:` e não dá pra entregar pra outro app. `pdfViewerEnabled` responde
  // exatamente essa pergunta; onde ele não existe, o dedo é o melhor palpite.
  const podeVerPdfAqui = typeof navigator.pdfViewerEnabled === 'boolean'
    ? navigator.pdfViewerEnabled
    : !window.matchMedia?.('(pointer: coarse)').matches;

  // PDF entra como blob: o visualizador do navegador só deixa imprimir de um
  // iframe da MESMA origem, e a API fica noutro domínio.
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfErro, setPdfErro] = useState(false);
  const pdfFrameRef = useRef(null);
  useEffect(() => {
    if (!url || !ehPdf || !podeVerPdfAqui) { setPdfBlobUrl(null); setPdfErro(false); return undefined; }
    let vivo = true;
    let href = null;
    setPdfErro(false);
    fetch(url)
      .then((r) => { if (!r.ok) throw new Error('falhou'); return r.blob(); })
      .then((b) => {
        if (!vivo) return;
        href = URL.createObjectURL(b);
        setPdfBlobUrl(href);
      })
      .catch(() => { if (vivo) setPdfErro(true); });
    return () => { vivo = false; if (href) URL.revokeObjectURL(href); };
  }, [url, ehPdf, podeVerPdfAqui]);

  const [rotacao, setRotacao] = useState(0);
  const [zoom, setZoom] = useState(1);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  const ajustarZoom = (delta) =>
    setZoom((z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round((z + delta) * 10) / 10)));

  useEffect(() => {
    if (!url) return undefined;
    setRotacao(0);
    setZoom(1);
    const onKey = (e) => {
      if (e.key === 'Escape') onCloseRef.current();
      if (ehPdf) return; // no PDF quem controla zoom/rolagem é o visualizador do navegador
      if (e.key === 'r' || e.key === 'R') setRotacao((r) => (r + 90) % 360);
      if (e.key === '+' || e.key === '=') ajustarZoom(ZOOM_STEP);
      if (e.key === '-') ajustarZoom(-ZOOM_STEP);
      if (e.key === '0') setZoom(1);
    };
    const onWheel = (e) => {
      if (ehPdf) return;
      e.preventDefault();
      ajustarZoom(e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('wheel', onWheel);
    };
  }, [url, ehPdf]);

  if (!url) return null;

  // Salvar: o atributo `download` do <a> só força o download em URL da mesma
  // origem — com a mídia vindo do storage assinado ele acabava só navegando.
  // Baixar como blob resolve e ainda deixa dar um nome decente ao arquivo.
  const salvarImagem = async () => {
    try {
      // No app, quem baixa é o lado nativo e o arquivo cai na folha de
      // compartilhamento do celular. A âncora com `download` é inerte dentro da
      // WebView — é ela que o botão "Baixar e abrir" aqui embaixo usa, então
      // sem este desvio aquele botão não faria NADA no app.
      // O caminho do navegador continua igual, e não é duplicação à toa: lá o
      // nome do arquivo sai do TIPO REAL do blob, e é isso que faz um .png
      // chegar como .png.
      if (noApp()) {
        const extUrl = (String(url).match(/\.([a-z0-9]{3,4})(?:$|\?)/i)?.[1] || 'jpg').toLowerCase();
        await baixarDaUrl(ehPdf ? nomePdf : `fio-imagem-${Date.now()}.${extUrl}`, url);
        return;
      }
      const r = await fetch(url);
      if (!r.ok) throw new Error('falhou');
      const blob = await r.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      const ext = (blob.type.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
      a.download = ehPdf ? nomePdf : `fio-imagem-${Date.now()}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 4000);
    } catch (e) {
      // Sem permissão de leitura cruzada: abre numa aba pro usuário salvar pelo
      // menu do navegador. Melhor do que nada acontecer ao clicar.
      window.open(url, '_blank', 'noopener');
    }
  };

  // Imprimir sem baixar: janela só com a imagem, ajustada à folha, e a caixa de
  // impressão abre sozinha. Fecha ao terminar (ou ao cancelar).
  const imprimirImagem = () => {
    if (ehPdf) {
      // O iframe já está com o blob (mesma origem), então dá pra mandar imprimir
      // direto — a atendente não sai da conversa nem baixa nada.
      try {
        pdfFrameRef.current?.contentWindow?.focus();
        pdfFrameRef.current?.contentWindow?.print();
      } catch (e) {
        if (pdfBlobUrl) window.open(pdfBlobUrl, '_blank', 'noopener');
      }
      return;
    }
    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) {
      Swal.fire({ icon: 'info', title: 'Libere os pop-ups', text: 'O navegador bloqueou a janela de impressão.' });
      return;
    }
    w.document.write('<!doctype html><html><head><title>Imprimir imagem</title>'
      + '<style>@page{margin:12mm}html,body{margin:0;padding:0;height:100%}'
      + 'body{display:flex;align-items:center;justify-content:center}'
      + 'img{max-width:100%;max-height:100vh;object-fit:contain}</style>'
      + '</head><body><img src="' + url + '" alt="" /></body></html>');
    w.document.close();
    const imprimir = () => { w.focus(); w.print(); };
    const img = w.document.querySelector('img');
    if (img && !img.complete) {
      img.onload = imprimir;
      img.onerror = () => { w.close(); };
    } else {
      setTimeout(imprimir, 150);
    }
    w.onafterprint = () => w.close();
  };

  // zIndex nos controles: a <img> vem DEPOIS deles no DOM e tem `transform`
  // (rotação/zoom), o que cria contexto de empilhamento próprio — sem isso ela
  // pintava por cima e engolia os botões quando a foto era grande.
  // Fundo mais escuro que o da imagem, porque eles ficam sobre a foto.
  const btnStyle = {
    background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.25)',
    color: '#fff', width: 36, height: 36, borderRadius: '50%',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', fontSize: '1.1rem',
    zIndex: 2, backdropFilter: 'blur(2px)',
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1300,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '68px 24px 24px', // 68px no topo = faixa dos controles
        overflow: 'hidden',
      }}
      onClick={onClose}
    >
      {/* linha de botões */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        title="Fechar (ESC)"
        aria-label="Fechar"
        style={{ ...btnStyle, position: 'absolute', top: 16, right: 16 }}
      >
        <i className="bi bi-x-lg" />
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); salvarImagem(); }}
        title="Salvar imagem no computador"
        aria-label="Salvar imagem"
        style={{ ...btnStyle, position: 'absolute', top: 16, right: 64 }}
      >
        <i className="bi bi-download" />
      </button>
      {/* Imprimir depende do visualizador embutido (é dele que sai a caixa de
          impressão). Sem ele, o botão não teria o que fazer — e dentro do app
          isso vale pra TUDO, imagem inclusive: a WebView ignora `print()`. */}
      {!(ehPdf && !podeVerPdfAqui) && !noApp() && (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); imprimirImagem(); }}
        title="Imprimir (sem precisar baixar)"
        aria-label="Imprimir imagem"
        style={{ ...btnStyle, position: 'absolute', top: 16, right: 112 }}
      >
        <i className="bi bi-printer" />
      </button>
      )}
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        title="Abrir em nova aba"
        onClick={(e) => e.stopPropagation()}
        style={{ ...btnStyle, position: 'absolute', top: 16, right: 160, textDecoration: 'none' }}
      >
        <i className="bi bi-box-arrow-up-right" />
      </a>
      {!ehPdf && (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setRotacao((r) => (r + 90) % 360); }}
        title="Girar 90° (R)"
        aria-label="Girar imagem"
        style={{ ...btnStyle, position: 'absolute', top: 16, right: 208 }}
      >
        <i className="bi bi-arrow-clockwise" />
      </button>
      )}
      {!ehPdf && (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); ajustarZoom(ZOOM_STEP); }}
        title="Ampliar (+)"
        aria-label="Ampliar"
        disabled={zoom >= ZOOM_MAX}
        style={{ ...btnStyle, position: 'absolute', top: 16, right: 256, opacity: zoom >= ZOOM_MAX ? 0.4 : 1 }}
      >
        <i className="bi bi-zoom-in" />
      </button>
      )}
      {!ehPdf && (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); ajustarZoom(-ZOOM_STEP); }}
        title="Reduzir (-)"
        aria-label="Reduzir"
        disabled={zoom <= ZOOM_MIN}
        style={{ ...btnStyle, position: 'absolute', top: 16, right: 304, opacity: zoom <= ZOOM_MIN ? 0.4 : 1 }}
      >
        <i className="bi bi-zoom-out" />
      </button>
      )}
      {!ehPdf && (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setZoom(1); }}
        title="Zoom original (0)"
        aria-label="Zoom original"
        style={{ ...btnStyle, position: 'absolute', top: 16, right: 352, fontSize: '0.7rem', fontWeight: 700, opacity: zoom === 1 ? 0.4 : 1 }}
      >
        1:1
      </button>
      )}

      {/* indicador de zoom */}
      {!ehPdf && zoom !== 1 && (
        <div style={{
          position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(0,0,0,0.55)', color: '#fff', borderRadius: 12,
          padding: '3px 12px', fontSize: '0.78rem', pointerEvents: 'none', zIndex: 2,
        }}>
          {Math.round(zoom * 100)}%
        </div>
      )}

      {ehPdf ? (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: 'min(1000px, 96vw)', height: '100%', zIndex: 1,
            borderRadius: 8, overflow: 'hidden', background: '#fff',
            boxShadow: '0 6px 32px rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {!podeVerPdfAqui ? (
            /* Sem visualizador embutido, mostrar uma moldura vazia seria mentir.
               Melhor dizer o que dá pra fazer e fazer num toque: baixa com o
               nome certo, e o Android abre no leitor de PDF em seguida. */
            <div style={{ color: '#333', textAlign: 'center', padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
              <i className="bi bi-file-earmark-pdf" style={{ fontSize: '3rem', color: '#c0392b' }} />
              <strong style={{ fontSize: '1rem', wordBreak: 'break-all', maxWidth: 320 }}>{nomePdf}</strong>
              <span style={{ fontSize: '0.85rem', color: '#666', maxWidth: 320 }}>
                Este navegador não abre PDF dentro da página.
              </span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); salvarImagem(); }}
                style={{
                  background: '#3D8B37', color: '#fff', border: 'none', borderRadius: 10,
                  padding: '12px 22px', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer',
                }}
              >
                <i className="bi bi-download me-2" />
                Baixar e abrir
              </button>
            </div>
          ) : pdfErro ? (
            <div style={{ color: '#333', textAlign: 'center', padding: 24, fontSize: '0.9rem' }}>
              Não deu pra abrir o documento aqui.
              <br />
              <a href={url} target="_blank" rel="noreferrer" style={{ color: '#3D8B37' }}>Abrir numa aba</a>
            </div>
          ) : pdfBlobUrl ? (
            <iframe
              ref={pdfFrameRef}
              src={pdfBlobUrl}
              title={nomeArquivo}
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          ) : (
            <div style={{ color: '#666', fontSize: '0.9rem' }}>Carregando documento…</div>
          )}
        </div>
      ) : (
      <img
        src={url}
        alt="imagem ampliada"
        onClick={(e) => e.stopPropagation()}
        style={{
          // 82vh (e nao 90vh) deixa a faixa dos controles livre no topo: eles
          // ficam legiveis mesmo em foto clara, sem depender so do z-index.
          maxWidth: '95vw', maxHeight: '82vh', objectFit: 'contain',
          borderRadius: 8, boxShadow: '0 6px 32px rgba(0,0,0,0.5)',
          zIndex: 1,
          cursor: zoom > 1 ? 'grab' : 'default',
          transform: `rotate(${rotacao}deg) scale(${zoom})`,
          transition: 'transform 0.2s ease',
          transformOrigin: 'center center',
        }}
      />
      )}
    </div>
  );
}

// Modal pra atendente iniciar uma conversa "outbound": busca contato existente
// ou cadastra um novo na hora, escreve a primeira mensagem e dispara via
// POST /atendimentos/iniciar. A conversa nasce em_atendimento com o atendente
// vinculado e bot calado - quando o cliente responder o bot nao mostra menu.
function NovaConversaModal({ aberto, onClose, onCriada }) {
  const textoRef = useRef(null); // campo da mensagem — usado pelas mensagens prontas
  const [busca, setBusca] = useState('');
  const [contatos, setContatos] = useState([]);
  const [loadingContatos, setLoadingContatos] = useState(false);
  const [contatoSel, setContatoSel] = useState(null);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  // Cadastro inline de novo contato
  const [criandoNovo, setCriandoNovo] = useState(false);
  const [canais, setCanais] = useState([]);
  const [filiais, setFiliais] = useState([]);
  const [setores, setSetores] = useState([]);
  const [novoForm, setNovoForm] = useState({ canal_id: '', numero: '', push_name: '', filial: '', setor: '' });
  const { user } = useAuth();
  const meuCanal = user?.canal_id ?? null;
  // Canais em que o usuario pode AGIR (= onde pode iniciar conversa outbound):
  // padrao + extras se assumir_outros. Ver [[multicanal-atendimento]].
  const canaisAtuar = (() => {
    const padrao = meuCanal != null ? Number(meuCanal) : null;
    const temNovo = Array.isArray(user?.canais_extra);
    const extras = temNovo ? user.canais_extra.map(Number).filter(Boolean) : [];
    const assumir = !!user?.assumir_outros;
    const dedupe = (a) => [...new Set(a.filter((v) => v != null))];
    return padrao != null ? dedupe([padrao, ...(assumir ? extras : [])]) : [];
  })();
  const multiCanalAtuar = canaisAtuar.length > 1;

  useEffect(() => {
    if (!aberto) return;
    setBusca(''); setContatoSel(null); setTexto(''); setErro('');
    setCriandoNovo(false);
    setNovoForm({ canal_id: '', numero: '', push_name: '', filial: '', setor: '' });
    (async () => {
      setLoadingContatos(true);
      try {
        const [r1, r2, r3, r4] = await Promise.all([
          axios.get(`${API}/contatos`),
          axios.get(`${API}/atendimento/canais`).catch(() => ({ data: { data: [] } })),
          axios.get(`${API}/atendimento/filiais`, { params: { ativos: 'true' } }).catch(() => ({ data: { data: [] } })),
          axios.get(`${API}/atendimento/setores`, { params: { ativos: 'true' } }).catch(() => ({ data: { data: [] } })),
        ]);
        setContatos(r1.data?.data || []);
        const cs = r2.data?.data || [];
        setCanais(cs);
        setFiliais(r3.data?.data || []);
        setSetores(r4.data?.data || []);
        setNovoForm((f) => ({ ...f, canal_id: meuCanal || canaisAtuar[0] || cs[0]?.id || '' }));
      } catch (e) {
        setErro('Não foi possível carregar contatos.');
      } finally {
        setLoadingContatos(false);
      }
    })();
  }, [aberto]);

  // Busca client-side
  const filtrados = useMemo(() => {
    if (!busca) return contatos;
    const q = busca.toLowerCase();
    return contatos.filter((c) =>
      String(c.push_name || '').toLowerCase().includes(q)
      || String(c.numero || '').includes(q),
    );
  }, [contatos, busca]);

  // Carrega a foto de quem está aparecendo na lista (só os primeiros — a agenda
  // tem milhares e o usuário só enxerga um punhado por vez).
  useEffect(() => {
    if (!aberto || !filtrados.length) return undefined;
    let vivo = true;
    const t = setTimeout(async () => {
      const fotos = await buscarFotosFaltando(filtrados.slice(0, 20)); // o teto real é o do helper
      if (!vivo || !Object.keys(fotos).length) return;
      setContatos((atual) => atual.map((c) => (fotos[c.id] ? { ...c, profile_pic_url: fotos[c.id] } : c)));
    }, 350);
    return () => { vivo = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, busca, contatos.length]);

  const [verificandoNum, setVerificandoNum] = useState(false);

  const cadastrarENovo = async () => {
    if (!novoForm.canal_id || !novoForm.numero) {
      setErro('Canal e número são obrigatórios.'); return;
    }
    setErro('');
    // Confere se o número tem WhatsApp e traz o nome do perfil (evita iniciar
    // conversa com número errado). Se a checagem falhar, não bloqueia o cadastro.
    let nomeWa = null;
    setVerificandoNum(true);
    try {
      const chk = await axios.get(`${API}/atendimentos/checar-numero`, {
        params: { canal_id: Number(novoForm.canal_id), numero: novoForm.numero },
      });
      const d = chk.data?.data;
      if (d && d.existe === false) {
        setErro('Esse número não tem WhatsApp. Confira o DDD e o número.');
        setVerificandoNum(false);
        return;
      }
      nomeWa = d?.nome || null;
    } catch (_) { /* checagem indisponível: segue sem bloquear */ }
    setVerificandoNum(false);
    const nomeFinal = (novoForm.push_name?.trim() || nomeWa || '').trim();
    if (!nomeFinal) { setErro('Nome é obrigatório.'); return; }
    try {
      const res = await axios.post(`${API}/contatos`, {
        canal_id: Number(novoForm.canal_id),
        numero: novoForm.numero,
        push_name: nomeFinal,
        filial: novoForm.filial.trim() || null,
        setor: novoForm.setor.trim() || null,
      });
      const novo = res.data?.data;
      if (novo) {
        setContatos((prev) => [novo, ...prev.filter((c) => c.id !== novo.id)]);
        setContatoSel(novo);
        setCriandoNovo(false);
      }
    } catch (e) {
      setErro(e.response?.data?.message || 'Falha ao cadastrar contato.');
    }
  };

  const enviar = async () => {
    if (!contatoSel) { setErro('Selecione um contato.'); return; }
    if (!texto.trim()) { setErro('Escreva a mensagem inicial.'); return; }
    setEnviando(true); setErro('');
    try {
      const res = await axios.post(`${API}/atendimentos/iniciar`, {
        contato_id: contatoSel.id,
        // Canal da agenda em que o contato foi escolhido (é por lá que sai).
        canal_id: contatoSel.canal_id,
        texto: texto.trim(),
      });
      onCriada?.(res.data?.data);
    } catch (e) {
      setErro(e.response?.data?.message || 'Falha ao iniciar conversa.');
    } finally {
      setEnviando(false);
    }
  };

  if (!aberto) return null;
  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(8, 23, 43, 0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        className="content-card"
        style={{
          width: 'min(560px, 96vw)', padding: 0, display: 'flex',
          flexDirection: 'column', maxHeight: '90vh', overflow: 'hidden',
        }}
      >
        <header style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '14px 18px', borderBottom: '1px solid var(--portal-border)',
        }}>
          <strong>
            <i className="bi bi-plus-circle me-2" /> Nova conversa
          </strong>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={onClose} disabled={enviando}
          >
            <i className="bi bi-x-lg" />
          </button>
        </header>

        <div style={{ padding: 16, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {erro && (
            <div className="feedback-banner feedback-banner--error" style={{ fontSize: '0.85rem' }}>
              {erro}
            </div>
          )}

          {!criandoNovo ? (
            <>
              <div>
                <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>Contato</label>
                <input
                  type="search"
                  className="form-control"
                  placeholder="Buscar por nome ou número..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  autoFocus
                />
                <div style={{
                  marginTop: 6, maxHeight: 180, overflowY: 'auto',
                  border: '1px solid var(--portal-border)', borderRadius: 6,
                  background: 'var(--portal-surface)',
                }}>
                  {loadingContatos ? (
                    <div style={{ padding: 12, textAlign: 'center' }}>
                      <div className="screen-loader__spinner" />
                    </div>
                  ) : filtrados.length === 0 ? (
                    <div style={{ padding: 12, textAlign: 'center', color: 'var(--portal-muted)', fontSize: '0.85rem' }}>
                      Nenhum contato. Cadastre um novo abaixo.
                    </div>
                  ) : filtrados.map((c) => {
                    const ativo = contatoSel?.id === c.id;
                    // Bot so faz intake quando falta NOME; filial/setor sao
                    // preenchidos pelo atendente e nao disparam o alerta.
                    const semNome = !c.push_name;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setContatoSel(c)}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left',
                          padding: '8px 12px',
                          background: ativo ? 'rgba(61, 139, 55, 0.18)' : 'transparent',
                          border: 'none', borderBottom: '1px solid var(--portal-border)',
                          color: 'var(--portal-text)', cursor: 'pointer', fontSize: '0.85rem',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                          <Avatar nome={c.push_name} numero={c.numero} fotoUrl={c.profile_pic_url} size={32} />
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ display: 'block' }}>
                              <strong>{c.push_name || '(sem nome)'}</strong>
                              {semNome && (
                                <i
                                  className="bi bi-exclamation-triangle ms-2"
                                  title="Contato sem nome - bot fará a identificação na próxima conversa"
                                  style={{ color: '#F5A623' }}
                                />
                              )}
                              <span style={{ marginLeft: 8, color: 'var(--portal-muted)' }}>
                                {c.numero}
                              </span>
                            </span>
                            <span style={{ display: 'block', fontSize: '0.72rem', color: 'var(--portal-muted)' }}>
                              {[c.filial, c.setor].filter(Boolean).join(' · ') || 'sem filial/setor'}
                            </span>
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-info mt-2"
                  onClick={() => setCriandoNovo(true)}
                >
                  <i className="bi bi-plus-lg me-1" /> Cadastrar novo contato
                </button>
              </div>

              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                    Mensagem inicial
                  </label>
                  {/* Mensagem padrão (cobrança etc) já com o nome do contato no
                      lugar de {nome} — é onde o texto pronto mais rende. */}
                  <RespostasRapidas
                    value={texto}
                    inputRef={textoRef}
                    variaveis={{ nomeCliente: contatoSel?.push_name, atendente: user?.nome || user?.usuario }}
                    onInserir={(pronto) => {
                      setTexto((cur) => {
                        const ehTrigger = /^\/\S*$/.test(cur || '');
                        if (!pronto) return ehTrigger ? '' : cur; // Esc limpa o gatilho
                        if (!cur || ehTrigger) return pronto;
                        return `${cur.trimEnd()} ${pronto}`;
                      });
                      setTimeout(() => textoRef.current?.focus(), 30);
                    }}
                  />
                </div>
                <textarea
                  ref={textoRef}
                  className="form-control"
                  rows={4}
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  placeholder="Digite a primeira mensagem, ou use o raio (⚡) para uma mensagem pronta..."
                />
                <div style={{ fontSize: '0.72rem', color: 'var(--portal-muted)', marginTop: 3 }}>
                  Nas mensagens prontas, <code>{'{nome}'}</code> já vem trocado pelo primeiro nome do
                  contato — dá pra ajustar aqui antes de enviar.
                </div>
              </div>
            </>
          ) : (
            <div style={{
              padding: 12, borderRadius: 8,
              border: '1px solid var(--portal-border)',
              background: 'rgba(61, 139, 55, 0.08)',
              display: 'flex', flexDirection: 'column', gap: 10,
            }}>
              <strong style={{ fontSize: '0.9rem' }}>
                <i className="bi bi-person-plus me-2" /> Cadastrar novo contato
              </strong>
              <div>
                <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>Canal</label>
                {multiCanalAtuar ? (
                  <select
                    className="form-select"
                    value={novoForm.canal_id}
                    onChange={(e) => setNovoForm({ ...novoForm, canal_id: e.target.value })}
                  >
                    {canais.filter((c) => canaisAtuar.includes(c.id)).map((c) => (
                      <option key={c.id} value={c.id}>{c.nome}</option>
                    ))}
                  </select>
                ) : (
                  <div className="form-control" style={{ background: 'rgba(0,0,0,0.04)', color: 'var(--portal-muted)' }}>
                    <i className="bi bi-whatsapp me-2" style={{ color: '#25D366' }} />
                    {canais.find((c) => c.id === Number(novoForm.canal_id || meuCanal))?.nome || 'Seu canal'}
                  </div>
                )}
              </div>
              <div>
                <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                  Número (com DDD)
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={novoForm.numero}
                  onChange={(e) => setNovoForm({ ...novoForm, numero: e.target.value })}
                  placeholder="Ex: 21965868915"
                />
              </div>
              <div>
                <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                  Nome <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={novoForm.push_name}
                  onChange={(e) => setNovoForm({ ...novoForm, push_name: e.target.value })}
                />
              </div>
              <div className="row g-2" style={{ margin: 0 }}>
                <div className="col-12 col-sm-6" style={{ paddingLeft: 0 }}>
                  <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                    Filial (opcional)
                  </label>
                  <select
                    className="form-select"
                    value={novoForm.filial}
                    onChange={(e) => setNovoForm({ ...novoForm, filial: e.target.value })}
                  >
                    <option value="">Não informar</option>
                    {filiais.map((f) => (
                      <option key={f.id || f.nome} value={f.nome}>
                        {f.codigo ? `${f.codigo} - ${f.nome}` : f.nome}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-sm-6" style={{ paddingRight: 0 }}>
                  <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                    Setor (opcional)
                  </label>
                  <select
                    className="form-select"
                    value={novoForm.setor}
                    onChange={(e) => setNovoForm({ ...novoForm, setor: e.target.value })}
                  >
                    <option value="">Não informar</option>
                    {setores.map((s) => (
                      <option key={s.id || s.nome} value={s.nome}>{s.nome}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setCriandoNovo(false)}
                >
                  Voltar
                </button>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={cadastrarENovo}
                  disabled={verificandoNum}
                  style={{ background: '#3D8B37', color: '#fff', border: 'none', opacity: verificandoNum ? 0.6 : 1 }}
                >
                  <i className={`bi ${verificandoNum ? 'bi-hourglass-split' : 'bi-check2'} me-1`} />
                  {verificandoNum ? ' Verificando número...' : ' Cadastrar e selecionar'}
                </button>
              </div>
            </div>
          )}
        </div>

        <footer style={{
          padding: '12px 18px', borderTop: '1px solid var(--portal-border)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
        }}>
          <span style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
            {contatoSel
              ? <><i className="bi bi-person-check" /> Selecionado: <strong>{contatoSel.push_name || contatoSel.numero}</strong></>
              : 'Selecione um contato'}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={onClose}
              disabled={enviando}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-sm"
              onClick={enviar}
              disabled={enviando || !contatoSel || !texto.trim()}
              style={{ background: '#3D8B37', color: '#fff', border: 'none' }}
            >
              <i className="bi bi-send me-1" />
              {enviando ? 'Enviando...' : 'Iniciar conversa'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function MultiSelectDropdown({ label, options, selected, onChange, disabled }) {
  const [aberto, setAberto] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!aberto) return undefined;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setAberto(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [aberto]);

  const ativo = selected.length > 0;
  const btnLabel = ativo ? `${label} (${selected.length})` : label;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setAberto((v) => !v)}
        style={{
          fontSize: '0.7rem',
          padding: '4px 10px',
          borderRadius: 6,
          border: `1px solid ${ativo ? '#3D8B37' : 'var(--portal-border)'}`,
          background: ativo ? 'rgba(61, 139, 55,0.18)' : 'transparent',
          color: ativo ? '#3D8B37' : 'var(--portal-text)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          whiteSpace: 'nowrap',
          fontWeight: ativo ? 600 : 400,
        }}
      >
        {btnLabel}
        <i className={`bi bi-chevron-${aberto ? 'up' : 'down'}`} style={{ fontSize: '0.6rem' }} />
      </button>
      {aberto && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 4,
          zIndex: 100,
          width: 220,
          background: 'var(--portal-surface)',
          border: '1px solid var(--portal-border)',
          borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          overflow: 'hidden',
        }}>
          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
            {options.length === 0 ? (
              <div style={{ padding: '8px 12px', fontSize: '0.8rem', color: 'var(--portal-muted)' }}>
                Nenhuma opção disponível
              </div>
            ) : options.map((opt) => {
              const marcado = selected.includes(opt.value);
              return (
                <label
                  key={opt.value}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 12px',
                    cursor: 'pointer',
                    fontSize: '0.82rem',
                    background: 'transparent',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--portal-border)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <input
                    type="checkbox"
                    checked={marcado}
                    onChange={() => {
                      if (marcado) onChange(selected.filter((v) => v !== opt.value));
                      else onChange([...selected, opt.value]);
                    }}
                    style={{ accentColor: '#3D8B37', cursor: 'pointer' }}
                  />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {opt.label}
                  </span>
                </label>
              );
            })}
          </div>
          {selected.length > 0 && (
            <div style={{ borderTop: '1px solid var(--portal-border)', padding: '4px 8px' }}>
              <button
                type="button"
                onClick={() => { onChange([]); setAberto(false); }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  color: 'var(--portal-muted)',
                  padding: '2px 4px',
                }}
              >
                Limpar
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Atendimentos() {
  const { user, temPermissao, refreshSession } = useAuth();
  const [prefs] = usePrefs();
  const podeUsar = temPermissao('ti:atendimentos');

  // Ao abrir a tela, reemite o token com o escopo de canais atual do banco.
  // Garante que mudancas de canal/assumir_outros valham sem precisar relogar.
  useEffect(() => {
    refreshSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Lembra o filtro de canal escolhido pelo supervisor (por usuario).
  const filtroCanalKey = `ti_atend_canais_filtro_${user?.id || 'anon'}`;
  // Lembra as camadas "Ver junto" (Por fora / Time) — quem trabalha com elas
  // ligadas não quer religar a cada vez que abre a tela.
  const verJuntoKey = `ti_atend_verjunto_${user?.id || 'anon'}`;
  // Lembra a fila filtrada — quem trabalha só numa fila não quer reclicar toda vez.
  const filaFiltroKey = `ti_atend_fila_${user?.id || 'anon'}`;
  const verJuntoSalvo = (() => {
    try { return JSON.parse(localStorage.getItem(verJuntoKey)) || {}; } catch { return {}; }
  })();

  const [conversas, setConversas] = useState([]);
  const [loadingLista, setLoadingLista] = useState(true);
  const [erro, setErro] = useState('');
  // Filtros iniciam no padrão configurado pelo usuário (Configurações → Filtros padrão).
  const [statusFiltro, setStatusFiltro] = useState(() => {
    const f = getPrefs().filtroStatus;
    // migra valores antigos (''/'aberta'/'em_atendimento') pro novo 'aberto'.
    return f === 'encerrada' ? 'encerrada' : 'aberto';
  });
  // Filtro de fila: '' = todas; '__pendente__' = sem fila; senão o ID da fila.
  // Lembra a última escolha (igual "Ver junto"); sem nada salvo, cai no padrão
  // de Configurações → Filtros padrão.
  const [setorFiltro, setSetorFiltro] = useState(() => {
    try {
      const salvo = localStorage.getItem(filaFiltroKey);
      if (salvo != null) return salvo;
    } catch { /* localStorage indisponivel */ }
    return getPrefs().filtroFila || '';
  });
  const [vista, setVista] = useState('minha'); // minha | pendente | sem_dono | time
  const [pendenteCount, setPendenteCount] = useState(0);
  const [semDonoCount, setSemDonoCount] = useState(0);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);

  // Quais grupos de filtro ficam SEMPRE à vista, fora do painel. É preferência
  // de gente, guardada no servidor (ver sql/097): quem trabalha por fila quer a
  // fila à mão, quem cuida de campanha quer as tags, e quem só atende a própria
  // fila não quer nenhum dos dois ocupando tela.
  const [filtrosFixados, setFiltrosFixados] = useState(['ver_junto']);
  const [tagFiltro, setTagFiltro] = useState('');

  useEffect(() => {
    if (!podeUsar) return;
    axios.get(`${API}/preferencias`)
      .then((r) => {
        const f = r.data?.data?.filtros_fixados;
        if (Array.isArray(f)) setFiltrosFixados(f);
        // Assinatura lembrada (sql/099). Vem no mesmo pedido: a tela já busca
        // as preferências uma vez ao abrir.
        const a = r.data?.data?.assinatura_ativa;
        if (typeof a === 'boolean') setAssinaturaAtiva(a);
      })
      .catch(() => { /* preferência de tela não derruba a lista */ });
  }, [podeUsar]);

  const alternarFixado = async (chave) => {
    const novo = filtrosFixados.includes(chave)
      ? filtrosFixados.filter((x) => x !== chave)
      : [...filtrosFixados, chave];
    setFiltrosFixados(novo); // responde na hora; o servidor confirma depois
    try {
      await axios.put(`${API}/preferencias/filtros`, { filtros_fixados: novo });
    } catch (e) {
      setFiltrosFixados(filtrosFixados); // reconcilia com a verdade do servidor
      alertarErro(e, 'Não deu pra salvar a preferência');
    }
  };

  // Um grupo aparece se a pessoa fixou OU se o painel está aberto.
  const mostraGrupo = (chave) => filtrosFixados.includes(chave) || filtrosAbertos;
  // "Minha fila" é composável: camadas somadas por cima (multi-seleção).
  const [incluirPorFora, setIncluirPorFora] = useState(!!verJuntoSalvo.porFora);
  const [incluirTime, setIncluirTime] = useState(!!verJuntoSalvo.time);
  const [busca, setBusca] = useState('');
  // Busca estilo WhatsApp: além das conversas da lista, procura na AGENDA. Assim
  // dá pra começar uma conversa nova digitando o nome, sem abrir "Nova conversa".
  const [contatosBusca, setContatosBusca] = useState([]);
  const [buscandoContatos, setBuscandoContatos] = useState(false);
  // Quantos contatos a busca traz. Começa em 20 e sobe pro teto quando o
  // usuário pede "Ver todos" — 200 é a proteção do servidor: acima disso a
  // busca está genérica demais e o certo é digitar mais.
  const LIMITE_PADRAO = 20;
  const LIMITE_MAX = 200;
  const [limiteContatos, setLimiteContatos] = useState(LIMITE_PADRAO);
  // Visões principais (modos exclusivos): Minha fila / Pendente / Encerradas.
  const aplicarView = (key) => {
    // 'encerradas' tem vista própria no backend: histórico do que EU enxergo
    // (minhas + sem dono + colegas de fila colaborativa), não só as minhas.
    if (key === 'encerradas') { setVista('encerradas'); setStatusFiltro('encerrada'); return; }
    setStatusFiltro('aberto');
    setVista(key); // 'minha' | 'pendente'
  };
  const viewAtual = statusFiltro === 'encerrada' ? 'encerradas'
    : vista === 'pendente' ? 'pendente' : 'minha';
  // Janela de atendimento + relogio para o cronometro de TTFR ao vivo.
  // O backend manda os horarios junto com a lista (meta.horarios). O `agora`
  // tica a cada 1s para o cronometro renderizar suavemente.
  const [horarios, setHorarios] = useState([]);
  const [horarioAtivo, setHorarioAtivo] = useState(false);
  // Pedir motivo ao encerrar? Desligado = encerra em 1 clique (config da loja).
  const [exigirMotivo, setExigirMotivo] = useState(false);
  // SLA TTFR (1a resposta) e TTR (solucao) por prioridade vem do meta da
  // listagem. Cronometro usa o TTFR enquanto nao houve primeira resposta;
  // depois muda pra TTR ate o chamado ser resolvido.
  const [slaTtfrMap, setSlaTtfrMap] = useState({});
  const [slaTtrMap, setSlaTtrMap] = useState({});
  // Som ao chegar mensagem vem das preferências do usuário (por aparelho).
  const somNovaMensagem = somConfigDe(prefs);
  const [agora, setAgora] = useState(() => new Date());
  // Filtro por atendente (multi-select). [] = todos.
  const [atendentesFiltro, setAtendentesFiltro] = useState([]);
  // Filtro por canal (multi-select, enviado ao backend via canal_ids). [] = todos.
  // Inicializa com a ultima escolha salva do usuario (supervisor).
  const [canaisFiltro, setCanaisFiltro] = useState(() => {
    try {
      const raw = localStorage.getItem(filtroCanalKey);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.map(Number).filter(Boolean) : [];
    } catch {
      return [];
    }
  });
  const [canais, setCanais] = useState([]);
  const [filas, setFilas] = useState([]); // setores/filas ativos (Caixa, Crediario, ...)

  // Mobile (estilo WhatsApp): uma coluna por vez — lista OU conversa aberta.
  //
  // COMPACTO é a faixa do meio, onde mais gente trabalha do que parece: iPad em
  // pé (820-834), iPad deitado (1024-1194) e notebook com a janela dividida.
  // Ali ainda cabem as duas colunas, mas não cabe o cabeçalho inteiro com os
  // rótulos dos botões — sem esse degrau o bloco do contato era espremido até
  // o telefone quebrar em "+55 / (32) / 99916- / 8902", uma letra por linha.
  const larguraDaJanela = () => (typeof window === 'undefined' ? 1440 : window.innerWidth);
  const [largura, setLargura] = useState(larguraDaJanela);
  useEffect(() => {
    const onResize = () => setLargura(window.innerWidth);
    window.addEventListener('resize', onResize);
    // orientationchange: o iPad girado dispara antes do resize valer.
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);
  // `pointer: coarse` = o ponteiro principal é o DEDO (celular, tablet). Notebook
  // com tela touch continua `fine`, porque ali o primário é o mouse — por isso a
  // regra é essa e não "largura > X".
  const [toque] = useState(() => (typeof window !== 'undefined'
    && !!window.matchMedia?.('(pointer: coarse)').matches));

  // Tela estreita OU tablet de toque usam o mesmo layout do WhatsApp: uma coisa
  // por vez, lista OU conversa. Em tablet, mostrar as duas colunas juntas
  // espremia a conversa até ficar inútil — e o `100vh` do modo desktop conta a
  // barra do navegador, o que empurrava a barra de digitar pra baixo da dobra.
  // O teto de 1400 cobre tablet deitado (1024, 1280, 1366) sem alcançar monitor.
  const isMobile = largura <= 768 || (toque && largura <= 1400);
  const isCompacto = largura <= 1200;

  // Presença fica no canto superior direito no toque: sem a sidebar, era o
  // único jeito de a atendente ver (e trocar) se está disponível.
  const {
    status: presenca, ausente, podeAtender: podeAtenderPresenca, toggleAusente,
  } = usePresenca();

  // Altura da área visível quando o teclado abre (iOS não encolhe o 100dvh
  // sozinho -> sobra espaço). Com o visualViewport, a tela imersiva cola no
  // teclado, sem o vão. null = teclado fechado (usa 100dvh normal).
  const [areaVisivel, setAreaVisivel] = useState(null);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const onVV = () => {
      const tecladoAberto = (window.innerHeight - vv.height) > 120;
      setAreaVisivel(tecladoAberto ? Math.round(vv.height) : null);
    };
    vv.addEventListener('resize', onVV);
    vv.addEventListener('scroll', onVV);
    return () => { vv.removeEventListener('resize', onVV); vv.removeEventListener('scroll', onVV); };
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  const navegar = useNavigate();

  const [conversaAtivaId, setConversaAtivaId] = useState(null);
  const [conversaAtiva, setConversaAtiva] = useState(null);

  // Conversa imersiva no mobile: quando uma conversa está aberta, ligamos a
  // classe no body que esconde a barra do topo e vai full-bleed (ver index.css).
  // (Precisa vir DEPOIS de declarar conversaAtivaId — senão TDZ e tela preta.)
  useEffect(() => {
    const imersivo = isMobile && !!conversaAtivaId;
    document.body.classList.toggle('conversa-imersiva', imersivo);
    return () => document.body.classList.remove('conversa-imersiva');
  }, [isMobile, conversaAtivaId]);
  const [loadingDetalhe, setLoadingDetalhe] = useState(false);
  const [texto, setTexto] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  // Assinatura (o `*Nome:*` que vai no início da mensagem no WhatsApp do
  // cliente). Desligável na hora, pela barra de digitar, quando o atendente
  // quer mandar recado no lugar de outra pessoa ou continuar tom formal.
  //
  // LEMBRADA por pessoa, no servidor (sql/099). Antes morava só nesta tela e
  // voltava a ligar a cada troca de aba — Conversas, Grupos, Contatos e Mais
  // são rotas separadas, e sair daqui recriava a tela ligada. Era proteção
  // contra esquecer desligada; quem protege agora é o aviso laranja em cima do
  // campo, que aparece SEMPRE que ela está desligada, com o botão de religar.
  const [assinaturaAtiva, setAssinaturaAtiva] = useState(true);
  const salvarAssinatura = (ativa) => {
    setAssinaturaAtiva(ativa); // responde na hora; o servidor confirma depois
    axios.put(`${API}/preferencias/assinatura-ativa`, { ativa })
      .catch((e) => {
        setAssinaturaAtiva(!ativa);
        alertarErro(e, 'Não deu pra salvar a assinatura');
      });
  };
  // Quem assina é escolha de cada um, em Configurações: o próprio nome ou a
  // função (Crediário, Caixa). A tela precisa saber pra não prometer "seu nome"
  // pra quem escolheu esconder justamente o nome.
  const [assinaturaComo, setAssinaturaComo] = useState('seu nome');
  useEffect(() => {
    axios.get(`${API}/preferencias`)
      .then((r) => {
        const d = r.data?.data || {};
        if (d.assinatura_modo !== 'funcao') return;
        // Uma função só: dá pra dizer qual. Mais de uma (caixa E crediário),
        // muda conforme a conversa — então fica no genérico mesmo.
        const funcoes = d.assinatura_previa?.funcoes || [];
        setAssinaturaComo(funcoes.length === 1 ? funcoes[0] : 'sua função');
      })
      .catch(() => {}); // sem preferência = o padrão de sempre (o nome)
  }, []);
  const [chamadosVinculados, setChamadosVinculados] = useState([]);
  const [chamadoEmFoco, setChamadoEmFoco] = useState(null);
  const [encerrarAberto, setEncerrarAberto] = useState(false);
  // Faxina da bandeja: marcar várias conversas e encerrar de uma vez. Nasceu
  // porque ninguém encerra uma a uma — em 23/09/2026 o canal do Victor tinha
  // 184 abertas sem dono, 106 paradas havia mais de 7 dias.
  const [menuListaAberto, setMenuListaAberto] = useState(false);
  const [selecionando, setSelecionando] = useState(false);
  const [selecionadas, setSelecionadas] = useState(() => new Set());
  const [encerrandoLote, setEncerrandoLote] = useState(false);
  const [encerrarLoteAberto, setEncerrarLoteAberto] = useState(false);
  const [modosEncerrar, setModosEncerrar] = useState([]);
  const [consultaClienteAberta, setConsultaClienteAberta] = useState(false);
  const [consultaProdutoAberta, setConsultaProdutoAberta] = useState(false);
  const [historicoAberto, setHistoricoAberto] = useState(false);
  const [orcamentoAberto, setOrcamentoAberto] = useState(false);
  const [menuTransferirAberto, setMenuTransferirAberto] = useState(false);
  const [arquivoSelecionado, setArquivoSelecionado] = useState(null);
  const inputArquivoRef = useRef(null);
  // Input separado só pra câmera. Não dá pra reaproveitar o do clipe: o
  // `capture` fica no ELEMENTO, e pôr nele faria o clipe também abrir a câmera
  // em vez de deixar escolher arquivo.
  const inputCameraRef = useRef(null);
  const inputDocumentoRef = useRef(null);
  const [gravando, setGravando] = useState(false);
  const [tempoGravacao, setTempoGravacao] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerGravacaoRef = useRef(null);
  const canceladoRef = useRef(false);
  const conversaParaEnvioRef = useRef(null);
  // URL da imagem atualmente aberta no lightbox; null = fechado.
  const [lightboxUrl, setLightboxUrl] = useState(null);
  // Buscar dentro da conversa (localiza mensagens na thread aberta).
  const [buscaConversa, setBuscaConversa] = useState('');
  const [buscaConversaAberta, setBuscaConversaAberta] = useState(false);
  const [buscaIdx, setBuscaIdx] = useState(0);
  // Encaminhar: mensagem selecionada + busca no seletor de destino.
  const [encaminharAlvo, setEncaminharAlvo] = useState(null);
  const [encaminharBusca, setEncaminharBusca] = useState('');
  // Agenda no encaminhar: sem isto só dá pra encaminhar pra quem JÁ tem conversa
  // aberta — e o caso comum é justamente mandar pra alguém que ainda não tem.
  const [encaminharContatos, setEncaminharContatos] = useState([]);
  const [encaminharBuscandoAgenda, setEncaminharBuscandoAgenda] = useState(false);
  // Qual destino está sendo enviado AGORA (a chave da linha), ou null.
  //
  // Encaminhar demora: pra contato que ainda não tem conversa são dois pedidos
  // em sequência (abrir a conversa, depois encaminhar). Sem sinal nenhum na
  // tela, o atendente acha que não pegou o clique e clica de novo — e aí
  // encaminha duas vezes. O estado serve pras duas coisas: mostrar que está
  // indo, e recusar o segundo clique.
  const [encaminhando, setEncaminhando] = useState(null);
  const [novaConversaAberta, setNovaConversaAberta] = useState(false);
  const [chatInternoAberto, setChatInternoAberto] = useState(false);
  const [equipeNaoLidas, setEquipeNaoLidas] = useState(0);
  const [contatoSidebarAberta, setContatoSidebarAberta] = useState(false);
  // null = ainda não sabemos · true = número sem WhatsApp (bloqueia escrever)
  const [semWhatsapp, setSemWhatsapp] = useState(null);
  // Ver o grid da tela: em tela apertada a ficha do contato e a lista disputam
  // o mesmo espaço, e quem sai é a lista.
  const listaEscondidaPelaFicha = !isMobile && isCompacto && contatoSidebarAberta;
  const [acoesMenuAberto, setAcoesMenuAberto] = useState(false);

  // Paleta das tags. Cores escolhidas pra se distinguirem umas das outras E
  // pra terem contraste com texto branco em cima — tag é lida de relance, numa
  // linha de lista, não olhada de perto.
  const CORES_TAG = [
    '#3d8b37', '#1d6f88', '#8a6d3b', '#7a4b8a', '#c0392b', '#0f766e',
    '#b45309', '#4f46e5', '#be185d', '#15803d', '#0369a1', '#a16207',
  ];

  // Sorteia entre as que AINDA NÃO estão em uso. Criar cinco tags e receber
  // cinco vezes o mesmo verde é o mesmo que não ter cor.
  const corNovaTag = (jaUsadas) => {
    const usadas = new Set((jaUsadas || []).map((t) => (t.cor || '').toLowerCase()));
    const livres = CORES_TAG.filter((c) => !usadas.has(c));
    const pool = livres.length ? livres : CORES_TAG; // esgotou: repete, mas sorteando
    return pool[Math.floor(Math.random() * pool.length)];
  };

  // Tags DENTRO da conversa. É aqui que a informação nasce: o vendedor descobre
  // "esse tá cotando com outro" falando com a pessoa, não abrindo cadastro.
  // Obrigar a sair da conversa pra marcar é garantir que ninguém marque.
  const [minhasTags, setMinhasTags] = useState([]);
  const [tagsDaConversa, setTagsDaConversa] = useState([]);
  const [tagsAberto, setTagsAberto] = useState(false);

  useEffect(() => {
    if (!podeUsar) return;
    axios.get(`${API}/tags`).then((r) => setMinhasTags(r.data?.data || [])).catch(() => {});
  }, [podeUsar]);

  useEffect(() => {
    const contatoId = conversaAtiva?.contato_id;
    if (!contatoId) { setTagsDaConversa([]); return; }
    axios.get(`${API}/contatos/${contatoId}/tags`)
      .then((r) => setTagsDaConversa(r.data?.data || []))
      .catch(() => setTagsDaConversa([]));
  }, [conversaAtiva?.contato_id]);

  const alternarTagDaConversa = async (tag) => {
    const contatoId = conversaAtiva?.contato_id;
    if (!contatoId) return;
    const marcada = tagsDaConversa.some((t) => t.id === tag.id);
    try {
      const r = marcada
        ? await axios.delete(`${API}/contatos/${contatoId}/tags/${tag.id}`)
        : await axios.post(`${API}/contatos/${contatoId}/tags`, { tag_id: tag.id });
      setTagsDaConversa(r.data?.data || []);
    } catch (e) { alertarErro(e, 'Não deu pra mudar a tag'); }
  };

  // Criar do zero sem sair da conversa: o vocabulário nasce do atendimento, e
  // ir num cadastro pra criar "Cotando com outro" e voltar mata o hábito.
  const criarTagNaConversa = async () => {
    const { value: nome } = await Swal.fire({
      title: 'Nova tag', input: 'text', inputPlaceholder: 'Ex.: Cotando com outro',
      showCancelButton: true, confirmButtonText: 'Criar e marcar', cancelButtonText: 'Cancelar',
      confirmButtonColor: '#3D8B37',
      inputValidator: (v) => (!String(v || '').trim() ? 'Dê um nome pra tag.' : undefined),
    });
    if (!nome) return;
    try {
      const criada = (await axios.post(`${API}/tags`, {
        nome: nome.trim(), cor: corNovaTag(minhasTags),
      })).data?.data;
      setMinhasTags((atuais) => [...atuais, criada].sort((a2, b) => a2.nome.localeCompare(b.nome)));
      if (criada?.id) await alternarTagDaConversa(criada);
    } catch (e) { alertarErro(e, 'Não deu pra criar a tag'); }
  };
  const [contatoSidebarForm, setContatoSidebarForm] = useState({ push_name: '', filial: '', setor: '' });
  const [contatoSidebarFiliais, setContatoSidebarFiliais] = useState([]);
  const [contatoSidebarSetores, setContatoSidebarSetores] = useState([]);
  const [contatoSidebarSalvando, setContatoSidebarSalvando] = useState(false);
  const [contatoSidebarErro, setContatoSidebarErro] = useState('');

  const textareaRef = useRef(null);
  const rascunhosRef = useRef({});   // texto digitado por conversa (rascunho)
  const prevConvRef = useRef(null);  // conversa anterior, pra salvar o rascunho ao trocar
  const pendingMsgsRef = useRef([]); // mensagens otimistas ainda não confirmadas pelo servidor
  const pollListaRef = useRef(null);
  const pollDetalheRef = useRef(null);
  const mensagensRef = useRef(null);
  const totalNaoLidasRef = useRef(null);
  const somNovaMensagemRef = useRef(somNovaMensagem);
  const deveVibrarRef = useRef(deveVibrar(prefs));
  // Refs para o canal SSE acessar sempre o estado/funcoes mais recentes sem
  // reconectar a cada render ou troca de conversa.
  const conversaAtivaIdRef = useRef(null);
  const conversaAtivaNumeroRef = useRef(null);
  const presencaTimerRef = useRef(null);
  const [clientePresenca, setClientePresenca] = useState(null); // 'composing' | 'recording' | null
  const carregarDetalheRef = useRef(() => {});
  const carregarListaRef = useRef(() => {});
  const listaDebounceRef = useRef(null);
  // Deep-link "abrir conversa de origem" do chamado: quando setado, o proximo
  // carregamento de mensagens reposiciona a thread em vez de ir pro fim.
  // marcoOrigemRef guarda o instante em que o chamado nasceu (created_at);
  // posicionamos na mensagem mais proxima desse instante. Sem marco, rola pro topo.
  const rolarParaInicioRef = useRef(false);
  const marcoOrigemRef = useRef(null);

  useEffect(() => {
    somNovaMensagemRef.current = somNovaMensagem;
    deveVibrarRef.current = deveVibrar(prefs);
  }, [somNovaMensagem, prefs]);

  // Em voo: o disparo automático não empilha outro enquanto este não voltar
  // (ver helpers/rede). Quem chama por motivo — filtro, tempo real, fila de
  // envio — chama direto, sem trava.
  const listaEmVooRef = useRef(false);
  const detalheEmVooRef = useRef(false);

  const carregarLista = async (mostrarLoading = false) => {
    listaEmVooRef.current = true;
    if (mostrarLoading) setLoadingLista(true);
    try {
      const paramsBase = {};
      if (statusFiltro) paramsBase.status = statusFiltro;
      if (busca) paramsBase.busca = busca;
      if (canaisFiltro.length > 0) paramsBase.canal_ids = canaisFiltro.join(',');
      if (atendentesFiltro.length > 0 && temPermissao('ti:atendimentos:todos')) paramsBase.atendentes = atendentesFiltro.join(',');
      if (tagFiltro) paramsBase.tag_id = tagFiltro;
      // Minha fila é composável: soma "Por fora" (sem_dono) e/ou "Time" por cima.
      // Pendente/Encerradas são únicas. Mescla as respostas por id.
      const vistasFetch = viewAtual === 'minha'
        ? ['minha', ...(incluirPorFora ? ['sem_dono'] : []), ...(incluirTime ? ['time'] : [])]
        : [vista];
      const respostas = await Promise.all(
        vistasFetch.map((v) => axios.get(`${API}/atendimentos`, { params: { ...paramsBase, vista: v }, timeout: TEMPO_DE_POLL_MS })),
      );
      const porId = new Map();
      respostas.forEach((r) => (r.data?.data || []).forEach((c) => porId.set(c.id, c)));
      const lista = [...porId.values()];
      setConversas(lista);
      const meta = respostas[0]?.data?.meta;
      if (meta) {
        setHorarios(Array.isArray(meta.horarios) ? meta.horarios : []);
        setHorarioAtivo(Boolean(meta.horario_ativo));
        setSlaTtfrMap(meta.sla_ttfr_min || {});
        setSlaTtrMap(meta.sla_ttr_min || {});
      }

      const totalNaoLidas = lista.reduce((total, c) => total + (Number(c.nao_lidas) || 0), 0);
      const totalAnterior = totalNaoLidasRef.current;
      if (totalAnterior !== null && totalNaoLidas > totalAnterior) {
        playNotificationSound(somNovaMensagemRef.current).catch(() => {});
        if (deveVibrarRef.current && navigator.vibrate) navigator.vibrate(200);
      }
      totalNaoLidasRef.current = totalNaoLidas;
      setErro('');
    } catch (err) {
      setErro('Não foi possível carregar as conversas.');
    } finally {
      listaEmVooRef.current = false;
      if (mostrarLoading) setLoadingLista(false);
    }
  };

  const carregarDetalhe = async (id, mostrarLoading = false) => {
    if (!id) return;
    detalheEmVooRef.current = true;
    if (mostrarLoading) setLoadingDetalhe(true);
    try {
      const res = await axios.get(`${API}/atendimentos/${id}`, { timeout: TEMPO_DE_POLL_MS });
      const novo = res.data?.data || null;
      // Preserva midia_signed_url ja conhecida por mensagem entre polls. O
      // backend regenera a signed URL a cada GET, e se trocarmos o `src` do
      // <video>/<audio> em pleno polling de 4s o player reinicia toda vez.
      // Mantendo a mesma URL, o React nao toca em src e a reproducao segue.
      if (novo?.mensagens) {
        setConversaAtiva((prev) => {
          const prevMsgs = prev?.id === novo.id ? (prev.mensagens || []) : [];
          const prevById = new Map(prevMsgs.map((m) => [m.id, m]));
          const mensagens = novo.mensagens.map((m) => {
            const old = prevById.get(m.id);
            return old?.midia_signed_url
              ? { ...m, midia_signed_url: old.midia_signed_url }
              : m;
          });
          // Reconcilia otimistas: mantém as pendentes desta conversa que o servidor
          // ainda não devolveu (evita o "some e reaparece") e descarta as já confirmadas.
          const pend = pendingMsgsRef.current.filter((p) => p.conversaId === novo.id);
          // Um-pra-um (helpers/pendentes): cada mensagem real confirma UMA
          // pendente. Com a fila, "ok" e "ok" seguidos sumiam os dois de uma vez.
          const aindaPendentes = reconciliarPendentes(mensagens, pend);
          pendingMsgsRef.current = pendingMsgsRef.current.filter(
            (p) => p.conversaId !== novo.id || aindaPendentes.includes(p),
          );
          return { ...novo, mensagens: [...mensagens, ...aindaPendentes] };
        });
      } else {
        setConversaAtiva(novo);
      }
      // Chegaram mensagens nao lidas enquanto o chat estava aberto — zera silenciosamente.
      if (!mostrarLoading && novo?.nao_lidas > 0) {
        axios.post(`${API}/atendimentos/${id}/lida`).catch(() => {});
        // A pessoa está olhando a conversa: a notificação desta mensagem já
        // não tem o que avisar.
        fecharNotificacoesDeConversas([id]);
      }
    } catch (err) {
      setConversaAtiva(null);
    } finally {
      detalheEmVooRef.current = false;
      if (mostrarLoading) setLoadingDetalhe(false);
    }
  };

  const carregarChamadosVinculados = async (id) => {
    if (!id) {
      setChamadosVinculados([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/atendimentos/${id}/chamados-vinculados`);
      const lista = res.data?.data || [];
      setChamadosVinculados(lista);
      // Auto-foca: se conversa ja tem chamado_id e ele esta na lista, foca nele.
      // Senao, pega o primeiro da lista.
      setChamadoEmFoco((prev) => {
        if (lista.some((c) => c.id === prev)) return prev;
        return lista[0]?.id || null;
      });
    } catch (e) {
      setChamadosVinculados([]);
    }
  };

  // Carga inicial e quando os filtros mudam
  useEffect(() => {
    if (!podeUsar) {
      setLoadingLista(false);
      return;
    }
    totalNaoLidasRef.current = null;
    carregarLista(true);
  }, [podeUsar, statusFiltro, vista, busca, canaisFiltro, atendentesFiltro, incluirPorFora, incluirTime, tagFiltro]);

  useEffect(() => {
    if (!podeUsar) return undefined;
    const desbloquear = () => {
      unlockNotificationSound().catch(() => {});
    };
    window.addEventListener('pointerdown', desbloquear, { once: true });
    window.addEventListener('keydown', desbloquear, { once: true });
    return () => {
      window.removeEventListener('pointerdown', desbloquear);
      window.removeEventListener('keydown', desbloquear);
    };
  }, [podeUsar]);

  // Carrega lista de canais para o filtro (uma vez por sessao).
  useEffect(() => {
    if (!podeUsar) return;
    axios.get(`${API}/atendimento/canais`)
      .then((res) => {
        const lista = res.data?.data || [];
        setCanais(lista);
        // Saneamento: descarta do filtro qualquer canal que nao existe mais
        // (ex.: canal excluido). Sem isso, um id morto preso no localStorage
        // faz o backend intersectar para vazio e a lista some por completo.
        const idsValidos = new Set(lista.map((c) => Number(c.id)));
        setCanaisFiltro((prev) => {
          const limpo = prev.filter((id) => idsValidos.has(Number(id)));
          return limpo.length === prev.length ? prev : limpo;
        });
      })
      .catch(() => {});
  }, [podeUsar]);

  // Config do atendimento: hoje só o "pedir motivo ao encerrar".
  useEffect(() => {
    if (!podeUsar) return;
    axios.get(`${API}/atendimento/config-publica`)
      .then((r) => setExigirMotivo(Boolean(r.data?.data?.exigir_motivo_encerramento)))
      .catch(() => setExigirMotivo(false));
  }, [podeUsar]);

  // Filas/setores ativos — alimenta o filtro de Fila e o "mover de fila".
  useEffect(() => {
    if (!podeUsar) return;
    axios.get(`${API}/setores?ativos=true`).then((res) => setFilas(res.data?.data || [])).catch(() => {});
  }, [podeUsar]);

  // Termo novo recomeça mostrando 20 (o "Ver todos" vale só pra busca atual).
  useEffect(() => { setLimiteContatos(LIMITE_PADRAO); }, [busca]);

  // Procura na agenda enquanto digita (300ms de folga pra não bater no servidor a
  // cada tecla). Só a partir de 2 letras — com 1 letra vem meio mundo.
  useEffect(() => {
    const termo = busca.trim();
    if (!podeUsar || termo.length < 2) { setContatosBusca([]); setBuscandoContatos(false); return undefined; }
    setBuscandoContatos(true);
    let vivo = true;
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/contatos`, { params: { busca: termo, limite: limiteContatos } });
        const achados = r.data?.data || [];
        if (!vivo) return;
        setContatosBusca(achados);
        setBuscandoContatos(false);
        // Fotos entram depois, sem segurar a lista.
        const fotos = await buscarFotosFaltando(achados);
        if (!vivo || !Object.keys(fotos).length) return;
        setContatosBusca((atual) => atual.map((c) => (
          fotos[c.id] ? { ...c, profile_pic_url: fotos[c.id] } : c
        )));
      } catch (e) {
        if (vivo) { setContatosBusca([]); setBuscandoContatos(false); }
      }
    }, 300);
    return () => { vivo = false; clearTimeout(t); };
  }, [busca, podeUsar, limiteContatos]);

  // Mesma busca da Minha fila, agora dentro do "Encaminhar para…": 300ms de
  // folga, mínimo de 2 letras. Só roda com o seletor aberto — não faz sentido
  // bater no servidor quando ninguém está encaminhando nada.
  useEffect(() => {
    const termo = encaminharBusca.trim();
    if (!encaminharAlvo || termo.length < 2) {
      setEncaminharContatos([]); setEncaminharBuscandoAgenda(false); return undefined;
    }
    setEncaminharBuscandoAgenda(true);
    let vivo = true;
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/contatos`, { params: { busca: termo, limite: 20 } });
        if (!vivo) return;
        const achados = r.data?.data || [];
        setEncaminharContatos(achados);
        setEncaminharBuscandoAgenda(false);
        const fotos = await buscarFotosFaltando(achados);
        if (!vivo || !Object.keys(fotos).length) return;
        setEncaminharContatos((atual) => atual.map((c) => (
          fotos[c.id] ? { ...c, profile_pic_url: fotos[c.id] } : c
        )));
      } catch (e) {
        if (vivo) { setEncaminharContatos([]); setEncaminharBuscandoAgenda(false); }
      }
    }, 300);
    return () => { vivo = false; clearTimeout(t); };
  }, [encaminharBusca, encaminharAlvo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persiste as camadas "Ver junto" pra próxima abertura da tela.
  useEffect(() => {
    try {
      localStorage.setItem(verJuntoKey, JSON.stringify({ porFora: incluirPorFora, time: incluirTime }));
    } catch { /* localStorage indisponivel - ignora */ }
  }, [incluirPorFora, incluirTime, verJuntoKey]);

  // Persiste o filtro de canal escolhido para lembrar entre sessoes.
  useEffect(() => {
    try {
      localStorage.setItem(filtroCanalKey, JSON.stringify(canaisFiltro));
    } catch {
      /* localStorage indisponivel - ignora */
    }
  }, [canaisFiltro, filtroCanalKey]);

  // Polling da lista a cada 5s
  useEffect(() => {
    if (!podeUsar) return undefined;
    pollListaRef.current = setInterval(() => {
      if (listaEmVooRef.current || !telaVisivel()) return;
      carregarLista(false);
    }, 5000);
    return () => {
      if (pollListaRef.current) clearInterval(pollListaRef.current);
      pollListaRef.current = null;
    };
  }, [podeUsar, statusFiltro, vista, busca, canaisFiltro, atendentesFiltro, incluirPorFora, incluirTime, tagFiltro]);

  // Contadores das abas (bolinhas) — sempre visíveis, mesmo em outra aba.
  //
  // Contam o MESMO recorte que a lista vai mostrar: canal e fila do filtro
  // atual. Antes contavam tudo, e clicar no "1" vermelho abria uma lista vazia
  // — o número existia, a conversa estava fora do filtro, e não havia como
  // saber disso pela tela.
  useEffect(() => {
    if (!podeUsar) return undefined;
    const daFilaFiltrada = (lista) => lista.filter((c) => {
      if (!setorFiltro) return true;
      if (setorFiltro === '__pendente__') return c.setor_id == null;
      return String(c.setor_id) === String(setorFiltro);
    });
    let emVoo = false;
    const buscar = () => {
      if (emVoo || !telaVisivel()) return;
      emVoo = true;
      const base = { status: 'aberto' };
      if (canaisFiltro.length > 0) base.canal_ids = canaisFiltro.join(',');
      Promise.allSettled([
        axios.get(`${API}/atendimentos`, { params: { ...base, vista: 'pendente' }, timeout: TEMPO_DE_POLL_MS })
          .then((r) => setPendenteCount(daFilaFiltrada(r.data?.data || []).length)),
        axios.get(`${API}/atendimentos`, { params: { ...base, vista: 'sem_dono' }, timeout: TEMPO_DE_POLL_MS })
          .then((r) => setSemDonoCount(daFilaFiltrada(r.data?.data || []).length)),
      ]).finally(() => { emVoo = false; });
    };
    buscar();
    const t = setInterval(buscar, 12000);
    return () => clearInterval(t);
  }, [podeUsar, canaisFiltro, setorFiltro]);

  // Tick de 1s para o cronometro de TTFR. Sem efeito quando ninguem esta
  // aguardando (CronometroTTFR retorna null) - so causa re-render barato.
  useEffect(() => {
    if (!podeUsar) return undefined;
    const t = setInterval(() => setAgora(new Date()), 1000);
    return () => clearInterval(t);
  }, [podeUsar]);

  // Deep-link "abrir conversa de origem" do chamado: ?conversa=ID seleciona a
  // conversa e marca pra rolar ate o inicio da thread. Consome o parametro da
  // URL apos aplicar pra nao reabrir ao trocar de conversa manualmente.
  useEffect(() => {
    const alvo = Number(searchParams.get('conversa'));
    if (!alvo) return;
    marcoOrigemRef.current = searchParams.get('marco') || null;
    rolarParaInicioRef.current = true;
    setConversaAtivaId(alvo);
    const restante = new URLSearchParams(searchParams);
    restante.delete('conversa');
    restante.delete('marco');
    setSearchParams(restante, { replace: true });
  }, [searchParams, setSearchParams]);

  // Deep-link do push do chat interno: ?equipe=1 abre o modal da Equipe.
  // `?grupos=1` é de push antigo, de quando Grupos era modal daqui — hoje é
  // tela própria, então só encaminha. Notificação velha não pode virar clique
  // que não faz nada.
  useEffect(() => {
    if (searchParams.get('grupos')) { navegar('/ti/grupos', { replace: true }); return; }
    if (searchParams.get('equipe')) setChatInternoAberto(true);
    if (!searchParams.get('equipe')) return;
    const restante = new URLSearchParams(searchParams);
    restante.delete('equipe');
    setSearchParams(restante, { replace: true });
  }, [searchParams, setSearchParams, navegar]);

  // Contador de não-lidas do chat interno (badge vermelho no botão "Equipe").
  const recarregarNaoLidas = () => {
    axios.get(`${API}/chat-interno/nao-lidas`, { timeout: TEMPO_DE_POLL_MS })
      .then((r) => setEquipeNaoLidas(Number(r.data?.data?.total || 0)))
      .catch(() => {});
  };
  // Não-lidas dos Grupos (badge no botão "Grupos" — a tela é /ti/grupos).
  const [gruposNaoLidas, setGruposNaoLidas] = useState(0);
  const recarregarGruposNaoLidas = () => {
    axios.get(`${API}/grupos/nao-lidas`, { timeout: TEMPO_DE_POLL_MS })
      .then((r) => setGruposNaoLidas(Number(r.data?.data?.total || 0)))
      .catch(() => {});
  };
  useEffect(() => {
    recarregarNaoLidas();
    recarregarGruposNaoLidas();
    const t = setInterval(() => {
      if (!telaVisivel()) return;
      recarregarNaoLidas();
      recarregarGruposNaoLidas();
    }, 15000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Carregar detalhe, chamados vinculados e marcar como lida ao selecionar
  useEffect(() => {
    setContatoSidebarAberta(false);
    setContatoSidebarForm({ push_name: '', filial: '', setor: '' });
    setReplyTo(null);
    if (!conversaAtivaId) {
      setConversaAtiva(null);
      setChamadosVinculados([]);
      setChamadoEmFoco(null);
      return undefined;
    }
    let cancelado = false;
    (async () => {
      await carregarDetalhe(conversaAtivaId, true);
      if (cancelado) return;
      await carregarChamadosVinculados(conversaAtivaId);
      if (cancelado) return;
      // ANTES de deixar escrever: o número tem WhatsApp? Descobrir aqui evita
      // a pessoa redigir a mensagem inteira e tomar erro no clique de enviar.
      setSemWhatsapp(null);
      axios.get(`${API}/atendimentos/${conversaAtivaId}/pode-escrever`)
        .then((r) => { if (!cancelado) setSemWhatsapp(r.data?.data?.pode === false); })
        .catch(() => { /* checagem é conveniência: na dúvida, deixa escrever */ });
      // Abriu a conversa = leu. A notificação dela sai da bandeja, como no
      // WhatsApp (pedido do Gabriel: ficava lá até ele apagar na mão). Antes do
      // `/lida` e independente dele: a pessoa abriu, mesmo que o servidor tussa.
      fecharNotificacoesDeConversas([conversaAtivaId]);
      try {
        await axios.post(`${API}/atendimentos/${conversaAtivaId}/lida`);
        await carregarLista(false);
      } catch (e) { /* nao bloqueia */ }
    })();
    return () => { cancelado = true; };
  }, [conversaAtivaId]);

  // Voltou pro Fio (tablet desbloqueado, app trazido pra frente) com uma
  // conversa aberta na tela: as notificações dela chegaram enquanto ele não
  // olhava, e agora está olhando.
  //
  // SÓ a conversa aberta, de propósito. Fechar também as "já lidas" pela lista
  // em memória parece melhor e é pior: essa lista é de ANTES de o tablet
  // dormir — justamente quando chegam as mensagens novas — e fecharia a
  // notificação nova com base em dado velho.
  useEffect(() => {
    const aoVoltar = () => {
      if (document.visibilityState === 'visible' && conversaAtivaIdRef.current) {
        fecharNotificacoesDeConversas([conversaAtivaIdRef.current]);
      }
    };
    document.addEventListener('visibilitychange', aoVoltar);
    return () => document.removeEventListener('visibilitychange', aoVoltar);
  }, []);

  // Polling do detalhe a cada 4s (fallback caso o SSE caia ou seja bloqueado
  // por proxy; quando o SSE esta ativo, a UI ja atualizou bem antes).
  useEffect(() => {
    if (!conversaAtivaId) return undefined;
    pollDetalheRef.current = setInterval(() => {
      if (detalheEmVooRef.current || !telaVisivel()) return;
      carregarDetalhe(conversaAtivaId, false);
    }, 4000);
    return () => {
      if (pollDetalheRef.current) clearInterval(pollDetalheRef.current);
      pollDetalheRef.current = null;
    };
  }, [conversaAtivaId]);

  // Mantem os refs do SSE apontando para o estado/funcoes do render atual.
  // Sem array de deps: roda apos todo render.
  useEffect(() => {
    conversaAtivaIdRef.current = conversaAtivaId;
    conversaAtivaNumeroRef.current = conversaAtiva?.numero || null;
    carregarDetalheRef.current = carregarDetalhe;
    carregarListaRef.current = carregarLista;
  });
  // Ao trocar de conversa, zera o "digitando" do cliente.
  useEffect(() => { setClientePresenca(null); }, [conversaAtivaId]);

  // Aplica o ack recebido por SSE direto na mensagem (sem refetch). Monotonico:
  // nunca rebaixa o check ja exibido (enviada<entregue<lida).
  const patchStatusLocal = (conversaId, evolutionId, status) => {
    setConversaAtiva((prev) => {
      if (!prev || prev.id !== conversaId || !Array.isArray(prev.mensagens)) return prev;
      const rank = { enviada: 1, entregue: 2, lida: 3 };
      let mudou = false;
      const mensagens = prev.mensagens.map((m) => {
        if (m.evolution_id !== evolutionId) return m;
        if ((rank[status] || 0) <= (rank[m.status] || 0)) return m;
        mudou = true;
        return { ...m, status };
      });
      return mudou ? { ...prev, mensagens } : prev;
    });
  };

  // Canal SSE de tempo real. Conecta uma vez (por sessao com permissao) e
  // reage aos eventos do backend: status de ack, nova mensagem, mudanca de
  // conversa. O EventSource reconecta sozinho em caso de queda; o polling
  // acima cobre o intervalo ate a reconexao.
  useEffect(() => {
    if (!podeUsar) return undefined;
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return undefined;

    const es = new EventSource(`${API}/atendimentos/stream?token=${encodeURIComponent(token)}`);

    const agendarLista = () => {
      if (listaDebounceRef.current) return;
      listaDebounceRef.current = setTimeout(() => {
        listaDebounceRef.current = null;
        carregarListaRef.current(false);
      }, 400);
    };

    es.addEventListener('message_status', (e) => {
      try {
        const d = JSON.parse(e.data);
        patchStatusLocal(d.conversaId, d.evolutionId, d.status);
      } catch (_) { /* ignora payload malformado */ }
    });

    es.addEventListener('message_new', (e) => {
      try {
        const d = JSON.parse(e.data);
        if (d.conversaId === conversaAtivaIdRef.current) {
          carregarDetalheRef.current(conversaAtivaIdRef.current, false);
        }
        agendarLista();
      } catch (_) { /* ignora */ }
    });

    es.addEventListener('conversa_update', () => {
      agendarLista();
      const ativa = conversaAtivaIdRef.current;
      if (ativa) carregarDetalheRef.current(ativa, false);
    });

    // Cliente digitando/gravando -> mostra no header da conversa ativa (some sozinho).
    es.addEventListener('presence', (e) => {
      try {
        const d = JSON.parse(e.data);
        if (!d.numero || d.numero !== conversaAtivaNumeroRef.current) return;
        if (d.presence === 'composing' || d.presence === 'recording') {
          setClientePresenca(d.presence);
          if (presencaTimerRef.current) clearTimeout(presencaTimerRef.current);
          presencaTimerRef.current = setTimeout(() => setClientePresenca(null), 6000);
        } else {
          setClientePresenca(null);
        }
      } catch (_) { /* ignora */ }
    });

    es.onerror = () => { /* reconexao automatica do EventSource */ };

    return () => {
      es.close();
      if (listaDebounceRef.current) {
        clearTimeout(listaDebounceRef.current);
        listaDebounceRef.current = null;
      }
    };
  }, [podeUsar]);

  // Posiciona a thread na mensagem mais proxima do instante `marco` (created_at
  // do chamado) e destaca por alguns segundos. Se nao houver marco, rola pro topo.
  const posicionarNoMarco = (marco) => {
    const msgs = conversaAtiva?.mensagens || [];
    const el = mensagensRef.current;
    if (!el) return;
    if (!marco || !msgs.length) { el.scrollTop = 0; return; }
    const alvoTs = new Date(marco).getTime();
    if (Number.isNaN(alvoTs)) { el.scrollTop = 0; return; }
    let melhor = null;
    let melhorDelta = Infinity;
    for (const m of msgs) {
      const ts = new Date(m.criada_em).getTime();
      if (Number.isNaN(ts)) continue;
      const delta = Math.abs(ts - alvoTs);
      if (delta < melhorDelta) { melhorDelta = delta; melhor = m; }
    }
    if (!melhor) { el.scrollTop = 0; return; }
    const node = document.getElementById(`msg-${melhor.id}`);
    if (!node) { el.scrollTop = 0; return; }
    node.scrollIntoView({ block: 'center' });
    // Destaque temporario (faixa na linha) pro atendente achar o ponto de origem.
    node.style.transition = 'background-color 0.4s ease';
    node.style.backgroundColor = 'rgba(61, 139, 55,0.18)';
    node.style.borderRadius = '10px';
    setTimeout(() => {
      node.style.backgroundColor = 'transparent';
    }, 3000);
  };

  // Auto-scroll: normalmente vai pra ultima mensagem. Quando viemos do deep-link
  // "abrir conversa de origem", posiciona no instante em que o chamado nasceu
  // (so na primeira carga das mensagens).
  useEffect(() => {
    const el = mensagensRef.current;
    if (!el) return;
    if (rolarParaInicioRef.current && conversaAtiva?.mensagens?.length) {
      rolarParaInicioRef.current = false;
      const marco = marcoOrigemRef.current;
      marcoOrigemRef.current = null;
      posicionarNoMarco(marco);
      return;
    }
    el.scrollTop = el.scrollHeight;
  }, [conversaAtiva?.mensagens?.length, conversaAtivaId]); // eslint-disable-line react-hooks/exhaustive-deps

  // A lista encolhe quando o teclado abre (e quando o tablet gira). O scroll
  // fica onde estava, então a última mensagem — a que se está respondendo —
  // sai por baixo. Aqui ela continua colada no fim.
  //
  // Só vale se a pessoa JÁ estava no fim: quem subiu pra ler o histórico não
  // pode ser jogado lá pra baixo só porque tocou no campo de digitar.
  // Depende também de `conversaAtiva`: quando só o id muda, a lista ainda não
  // existe no DOM e o ref é null — o observer nunca chegava a ser ligado.
  // O "estava no fim" mora num ref porque o observer é remontado, e a resposta
  // não pode zerar junto com ele.
  const estavaNoFimRef = useRef(true);
  useEffect(() => {
    const el = mensagensRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const aoRolar = () => {
      estavaNoFimRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    };
    el.addEventListener('scroll', aoRolar, { passive: true });
    const ro = new ResizeObserver(() => {
      if (estavaNoFimRef.current) el.scrollTop = el.scrollHeight;
    });
    ro.observe(el);
    // Foto e vídeo carregam DEPOIS que a conversa já desceu pro fim, e a imagem
    // não reserva altura (MidiaMensagem: só maxHeight). O conteúdo cresce, a
    // tela fica parada e a conversa aparece "lá em cima" — pedido do Gabriel.
    // O observer acima olha o tamanho da CAIXA, não do conteúdo, e não pega
    // isso. `load` não borbulha, mas passa pelos ancestrais na CAPTURA: basta
    // escutar aqui e, se a pessoa estava no fim, continuar no fim.
    const aoCarregarMidia = () => { if (estavaNoFimRef.current) el.scrollTop = el.scrollHeight; };
    el.addEventListener('load', aoCarregarMidia, true);
    el.addEventListener('loadedmetadata', aoCarregarMidia, true);
    return () => {
      el.removeEventListener('scroll', aoRolar);
      el.removeEventListener('load', aoCarregarMidia, true);
      el.removeEventListener('loadedmetadata', aoCarregarMidia, true);
      ro.disconnect();
    };
  }, [conversaAtivaId, conversaAtiva?.id]);

  // Caminho independente do observer: se o navegador não redimensiona o layout
  // quando o teclado abre, quem avisa é o visualViewport (`areaVisivel`).
  useEffect(() => {
    const el = mensagensRef.current;
    if (el && estavaNoFimRef.current) el.scrollTop = el.scrollHeight;
  }, [areaVisivel]);

  // O campo de digitar cresce junto com o texto, até um teto — como no WhatsApp.
  // Antes ele era de uma linha só e quem escrevia um recado grande relia por uma
  // fresta, arrastando pra cima e pra baixo dentro do campo.
  //
  // O teto é contado em LINHAS (não em pixels fixos) pra continuar valendo dez
  // linhas quando a fonte é maior, como no tablet. Passando disso, rola dentro
  // do campo — senão a conversa some atrás dele.
  const LINHAS_MAX = 10;
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto'; // zera antes de medir: senão mede a altura atual
    const cs = getComputedStyle(el);
    const linha = parseFloat(cs.lineHeight) || (parseFloat(cs.fontSize) || 14) * 1.4;
    const extra = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom)
      + parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
    el.style.height = `${Math.min(el.scrollHeight, linha * LINHAS_MAX + extra)}px`;
  }, [texto, arquivoSelecionado]);

  // Um caminho só pro anexo — clipe, Ctrl+V e arrastar-e-soltar caem aqui.
  // Foto passa pelo editor antes de virar anexo: dá pra recortar, e sai de lá
  // comprimida. O teto de 25MB continua ANTES do editor — abrir uma imagem de
  // 30MB num canvas é o que trava o tablet, então nem chega lá.
  const [editandoImagem, setEditandoImagem] = useState(null);

  const aceitarArquivo = (f) => {
    if (!f) return false;
    const MAX = 25 * 1024 * 1024;
    if (f.size > MAX) {
      Swal.fire({ icon: 'warning', title: 'Arquivo muito grande', text: 'Limite de 25MB.' });
      return false;
    }
    if (daPraEditar(f)) { setEditandoImagem(f); return true; }
    setArquivoSelecionado(f);
    return true;
  };

  const escolherArquivo = (e) => {
    const ok = aceitarArquivo(e.target.files?.[0]);
    e.target.value = '';
    // Sem isso o foco fica no botão do clipe: o Enter seguinte reabria o
    // seletor de arquivo em vez de enviar. Anexou -> o cursor vai pro campo de
    // texto, então Enter envia (e dá pra escrever uma legenda antes).
    if (ok) setTimeout(() => textareaRef.current?.focus(), 50);
  };

  // Arrastar um arquivo pra cima da conversa e soltar já anexa (igual WhatsApp
  // Web). `dragDepth` evita o piscar do overlay: entrar num filho dispara
  // dragleave no pai, então conta entradas/saídas em vez de ligar/desligar.
  const [arrastando, setArrastando] = useState(false);
  const dragDepth = useRef(0);
  const temArquivoNoDrag = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
  const aoArrastarEntrar = (e) => {
    if (!temArquivoNoDrag(e)) return;
    e.preventDefault();
    dragDepth.current += 1;
    setArrastando(true);
  };
  const aoArrastarSair = (e) => {
    if (!temArquivoNoDrag(e)) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setArrastando(false);
  };
  const aoArrastarSobre = (e) => {
    if (!temArquivoNoDrag(e)) return;
    e.preventDefault(); // sem isso o navegador ABRE o arquivo em vez de soltar aqui
    e.dataTransfer.dropEffect = 'copy';
  };
  const aoSoltar = (e) => {
    if (!temArquivoNoDrag(e)) return;
    e.preventDefault(); // sempre: sem isso o navegador ABRE o arquivo na aba
    dragDepth.current = 0;
    setArrastando(false);
    // Não pode anexar aqui? Diz por quê. Antes o arquivo sumia em silêncio e
    // parecia que o arrastar estava quebrado.
    if (!podeAgir) {
      Swal.fire({
        icon: 'info',
        title: 'Não dá pra anexar aqui',
        text: `Este atendimento é do canal ${conversaAtiva?.canal_nome || 'de outro número'} — só quem atende nesse canal pode enviar.`,
      });
      return;
    }
    const arquivos = Array.from(e.dataTransfer.files || []);
    if (arquivos.length > 1) {
      Swal.fire({
        icon: 'info',
        title: 'Um arquivo por vez',
        text: `Anexei "${arquivos[0].name}". Mande os outros ${arquivos.length - 1} em seguida.`,
        timer: 2600,
        showConfirmButton: false,
      });
    }
    if (aceitarArquivo(arquivos[0])) setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const removerArquivo = () => setArquivoSelecionado(null);

  function fmtTempoGravacao(s) {
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  }

  // Codec e taxa da gravação. Sem passar nada, o MediaRecorder usa o padrão do
  // navegador — que no Chrome dá um Opus de taxa baixa, e o resultado chegava
  // abafado. 64 kbps em mono é folgado pra voz (o WhatsApp manda bem menos e
  // soa bom porque é bem codificado), e o custo em MB de um recado de 30s é
  // irrisório perto de uma foto.
  //
  // A ordem dos formatos é intencional: Opus é o codec do recado de voz do
  // WhatsApp, então mandar Opus evita uma reconversão com perda na Evolution.
  // O último da lista é a rede de segurança pra navegador que não aceita nenhum
  // (aí volta ao padrão de antes, que ao menos funciona).
  function opcoesDeGravacao() {
    const preferidos = [
      'audio/webm;codecs=opus',
      'audio/ogg;codecs=opus',
      'audio/mp4',
      'audio/webm',
    ];
    const suportado = typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported
      ? preferidos.find((t) => MediaRecorder.isTypeSupported(t))
      : null;
    const opcoes = { audioBitsPerSecond: 64000 };
    if (suportado) opcoes.mimeType = suportado;
    return opcoes;
  }

  async function iniciarGravacao() {
    if (!navigator.mediaDevices?.getUserMedia) {
      Swal.fire('Indisponível', 'Seu navegador não suporta gravação de áudio.', 'error');
      return;
    }
    let stream;
    try {
      // `audio: true` deixava tudo no padrão do navegador, e o padrão é ruim
      // pra recado de voz: o Chrome escolhe taxa baixa e o áudio chegava
      // abafado, bem pior que o do WhatsApp no mesmo aparelho.
      //
      // Mono de propósito: recado de voz é uma pessoa falando, e estéreo só
      // gastaria metade dos bits com um canal idêntico.
      // Os três tratamentos ficam LIGADOS — é o que o WhatsApp faz. Numa loja
      // tem serra, empilhadeira e cliente falando junto; sem supressão de ruído
      // e controle de ganho, quem ouve não entende.
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 48000,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch {
      Swal.fire('Sem permissão', 'Permita o acesso ao microfone no navegador e tente novamente.', 'warning');
      return;
    }
    const mr = new MediaRecorder(stream, opcoesDeGravacao());
    chunksRef.current = [];
    canceladoRef.current = false;
    mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    mr.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      clearInterval(timerGravacaoRef.current);
      setGravando(false);
      setTempoGravacao(0);
      if (canceladoRef.current) return;
      const idConversa = conversaParaEnvioRef.current;
      if (!idConversa || !chunksRef.current.length) return;
      const mime = mr.mimeType || 'audio/webm';
      const ext = mime.includes('ogg') ? 'ogg' : 'webm';
      const blob = new Blob(chunksRef.current, { type: mime });
      const file = new File([blob], `audio_${Date.now()}.${ext}`, { type: mime });
      // Mesma fila do texto: por fora dela, um áudio gravado logo depois de um
      // texto podia chegar ANTES dele no cliente.
      const job = {
        tempId: novoIdTemporario(), idConversa, texto: '', arquivo: file,
        replyId: null, chamadoId: null, semAssinatura: false, assumir: false, somentePorId: true,
      };
      mostrarPendente({
        id: job.tempId, conversaId: idConversa, direcao: 'out', tipo: 'texto',
        conteudo: rotuloDoAnexo(file), criada_em: new Date().toISOString(),
        autor: user?.usuario || 'atendente', status: 'enviando', somentePorId: true,
      });
      filaEnvioRef.current.enfileirar(idConversa, job);
    };
    mr.start();
    mediaRecorderRef.current = mr;
    setGravando(true);
    setTempoGravacao(0);
    timerGravacaoRef.current = setInterval(() => setTempoGravacao((t) => t + 1), 1000);
  }

  function pararEEnviarGravacao() {
    conversaParaEnvioRef.current = conversaAtivaId;
    canceladoRef.current = false;
    if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
    clearInterval(timerGravacaoRef.current);
  }

  function cancelarGravacao() {
    canceladoRef.current = true;
    if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
    clearInterval(timerGravacaoRef.current);
    setGravando(false);
    setTempoGravacao(0);
  }

  // Ctrl+V no textarea: se o clipboard tem uma imagem (print de tela, recorte,
  // arquivo copiado), trata como se o atendente tivesse anexado pelo paperclip.
  const aoColar = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (!file) continue;
        // Print de tela vem geralmente sem nome ("image.png"). Renomeia com timestamp
        // para o atendente identificar no historico.
        const nome = file.name && file.name !== 'image.png'
          ? file.name
          : `print-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.png`;
        // Mesmo caminho do clipe e do arrastar: aqui existia uma cópia do limite
        // de 25MB, e colar era o único jeito de anexar sem passar pelo editor —
        // logo o print de planilha, que é justamente o que mais pede recorte.
        aceitarArquivo(new File([file], nome, { type: file.type }));
        e.preventDefault();
        return;
      }
    }
  };

  // "Digitando…" pro cliente: dispara no máximo 1x a cada 2,5s enquanto escreve
  // (o WhatsApp expira sozinho). Best-effort — nunca trava a digitação.
  const presencaRef = useRef(0);
  const sinalizarDigitando = () => {
    if (!conversaAtivaId) return;
    const agora = Date.now();
    if (agora - presencaRef.current < 2500) return;
    presencaRef.current = agora;
    axios.post(`${API}/atendimentos/${conversaAtivaId}/presenca`, { presence: 'composing' }).catch(() => {});
  };

  const [anexoMenu, setAnexoMenu] = useState(false);

  // Menu do "+" fecha ao clicar fora (ou ESC), igual qualquer dropdown. O botão
  // fica DENTRO do ref, então clicar nele continua alternando normalmente.
  const anexoMenuRef = useRef(null);
  useEffect(() => {
    if (!anexoMenu) return undefined;
    const cliqueFora = (e) => {
      if (!anexoMenuRef.current?.contains(e.target)) setAnexoMenu(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setAnexoMenu(false); };
    document.addEventListener('pointerdown', cliqueFora);
    window.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('pointerdown', cliqueFora);
      window.removeEventListener('keydown', onEsc);
    };
  }, [anexoMenu]);

  const [pickerContatoAberto, setPickerContatoAberto] = useState(false);
  const [acaoMensagem, setAcaoMensagem] = useState(null); // mensagem no menu mobile
  const [acaoAncora, setAcaoAncora] = useState(null); // onde a bolha estava, pro menu colar nela

  // Ir até a mensagem citada, como no WhatsApp. O histórico da conversa vem
  // limitado, então a original pode simplesmente não estar em tela — e aí é
  // melhor dizer isso do que rolar pra lugar nenhum.
  const [destaqueId, setDestaqueId] = useState(null);
  const destaqueTimer = useRef(null);
  const irParaMensagem = (id) => {
    const alvo = document.getElementById(`msg-${id}`);
    if (!alvo) {
      Swal.fire({
        icon: 'info',
        title: 'Mensagem antiga',
        text: 'A mensagem citada é mais antiga do que o trecho carregado aqui.',
        timer: 2400,
        showConfirmButton: false,
      });
      return;
    }
    alvo.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setDestaqueId(id);
    clearTimeout(destaqueTimer.current);
    destaqueTimer.current = setTimeout(() => setDestaqueId(null), 1600);
  };
  useEffect(() => () => clearTimeout(destaqueTimer.current), []);

  // Voltar pra lista arrastando da borda esquerda (gesto do iPhone). A conversa
  // acompanha o dedo enquanto arrasta: sem esse retorno, o gesto parece ter
  // falhado até a tela trocar de uma vez.
  const [voltandoX, setVoltandoX] = useState(0);
  const bordaRef = useRef(null);

  const bordaInicio = (e) => {
    if (!isMobile || !conversaAtivaId) { bordaRef.current = null; return; }
    const t = e.touches[0];
    if (t.clientX > ZONA_BORDA) { bordaRef.current = null; return; }
    // A barra de digitar encosta na borda esquerda quando a conversa está em
    // tela cheia: o clipe e o "+" caem dentro da faixa do gesto. Toque em
    // controle é toque no controle, nunca começo de "voltar".
    if (e.target?.closest?.('button, a, input, textarea, select, label')) {
      bordaRef.current = null;
      return;
    }
    bordaRef.current = { x0: t.clientX, y0: t.clientY, dx: 0, engatou: false };
  };

  const bordaMove = (e) => {
    const b = bordaRef.current;
    if (!b) return;
    const t = e.touches[0];
    const dx = t.clientX - b.x0;
    const dy = t.clientY - b.y0;
    if (!b.engatou) {
      // Só engata se for claramente horizontal: empatado, a intenção era rolar
      // a conversa, e sequestrar isso deixa a lista travada na mão.
      if (Math.abs(dy) * 2 > Math.abs(dx)) { bordaRef.current = null; setVoltandoX(0); return; }
      if (dx < 14) return;
      b.engatou = true;
    }
    b.dx = Math.max(0, dx);
    setVoltandoX(b.dx);
  };

  // Se a conversa mudar no meio do gesto (por push, por erro, por qualquer
  // coisa), o painel não pode ficar preso deslocado na tela.
  useEffect(() => { bordaRef.current = null; setVoltandoX(0); }, [conversaAtivaId]);

  // O "voltar" do aparelho anda no histórico do navegador, e abrir conversa é
  // só estado — não criava entrada nenhuma. Então o botão voltava pra página
  // anterior de verdade (Grupos, Contatos...) em vez de fechar a conversa.
  // Empilhamos uma entrada boba só pra ele ter o que desfazer.
  // A entrada empilhada é UMA só enquanto houver conversa aberta, não uma por
  // conversa. Antes o efeito rodava a cada troca de `conversaAtivaId`: sair da
  // conversa A pra B fazia a limpeza de A chamar `history.back()`, o `popstate`
  // chegava DEPOIS de B já ter registrado o listener dela, e B se fechava
  // sozinha. Era isso que jogava pra lista ao clicar em "Conversar" no cartão
  // de contato — que é justamente trocar de conversa direto.
  const temEntradaNossa = useRef(false);

  useEffect(() => {
    if (!isMobile) return undefined;
    const aoVoltar = () => {
      temEntradaNossa.current = false; // a entrada saiu da pilha sozinha
      setConversaAtivaId(null);
    };
    window.addEventListener('popstate', aoVoltar);
    return () => window.removeEventListener('popstate', aoVoltar);
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile) return;
    if (conversaAtivaId && !temEntradaNossa.current) {
      // Abriu uma conversa: dá ao "voltar" do aparelho o que desfazer.
      window.history.pushState({ fioConversa: true }, '');
      temEntradaNossa.current = true;
    } else if (!conversaAtivaId && temEntradaNossa.current) {
      // Fechou por dentro (seta, arrasto): tira a entrada, senão sobra um
      // "voltar" que não faz nada visível. Só se ela ainda for a do topo — se a
      // pessoa saiu pela barra de abas, quem está no topo é a navegação dela.
      temEntradaNossa.current = false;
      if (window.history.state?.fioConversa) window.history.back();
    }
  }, [isMobile, conversaAtivaId]);

  const bordaFim = () => {
    const b = bordaRef.current;
    bordaRef.current = null;
    setVoltandoX(0);
    if (b?.engatou && b.dx > BORDA_DISPARA) setConversaAtivaId(null);
  };

  // --- Ações sobre uma mensagem (usadas no hover do desktop E no overlay mobile) ---
  const reagirMsg = async (msg, emoji) => {
    setConversaAtiva((prev) => (prev ? {
      ...prev,
      mensagens: (prev.mensagens || []).map((x) => (x.id === msg.id ? { ...x, reacao: emoji || null } : x)),
    } : prev));
    try {
      await axios.post(`${API}/atendimentos/${conversaAtivaId}/mensagens/${msg.id}/reagir`, { reaction: emoji });
    } catch (e) { carregarDetalhe(conversaAtivaId, false); }
  };
  const editarMsg = async (msg, texto) => {
    try {
      await axios.put(`${API}/atendimentos/${conversaAtivaId}/mensagens/${msg.id}`, { texto });
      carregarDetalhe(conversaAtivaId, false);
    } catch (e) { alertarErro(e, 'Não deu pra editar'); }
  };
  const editarMsgPrompt = async (msg) => {
    const { value } = await Swal.fire({
      title: 'Editar mensagem', input: 'textarea', inputValue: msg.conteudo || '',
      showCancelButton: true, confirmButtonText: 'Salvar', cancelButtonText: 'Cancelar', confirmButtonColor: '#3D8B37',
    });
    if (value != null && value.trim() && value.trim() !== (msg.conteudo || '')) editarMsg(msg, value.trim());
  };
  const apagarMsg = async (msg) => {
    const r = await Swal.fire({
      icon: 'warning', title: 'Apagar mensagem?', text: 'Ela some pra você e pro cliente no WhatsApp.',
      showCancelButton: true, confirmButtonText: 'Apagar', cancelButtonText: 'Cancelar', confirmButtonColor: '#dc3545',
    });
    if (!r.isConfirmed) return;
    try {
      await axios.delete(`${API}/atendimentos/${conversaAtivaId}/mensagens/${msg.id}`);
      carregarDetalhe(conversaAtivaId, false);
    } catch (e) { alertarErro(e, 'Não deu pra apagar'); }
  };
  const copiarMsg = (msg) => {
    const txt = msg.conteudo || '';
    if (navigator.clipboard) navigator.clipboard.writeText(txt).catch(() => {});
  };
  const responderMsg = (msg) => { setReplyTo(msg); setTimeout(() => textareaRef.current?.focus(), 50); };
  // Encaminhar: abre um seletor de conversa de destino e reenvia a mensagem.
  const encaminharMsg = (msg) => {
    setEncaminharAlvo(msg); setEncaminharBusca(''); setEncaminharContatos([]);
  };

  // Fixar UMA mensagem por conversa. Fixar outra desafixa a anterior (o backend
  // faz o overwrite). Só interno do Fio; o cliente não vê nada mudar.
  const fixarMsg = async (msg) => {
    if (!conversaAtivaId || !msg?.id) return;
    try {
      await axios.post(`${API}/atendimentos/${conversaAtivaId}/mensagens/${msg.id}/fixar`);
      carregarDetalhe(conversaAtivaId, false);
    } catch (e) { alertarErro(e, 'Não deu pra fixar'); }
  };
  const desafixarMsg = async () => {
    if (!conversaAtivaId) return;
    try {
      await axios.delete(`${API}/atendimentos/${conversaAtivaId}/mensagem-fixada`);
      carregarDetalhe(conversaAtivaId, false);
    } catch (e) { alertarErro(e, 'Não deu pra desafixar'); }
  };

  // Encaminhar pra quem ainda NÃO tem conversa: abre (ou reaproveita) a conversa
  // daquele contato e só então encaminha. Mesmo endpoint que a busca da Minha
  // fila usa — a conversa nasce igual, sem caminho paralelo.
  const encaminharParaContato = async (contato) => {
    if (!encaminharAlvo || !contato?.numero || !contato?.canal_id) return;
    if (encaminhando) return; // já tem um indo; segundo clique não vira segundo envio
    const chave = `ag-${contato.id}-${contato.canal_id}`;
    setEncaminhando(chave);
    try {
      const r = await axios.post(`${API}/atendimentos/abrir-por-numero`, {
        numero: contato.numero,
        nome: contato.push_name || null,
        canal_id: contato.canal_id,
      });
      const nova = r.data?.data;
      if (!nova?.id) throw new Error('Não deu pra abrir a conversa desse contato.');
      await confirmarEncaminhar(nova.id, chave);
    } catch (e) {
      alertarErro(e, 'Não deu pra encaminhar');
      setEncaminhando(null);
    }
  };
  // `chave` já vem preenchida quando o caminho é o do contato da agenda (ela
  // marcou a linha antes de abrir a conversa); quando vem da lista de conversas,
  // é aqui que ela nasce.
  const confirmarEncaminhar = async (destinoId, chave = null) => {
    if (!encaminharAlvo) return;
    const minhaChave = chave || `conv-${destinoId}`;
    if (!chave && encaminhando) return;
    setEncaminhando(minhaChave);
    try {
      await axios.post(`${API}/atendimentos/${conversaAtivaId}/mensagens/${encaminharAlvo.id}/encaminhar`, { para_conversa_id: destinoId });
      setEncaminharAlvo(null);
      Swal.fire({ icon: 'success', title: 'Mensagem encaminhada', timer: 1100, showConfirmButton: false });
      if (destinoId === conversaAtivaId) await carregarDetalhe(conversaAtivaId, false);
      await carregarLista(false);
    } catch (err) {
      alertarErro(err, 'Não deu pra salvar');
    } finally {
      setEncaminhando(null);
    }
  };

  // Números dos cartões de contato desta conversa que já estão na agenda do
  // canal. undefined = ainda não checado, null = não está, string = nome salvo.
  const [agendaDosCartoes, setAgendaDosCartoes] = useState({});
  const numeroDoCartao = (m) => {
    try { return JSON.parse(m.conteudo || '{}').phoneNumber || null; } catch (_) { return null; }
  };
  useEffect(() => {
    const canalId = conversaAtiva?.canal_id;
    const msgs = conversaAtiva?.mensagens || [];
    if (!canalId) return;
    const numeros = [...new Set(
      msgs.filter((m) => m.tipo === 'contato').map(numeroDoCartao).filter(Boolean),
    )].filter((n) => agendaDosCartoes[n] === undefined);
    if (!numeros.length) return;
    numeros.forEach(async (n) => {
      try {
        const r = await axios.get(`${API}/contatos`, { params: { busca: n, canal_id: canalId } });
        // Compara pelo final do número: o cartão pode vir sem DDI (55) ou com
        // máscara, mas os últimos 8 dígitos são os mesmos.
        const fim = String(n).replace(/\D/g, '').slice(-8);
        const achado = (r.data?.data || []).find((c) => String(c.numero || '').endsWith(fim));
        setAgendaDosCartoes((prev) => ({ ...prev, [n]: achado ? (achado.push_name || 'sem nome') : null }));
      } catch (_) {
        setAgendaDosCartoes((prev) => ({ ...prev, [n]: null }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversaAtiva?.mensagens, conversaAtiva?.canal_id]);
  // Trocou de conversa: esquece o que sabia (a agenda é por canal).
  useEffect(() => { setAgendaDosCartoes({}); }, [conversaAtivaId]);

  // Cartão de contato que o cliente mandou: falar com aquele número (abre/cria a
  // conversa no MESMO canal) ou guardar na agenda — igual ao WhatsApp.
  const conversarComContato = async (numero, nome) => {
    if (!numero || !conversaAtiva?.canal_id) return;
    try {
      const r = await axios.post(`${API}/atendimentos/abrir-por-numero`, {
        numero, nome: nome || null, canal_id: conversaAtiva.canal_id,
      });
      const nova = r.data?.data;
      if (nova?.id) {
        // Abre PRIMEIRO: o detalhe carrega pelo id, sem depender da lista. Se a
        // conversa nova cair numa vista que não é a aberta (Pendente, por ex.),
        // esperar a lista chegar antes só atrasa — e num tablet lento aparece
        // como "clicou e voltou pra lista".
        setConversaAtivaId(nova.id);
        carregarLista(false);
      }
    } catch (e) {
      alertarErro(e, 'Não deu pra abrir');
    }
  };

  // Telefone escrito no meio da mensagem, tocado. Abre a mesma folha de ações
  // que o WhatsApp abre: falar, guardar ou copiar.
  //
  // A verificação de WhatsApp só acontece AGORA, no toque — e não ao desenhar a
  // conversa. Checar todo número de toda mensagem seria uma ida à Evolution por
  // número a cada abertura de conversa, pra uma informação que quase nunca é
  // usada.
  const [numeroTocado, setNumeroTocado] = useState(null);

  const tocouNoNumero = async (numero, comoEstaEscrito) => {
    setNumeroTocado({ numero, escrito: comoEstaEscrito, checando: true, existe: null, nome: null });
    if (!conversaAtiva?.canal_id) return;
    try {
      const r = await axios.get(`${API}/atendimentos/checar-numero`, {
        params: { canal_id: conversaAtiva.canal_id, numero },
      });
      const d = r.data?.data || {};
      setNumeroTocado((a) => (a && a.numero === numero
        ? { ...a, checando: false, existe: Boolean(d.existe), nome: d.nome || null } : a));
    } catch (e) {
      // Falhou a consulta: NÃO bloqueia. Continua dando pra tentar conversar —
      // dizer "não deu pra verificar" e deixar a pessoa decidir é melhor que
      // esconder o botão por causa de uma consulta que caiu.
      setNumeroTocado((a) => (a && a.numero === numero
        ? { ...a, checando: false, existe: null } : a));
    }
  };

  // Clicou num contato da busca: abre (ou cria) a conversa com ele no canal
  // daquela agenda e já foca nela. Não manda mensagem nenhuma — só abre, igual
  // clicar num contato no WhatsApp.
  const abrirConversaComContato = async (contato) => {
    if (!contato?.numero || !contato?.canal_id) return;
    try {
      const r = await axios.post(`${API}/atendimentos/abrir-por-numero`, {
        numero: contato.numero,
        nome: contato.push_name || null,
        canal_id: contato.canal_id,
      });
      const nova = r.data?.data;
      if (!nova?.id) return;
      setBusca('');
      setContatosBusca([]);
      await carregarLista(false);
      setConversaAtivaId(nova.id);
    } catch (e) {
      alertarErro(e, 'Não deu pra abrir');
    }
  };

  const salvarContatoNaAgenda = async (numero, nome) => {
    if (!numero || !conversaAtiva?.canal_id) return;
    const canalNome = conversaAtiva.canal_nome || 'deste canal';
    const { value: nomeFinal, isConfirmed } = await Swal.fire({
      title: 'Salvar na agenda',
      input: 'text',
      inputValue: nome || '',
      inputPlaceholder: 'Nome do contato',
      html: `<div style="font-size:0.85rem;opacity:0.8">${formatNumero(numero)} — entra na agenda de <b>${canalNome}</b></div>`,
      showCancelButton: true,
      confirmButtonText: 'Salvar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#3D8B37',
      inputValidator: (v) => (!String(v || '').trim() ? 'Dê um nome pro contato.' : undefined),
    });
    if (!isConfirmed) return;
    try {
      await axios.post(`${API}/contatos`, {
        canal_id: conversaAtiva.canal_id, numero, push_name: String(nomeFinal).trim(),
        // Marca de onde veio: no histórico da ficha, "conversa" e "fio" contam
        // histórias diferentes sobre quem mexeu e por quê.
        origem: 'conversa',
      });
      setAgendaDosCartoes((prev) => ({ ...prev, [numero]: String(nomeFinal).trim() }));
      Swal.fire({ icon: 'success', title: 'Salvo na agenda', timer: 1200, showConfirmButton: false });
    } catch (e) {
      alertarErro(e, 'Não deu pra salvar');
    }
  };

  // Contato = escolher da AGENDA (não faz sentido digitar tendo os contatos).
  const anexarContato = () => { setAnexoMenu(false); setPickerContatoAberto(true); };
  const enviarContatoEscolhido = async (c) => {
    setPickerContatoAberto(false);
    const nome = c.push_name || formatNumero(c.numero) || 'Contato';
    // Confirma antes (evita mandar sem querer no clique).
    const r = await Swal.fire({
      title: 'Enviar este contato?',
      html: `<div style="font-size:1.05rem;font-weight:700">${nome}</div>`
        + `<div style="color:var(--portal-muted,#888)">${formatNumero(c.numero)}</div>`,
      icon: 'question', showCancelButton: true, confirmButtonText: 'Enviar',
      cancelButtonText: 'Cancelar', confirmButtonColor: '#3D8B37',
    });
    if (!r.isConfirmed) return;
    try {
      await axios.post(`${API}/atendimentos/${conversaAtivaId}/contato`, { fullName: nome, phoneNumber: c.numero });
      carregarDetalhe(conversaAtivaId, false);
    } catch (e) { alertarErro(e, 'Não deu pra salvar'); }
  };

  // Localização = pega a atual do navegador (igual WhatsApp "enviar localização").
  const anexarLocalizacao = () => {
    setAnexoMenu(false);
    if (!navigator.geolocation) {
      Swal.fire({ icon: 'error', title: 'Sem GPS', text: 'Seu navegador não suporta localização.' });
      return;
    }
    Swal.fire({ title: 'Pegando sua localização...', didOpen: () => Swal.showLoading(), allowOutsideClick: false });
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        Swal.close();
        // Pré-visualiza no mapa antes de enviar (mapa embed não precisa de chave).
        const r = await Swal.fire({
          title: 'Enviar esta localização?',
          html: `<iframe title="mapa" width="100%" height="220" style="border:0;border-radius:10px" loading="lazy" `
            + `src="https://maps.google.com/maps?q=${lat},${lng}&z=16&output=embed"></iframe>`,
          width: 460, showCancelButton: true, confirmButtonText: 'Enviar',
          cancelButtonText: 'Cancelar', confirmButtonColor: '#3D8B37',
        });
        if (!r.isConfirmed) return;
        try {
          await axios.post(`${API}/atendimentos/${conversaAtivaId}/localizacao`, {
            latitude: lat, longitude: lng, name: 'Localização atual',
          });
          carregarDetalhe(conversaAtivaId, false);
        } catch (e) {
          alertarErro(e, 'Não deu pra enviar');
        }
      },
      (err) => {
        Swal.fire({
          icon: 'error', title: 'Não consegui pegar a localização',
          text: err.code === 1 ? 'Permissão negada — autorize a localização no navegador.' : 'Tente de novo.',
        });
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  // --- Fila de envio (helpers/filaDeEnvio) ----------------------------------
  //
  // Antes, `enviar` travava a barra inteira até o servidor responder E a
  // conversa e a lista recarregarem — vários segundos por mensagem numa
  // internet de loja. O Gabriel digitava a próxima e não conseguia mandar.
  //
  // Agora cada envio entra na fila da conversa, aparece na hora com o
  // reloginho, e sai em ordem, uma de cada vez, em segundo plano. O campo
  // nunca trava. Se uma falha, ela e as de trás voltam pro campo (ver
  // aoFalharEnvio) — sair fora de ordem seria pior que não sair.
  //
  // A fila é criada uma vez; as funções que ela chama moram em refs pra sempre
  // enxergarem o estado atual da tela, e não o do render em que nasceu.
  const executarEnvioRef = useRef(null);
  const aoFalharEnvioRef = useRef(null);
  const aoEsvaziarEnvioRef = useRef(null);
  const filaEnvioRef = useRef(null);
  if (!filaEnvioRef.current) {
    filaEnvioRef.current = criarFilaDeEnvio({
      executar: (job) => executarEnvioRef.current(job),
      aoFalhar: (id, erro, jobs) => aoFalharEnvioRef.current(id, erro, jobs),
      aoEsvaziar: (id) => aoEsvaziarEnvioRef.current(id),
    });
  }

  const mostrarPendente = (tempMsg) => {
    pendingMsgsRef.current = [...pendingMsgsRef.current, tempMsg];
    setConversaAtiva((prev) => (prev && prev.id === tempMsg.conversaId
      ? { ...prev, mensagens: [...(prev.mensagens || []), tempMsg] }
      : prev));
  };

  const removerPendentes = (tempIds) => {
    const fora = new Set(tempIds);
    pendingMsgsRef.current = pendingMsgsRef.current.filter((p) => !fora.has(p.id));
    setConversaAtiva((prev) => (prev
      ? { ...prev, mensagens: (prev.mensagens || []).filter((m) => !fora.has(m.id)) }
      : prev));
  };

  // Foto e áudio não dá pra mostrar "de verdade" antes de subir; aparece uma
  // linha dizendo o que está saindo. Quem tira é a fila, pelo id
  // (`somentePorId`), quando o servidor confirma.
  const rotuloDoAnexo = (arquivo) => {
    const tipo = String(arquivo?.type || '');
    if (tipo.startsWith('audio/')) return '🎤 Enviando áudio…';
    if (tipo.startsWith('image/')) return '🖼️ Enviando foto…';
    if (tipo.startsWith('video/')) return '🎬 Enviando vídeo…';
    return `📎 Enviando ${arquivo?.name || 'arquivo'}…`;
  };

  const novoIdTemporario = () => `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  executarEnvioRef.current = async (job) => {
    const { idConversa } = job;
    if (job.assumir) {
      try {
        await axios.patch(`${API}/atendimentos/${idConversa}/status`, { status: 'em_atendimento', assumir: true });
      } catch (e2) { /* se a atribuição falhar, ainda tentamos enviar a mensagem */ }
    }
    if (job.arquivo) {
      const fd = new FormData();
      fd.append('arquivo', job.arquivo);
      if (job.texto) fd.append('texto', job.texto);
      if (job.chamadoId) fd.append('chamado_id', String(job.chamadoId));
      if (job.replyId) fd.append('quoted_mensagem_id', String(job.replyId));
      if (job.semAssinatura) fd.append('sem_assinatura', 'true');
      await axios.post(`${API}/atendimentos/${idConversa}/mensagens`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } else {
      const body = { texto: job.texto };
      if (job.chamadoId) body.chamado_id = job.chamadoId;
      if (job.replyId) body.quoted_mensagem_id = job.replyId;
      if (job.semAssinatura) body.sem_assinatura = true;
      await axios.post(`${API}/atendimentos/${idConversa}/mensagens`, body);
    }
    // Recarrega SÓ se a pessoa ainda está nesta conversa. `carregarDetalhe`
    // põe na tela a conversa que recarregou — com a fila em segundo plano,
    // quem trocou de conversa enquanto a foto subia seria puxado de volta.
    if (conversaAtivaIdRef.current === idConversa) await carregarDetalhe(idConversa, false);
    if (job.somentePorId) removerPendentes([job.tempId]);
  };

  aoFalharEnvioRef.current = (idConversa, erro, jobs) => {
    removerPendentes(jobs.map((j) => j.tempId));
    // Devolve TUDO que não saiu, na ordem, pro campo — nada do que a pessoa
    // escreveu some. Se ela já saiu da conversa, vira rascunho de lá.
    const textos = jobs.map((j) => j.texto).filter(Boolean).join('\n');
    const anexo = jobs.find((j) => j.arquivo)?.arquivo || null;
    if (conversaAtivaIdRef.current === idConversa) {
      if (textos) setTexto((cur) => (cur ? `${textos}\n${cur}` : textos));
      if (anexo) setArquivoSelecionado((cur) => cur || anexo);
      carregarDetalhe(idConversa, false);
    } else if (textos) {
      const cur = rascunhosRef.current[idConversa] || '';
      rascunhosRef.current[idConversa] = cur ? `${textos}\n${cur}` : textos;
    }
    const soAudio = jobs.length === 1 && String(jobs[0].arquivo?.type || '').startsWith('audio/');
    const titulo = soAudio ? 'Não deu pra enviar o áudio'
      : jobs.length > 1 ? `Não deu pra enviar — ${jobs.length} mensagens voltaram pro campo`
        : 'Não deu pra enviar';
    alertarErro(erro, titulo);
  };

  aoEsvaziarEnvioRef.current = () => { carregarLista(false); };

  const enviar = (e) => {
    e?.preventDefault?.();
    const t = texto.trim();
    if ((!t && !arquivoSelecionado) || !conversaAtivaId) return;
    const idConversa = conversaAtivaId;
    const fila = filaEnvioRef.current;
    // Tudo o que decide COMO a mensagem sai é congelado agora, no toque — e
    // não quando ela chegar a vez na fila. Responder citando, desligar a
    // assinatura ou trocar de conversa depois não pode mudar o que já foi.
    const job = {
      tempId: novoIdTemporario(),
      idConversa,
      texto: t,
      arquivo: arquivoSelecionado,
      replyId: replyTo?.id || null,
      chamadoId: chamadoEmFoco || null,
      semAssinatura: !assinaturaAtiva,
      // Quem responde vira o dono: vale pra conversa sem atendente E pra de
      // outro. Só a primeira da fila precisa assumir.
      assumir: fila.tamanho(idConversa) === 0 && conversaAtiva?.atendente !== user?.usuario,
      somentePorId: Boolean(arquivoSelecionado),
    };
    mostrarPendente({
      id: job.tempId, conversaId: idConversa, direcao: 'out', tipo: 'texto',
      conteudo: job.arquivo ? [rotuloDoAnexo(job.arquivo), t].filter(Boolean).join('\n') : t,
      criada_em: new Date().toISOString(), autor: user?.usuario || 'atendente',
      status: 'enviando', somentePorId: job.somentePorId,
    });
    setTexto('');
    rascunhosRef.current[idConversa] = '';
    setArquivoSelecionado(null);
    setReplyTo(null);
    fila.enfileirar(idConversa, job);
  };

  // Tem filtro mandando na lista? (fila, canal ou atendente escolhidos)
  const temFiltroLigado = Boolean(setorFiltro) || canaisFiltro.length > 0 || atendentesFiltro.length > 0;

  const alternarSelecao = (id) => setSelecionadas((prev) => {
    const nova = new Set(prev);
    if (nova.has(id)) nova.delete(id); else nova.add(id);
    return nova;
  });
  const entrarSelecao = (id) => { setSelecionando(true); setSelecionadas(new Set(id ? [id] : [])); };
  const sairSelecao = () => { setSelecionando(false); setSelecionadas(new Set()); };
  // "Todas" = todas as que ESTÃO NA LISTA agora (a vista e os filtros valem).
  const selecionarTodas = () => setSelecionadas(new Set(conversasOrdenadas.map((c) => c.id)));

  const encerrarSelecionadas = async () => {
    if (!selecionadas.size) return;
    // Mesma regra da de uma só: sem "pedir motivo", encerrar é um clique.
    if (exigirMotivo) {
      try {
        const resModos = await axios.get(`${API}/atendimento/motivos`, { params: { ativos: 'true' } });
        setModosEncerrar(resModos.data?.data || []);
      } catch (e) { setModosEncerrar([]); }
      setEncerrarLoteAberto(true);
      return;
    }
    await confirmarEncerrarLote({});
  };

  const confirmarEncerrarLote = async (payload) => {
    const ids = [...selecionadas];
    if (!ids.length) return;
    const quantas = ids.length;
    const r = await Swal.fire({
      title: `Encerrar ${quantas} ${quantas === 1 ? 'conversa' : 'conversas'}?`,
      text: 'Se o cliente voltar a escrever, a conversa reabre sozinha.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Encerrar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#3D8B37',
    });
    if (!r.isConfirmed) return;
    setEncerrandoLote(true);
    try {
      const res = await axios.post(`${API}/atendimentos/encerrar-lote`, { ids, ...payload });
      const d = res.data?.data || {};
      setEncerrarLoteAberto(false);
      sairSelecao();
      if (ids.includes(conversaAtivaId)) setConversaAtivaId(null);
      await carregarLista(false);
      const falhas = d.falhas || [];
      if (falhas.length) {
        // Diz o que NÃO deu, em vez de fingir que encerrou tudo.
        const motivos = [...new Set(falhas.map((f) => f.motivo))].slice(0, 3);
        Swal.fire({
          icon: 'warning',
          title: `${d.encerradas} encerrada(s), ${falhas.length} não`,
          html: motivos.map((m) => `<div style="font-size:0.85rem">${m}</div>`).join(''),
        });
      } else {
        Swal.fire({
          icon: 'success',
          title: `${d.encerradas} ${d.encerradas === 1 ? 'conversa encerrada' : 'conversas encerradas'}`,
          timer: 1500,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      alertarErro(err, 'Não deu pra encerrar');
    } finally {
      setEncerrandoLote(false);
    }
  };

  const abrirEncerrar = async () => {
    if (!conversaAtivaId) return;
    // Loja que não usa motivo: encerrar é um clique só. O passo a mais em ação
    // frequente é o que faz o atendente deixar de encerrar (e a fila incha).
    if (!exigirMotivo) {
      await confirmarEncerrar({});
      return;
    }
    try {
      const resModos = await axios.get(`${API}/atendimento/motivos`, { params: { ativos: 'true' } });
      setModosEncerrar(resModos.data?.data || []);
    } catch (e) {
      setModosEncerrar([]);
    }
    setEncerrarAberto(true);
  };

  const confirmarEncerrar = async (payload) => {
    if (!conversaAtivaId) return;
    try {
      await axios.post(`${API}/atendimentos/${conversaAtivaId}/encerrar`, payload);
      setEncerrarAberto(false);
      setConversaAtivaId(null);
      await carregarLista(false);
      Swal.fire({ icon: 'success', title: 'Atendimento encerrado', timer: 1300, showConfirmButton: false });
    } catch (err) {
      alertarErro(err, 'Não deu pra encerrar');
      throw err;
    }
  };

  const assumir = async () => {
    if (!conversaAtivaId) return;
    try {
      await axios.patch(`${API}/atendimentos/${conversaAtivaId}/status`, {
        status: 'em_atendimento',
        assumir: true,
      });
      await carregarDetalhe(conversaAtivaId, false);
      await carregarLista(false);
    } catch (err) {
      alertarErro(err, 'Não deu pra encerrar');
    }
  };


  // Devolve pro Pendente do setor (solta o dono) — quem estiver livre pega.
  const devolverPendente = async () => {
    const r = await Swal.fire({
      icon: 'question', title: 'Devolver pro Pendente?',
      text: 'Solta o atendimento pra qualquer um da fila pegar.',
      showCancelButton: true, confirmButtonText: 'Devolver', cancelButtonText: 'Cancelar',
    });
    if (!r.isConfirmed) return;
    try {
      await axios.post(`${API}/atendimentos/${conversaAtivaId}/devolver`);
      await carregarLista(false); await carregarDetalhe(conversaAtivaId, false);
      Swal.fire({ icon: 'success', title: 'Devolvido pro Pendente', timer: 1200, showConfirmButton: false });
    } catch (err) { alertarErro(err, 'Não deu pra salvar'); }
  };

  // Fixar/desafixar (pin no topo) e marcar como não-lida (voltar depois).
  const fixarConversa = async () => {
    if (!conversaAtiva) return;
    const novo = !conversaAtiva.fixada;
    try {
      await axios.patch(`${API}/atendimentos/${conversaAtivaId}/fixar`, { fixada: novo });
      await carregarLista(false); await carregarDetalhe(conversaAtivaId, false);
    } catch (err) { alertarErro(err, 'Não deu pra salvar'); }
  };
  const marcarNaoLidaConversa = async () => {
    if (!conversaAtiva) return;
    try {
      await axios.patch(`${API}/atendimentos/${conversaAtivaId}/nao-lida`);
      const id = conversaAtivaId;
      setConversaAtivaId(null); // fecha pra não re-marcar como lida ao ficar aberta
      await carregarLista(false);
      Swal.fire({ icon: 'success', title: 'Marcada como não-lida', timer: 1100, showConfirmButton: false });
      void id;
    } catch (err) { alertarErro(err, 'Não deu pra marcar'); }
  };

  const ninguemAssumiu = conversaAtiva?.status !== 'encerrada' && !conversaAtiva?.atendente;
  const outroAssumiu = conversaAtiva?.status !== 'encerrada'
    && !!conversaAtiva?.atendente
    && conversaAtiva?.atendente !== user?.usuario;

  // Multicanal v2: conjuntos de canais que o usuario VE e em que pode AGIR.
  // verTodos = fallback de token antigo (sem canais_extra) com ve_todos_canais.
  const meuEscopo = (() => {
    const padrao = user?.canal_id != null ? Number(user.canal_id) : null;
    const temNovo = Array.isArray(user?.canais_extra);
    const extras = temNovo ? user.canais_extra.map(Number).filter(Boolean) : [];
    const assumir = !!user?.assumir_outros;
    // ve_todos_canais vem da permissão ti:atendimentos:todos (Gestor) e vale por
    // si: quem tem a permissão vê e atua em qualquer canal, mesmo estando
    // vinculado a um. Espelha canalScope() do backend — a versão anterior tinha
    // um `!temNovo` aqui, que anulava a permissão pra todo mundo que tinha
    // canais_extra no token (ou seja: pro gestor, sempre).
    const verTodos = !!user?.ve_todos_canais;
    const dedupe = (a) => [...new Set(a.filter((v) => v != null))];
    const canaisVer = padrao != null ? dedupe([padrao, ...extras]) : dedupe(extras);
    const canaisAtuar = padrao != null ? dedupe([padrao, ...(assumir ? extras : [])]) : [];
    return { verTodos, canaisVer, canaisAtuar };
  })();
  const filaAtiva = conversaAtiva ? Number(conversaAtiva.fila_canal_id ?? conversaAtiva.canal_id) : null;

  // Pode AGIR (assumir/responder/encerrar): fila no conjunto de atuacao. Tolerante
  // a escopo desconhecido (token/cache antigo) — backend decide com 403.
  const podeAgir = !conversaAtiva
    || meuEscopo.verTodos // gestor atua em qualquer canal (igual assertPodeAtender)
    || meuEscopo.canaisAtuar.length === 0
    || meuEscopo.canaisAtuar.includes(filaAtiva);

  // Transferir = roteamento: basta ENXERGAR a conversa (espelha o backend).
  const podeTransferir = !!conversaAtiva
    && (meuEscopo.verTodos || meuEscopo.canaisVer.length === 0 || meuEscopo.canaisVer.includes(filaAtiva));

  const abrirContatoSidebar = async () => {
    if (!conversaAtiva?.contato_id) return;
    setContatoSidebarForm({
      push_name: conversaAtiva.push_name || '',
      filial: conversaAtiva.contato_filial || '',
      setor: conversaAtiva.contato_setor || '',
    });
    setContatoSidebarErro('');
    setContatoSidebarAberta(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/atendimento/filiais`),
        // Fila pertence ao canal: oferece só as filas do canal desta conversa.
        axios.get(`${API}/setores`, { params: { ativos: 'true', canal_id: conversaAtiva?.canal_id } }),
      ]);
      setContatoSidebarFiliais(r1.data?.data || []);
      setContatoSidebarSetores(r2.data?.data || []);
    } catch { /* silencioso */ }
  };

  const salvarContatoSidebar = async () => {
    if (!conversaAtiva?.contato_id) return;
    if (!contatoSidebarForm.push_name.trim()) {
      setContatoSidebarErro('Nome é obrigatório.');
      return;
    }
    setContatoSidebarSalvando(true);
    setContatoSidebarErro('');
    try {
      await axios.put(`${API}/contatos/${conversaAtiva.contato_id}`, {
        // Agenda por canal: edita a ficha do canal DESTA conversa. O nome que
        // outro canal usa pro mesmo número não é tocado.
        canal_id: conversaAtiva.canal_id,
        push_name: contatoSidebarForm.push_name.trim(),
        filial: contatoSidebarForm.filial || null,
        setor: contatoSidebarForm.setor || null,
      });
      setContatoSidebarAberta(false);
      await carregarDetalhe(conversaAtivaId, false);
      await carregarLista(false);
    } catch (err) {
      setContatoSidebarErro(err.response?.data?.message || 'Erro ao salvar contato.');
    } finally {
      setContatoSidebarSalvando(false);
    }
  };

  // Atendentes unicos presentes nas conversas carregadas (todos os status).
  // So aparece o filtro quando ha 2+ atendentes distintos.
  const atendentesDisponiveis = useMemo(() => {
    const set = new Set();
    for (const c of conversas) {
      if (c.atendente) set.add(c.atendente);
    }
    return Array.from(set).sort();
  }, [conversas]);

  // Reset do filtro de atendente quando os atendentes saem da lista carregada.
  useEffect(() => {
    if (atendentesFiltro.some((a) => !atendentesDisponiveis.includes(a))) {
      setAtendentesFiltro([]);
    }
  }, [atendentesDisponiveis, atendentesFiltro]);

  const conversasOrdenadas = useMemo(
    () => [...conversas]
      // 2 status: 'encerrada' mostra só encerradas; qualquer outro = em aberto
      // (aberta + em atendimento). Encerrada nunca vaza pra aba "Em aberto".
      .filter((c) => (statusFiltro === 'encerrada' ? c.status === 'encerrada' : c.status !== 'encerrada'))
      .filter((c) => atendentesFiltro.length === 0 || atendentesFiltro.includes(c.atendente))
      // Fila por ID, não por nome: "Caixa" do número da matriz e "Caixa" do
      // número de Carangola são filas diferentes, e filtrar por nome misturaria
      // as duas.
      .filter((c) => {
        if (!setorFiltro) return true;
        if (setorFiltro === '__pendente__') return c.setor_id == null;
        return String(c.setor_id) === String(setorFiltro);
      })
      .sort((a, b) => {
        // Fixadas sempre no topo.
        if (Boolean(a.fixada) !== Boolean(b.fixada)) return a.fixada ? -1 : 1;
        // Quem está esperando resposta sobe pro topo; entre os que esperam, o
        // que espera há MAIS tempo vem primeiro (timestamp menor).
        const aw = a.status !== 'encerrada' && a.aguardando_desde ? new Date(a.aguardando_desde).getTime() : null;
        const bw = b.status !== 'encerrada' && b.aguardando_desde ? new Date(b.aguardando_desde).getTime() : null;
        if (aw && bw) return aw - bw;
        if (aw) return -1;
        if (bw) return 1;
        // Resto: conversa mais recente primeiro.
        const ax = a.ultima_mensagem_em ? new Date(a.ultima_mensagem_em).getTime() : 0;
        const bx = b.ultima_mensagem_em ? new Date(b.ultima_mensagem_em).getTime() : 0;
        return bx - ax;
      }),
    [conversas, statusFiltro, atendentesFiltro, setorFiltro],
  );

  // Contatos da agenda que ainda NÃO estão na lista de conversas acima — evita
  // mostrar a mesma pessoa duas vezes (uma como conversa, outra como contato).
  const contatosSemConversa = useMemo(() => {
    if (!busca.trim()) return [];
    const jaNaLista = new Set(
      conversasOrdenadas.map((c) => String(c.numero || '').replace(/\D/g, '')).filter(Boolean),
    );
    return contatosBusca.filter((ct) => {
      const n = String(ct.numero || '').replace(/\D/g, '');
      return n && !jaNaLista.has(n);
    });
  }, [contatosBusca, conversasOrdenadas, busca]);

  // Conversas que casam com a busca do encaminhar (filtro local, como já era).
  const encaminharConversas = useMemo(() => {
    const t = encaminharBusca.trim().toLowerCase();
    const lista = t
      ? conversas.filter((c) => (c.push_name || '').toLowerCase().includes(t)
        || String(c.numero || '').includes(t))
      : conversas;
    return lista.slice(0, 60);
  }, [conversas, encaminharBusca]);

  // Contatos da agenda que ainda não têm conversa — mesma dedup da Minha fila,
  // pra a mesma pessoa não aparecer nas duas seções.
  const encaminharContatosSemConversa = useMemo(() => {
    if (!encaminharBusca.trim()) return [];
    const jaNaLista = new Set(
      conversas.map((c) => String(c.numero || '').replace(/\D/g, '')).filter(Boolean),
    );
    return encaminharContatos.filter((ct) => {
      const n = String(ct.numero || '').replace(/\D/g, '');
      return n && !jaNaLista.has(n);
    });
  }, [encaminharContatos, conversas, encaminharBusca]);

  // Busca na conversa: ids das mensagens (texto) que casam, em ordem. Navega e destaca.
  const matchesBusca = useMemo(() => {
    const termo = buscaConversa.trim().toLowerCase();
    if (!termo || !conversaAtiva?.mensagens) return [];
    return conversaAtiva.mensagens
      .filter((m) => !m.apagada && String(m.conteudo || '').toLowerCase().includes(termo))
      .map((m) => m.id);
  }, [buscaConversa, conversaAtiva]);
  const irParaMatch = (idx) => {
    if (!matchesBusca.length) return;
    const i = ((idx % matchesBusca.length) + matchesBusca.length) % matchesBusca.length;
    setBuscaIdx(i);
    const node = document.getElementById(`msg-${matchesBusca[i]}`);
    if (node) node.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };
  useEffect(() => {
    if (!matchesBusca.length) return;
    setBuscaIdx(0);
    const node = document.getElementById(`msg-${matchesBusca[0]}`);
    if (node) node.scrollIntoView({ block: 'center', behavior: 'smooth' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buscaConversa]);
  useEffect(() => { setBuscaConversa(''); setBuscaConversaAberta(false); setBuscaIdx(0); }, [conversaAtivaId]);

  // Quantas conversas estão ESPERANDO resposta em cada fila (para o contador
  // nos chips de filtro). Chave = id do setor; '__pendente__' = sem fila.
  const esperandoPorFila = useMemo(() => {
    const mapa = {};
    conversas.forEach((c) => {
      if (c.status === 'encerrada' || !c.aguardando_desde) return;
      const chave = c.setor_id != null ? String(c.setor_id) : '__pendente__';
      mapa[chave] = (mapa[chave] || 0) + 1;
    });
    return mapa;
  }, [conversas]);

  // Filas presentes na lista atual (para não sumir com um filtro que está em uso
  // e para saber se há conversa sem fila = pendente-geral).
  const setoresPresentes = useMemo(() => {
    const mapa = new Map();
    conversas.forEach((c) => {
      if (c.setor_id != null && !mapa.has(String(c.setor_id))) {
        mapa.set(String(c.setor_id), { id: c.setor_id, nome: c.setor_nome, canal_nome: c.canal_nome });
      }
    });
    const temPendente = conversas.some((c) => c.setor_id == null && c.status !== 'encerrada');
    return { filas: [...mapa.values()], temPendente };
  }, [conversas]);

  // Filas que ESTE usuario pode ver: Gestor (ti:atendimentos:todos) ve todas as
  // ativas; os demais veem apenas as filas liberadas no vinculo (setores_ver).
  // Uniao com as presentes na lista, por seguranca. Fila pertence a um canal:
  // quando o mesmo nome aparece em canais diferentes, o chip mostra o canal
  // junto ("Caixa · Carangola") — sem isso viravam dois chips iguais.
  const filasVisiveis = useMemo(() => {
    const ativas = filas.filter((f) => f.ativo !== false);
    const base = temPermissao('ti:atendimentos:todos')
      ? ativas
      : (() => {
        const ids = new Set((user?.setores_ver || []).map(Number));
        return ativas.filter((f) => ids.has(Number(f.id)));
      })();
    const mapa = new Map();
    [...base, ...setoresPresentes.filas].forEach((f) => {
      if (f?.id == null) return;
      const chave = String(f.id);
      const atual = mapa.get(chave) || {};
      mapa.set(chave, {
        id: f.id,
        nome: f.nome || atual.nome,
        canal_id: f.canal_id ?? atual.canal_id ?? null,
        canal_nome: f.canal_nome || atual.canal_nome || null,
      });
    });
    const lista = [...mapa.values()];
    const repetidos = new Set(
      lista.map((f) => f.nome).filter((n, i, arr) => arr.indexOf(n) !== i),
    );
    return lista
      .map((f) => ({
        ...f,
        rotulo: repetidos.has(f.nome) && f.canal_nome ? `${f.nome} · ${f.canal_nome}` : f.nome,
      }))
      .sort((a, b) => String(a.rotulo).localeCompare(String(b.rotulo)));
  }, [filas, user, temPermissao, setoresPresentes.filas]);

  // Guarda a fila filtrada e conserta valores que não valem mais:
  //   - legado: o filtro (e a preferência) guardavam o NOME da fila — converte
  //     pro id assim que as filas carregam;
  //   - fila que saiu do meu alcance (mudou de canal, fila desativada) volta
  //     pra "Todas", senão a tela fica vazia sem explicação.
  useEffect(() => {
    if (!setorFiltro || setorFiltro === '__pendente__') return;
    if (!filas.length) return;
    if (/^\d+$/.test(setorFiltro)) {
      if (!filasVisiveis.some((f) => String(f.id) === setorFiltro)) setSetorFiltro('');
      return;
    }
    const porNome = filasVisiveis.find((f) => f.nome === setorFiltro);
    setSetorFiltro(porNome ? String(porNome.id) : '');
  }, [setorFiltro, filas.length, filasVisiveis]);

  useEffect(() => {
    try {
      localStorage.setItem(filaFiltroKey, setorFiltro);
    } catch { /* localStorage indisponivel - ignora */ }
  }, [setorFiltro, filaFiltroKey]);

  // Ações da conversa aberta — no mobile viram um menu compacto "Ações".
  const acEncerrada = conversaAtiva?.status === 'encerrada';
  // Só faz sentido "transferir de canal" se existe OUTRO canal além do atual
  // (mesma regra que a transferir() usa pra montar as opções).
  const temOutroCanal = !!conversaAtiva && canais.some((c) => Number(c.id) !== Number(conversaAtiva.fila_canal_id));
  // Consulta no ERP: liberada só por permissão (crediário/caixa). Não tem
  // recorte de canal — cliente não pertence a filial, compra em qualquer loja.
  const podeConsultarCliente = temPermissao('atendimento:consulta_cliente');
  const podeConsultarProduto = temPermissao('atendimento:consulta_produto');
  const podeVerHistorico = temPermissao('atendimento:historico_cliente');
  // Enviar orçamento exige DUAS coisas: a permissão (função "vendas", sql/102) e
  // ter entrado pelo código de vendedor do ERP — o comercial identifica o dono
  // do orçamento pelo codvend, e quem entra pelo login do portal não tem um.
  // Sem a segunda trava o botão apareceria pro gestor e só falharia no envio.
  const podeEnviarOrcamento = temPermissao('atendimento:enviar_orcamento') && Boolean(user?.vendedor);
  const podeAssumirAcao = podeAgir && !acEncerrada && conversaAtiva?.atendente !== user?.usuario;
  const podeTransferirCanalAcao = podeTransferir && !acEncerrada && temOutroCanal;
  const podeMoverFilaAcao = podeTransferir && !acEncerrada && filasVisiveis.length > 0;
  // Canal da conversa (achado uma vez pros modos abaixo E pra decidir se o
  // botão "Transferir" aparece). Sem isto, canal sem fila esconderia o botão
  // mesmo com a flag ligada.
  const canalDaConversaAtiva = conversaAtiva
    ? canais.find((c) => Number(c.id) === Number(conversaAtiva.fila_canal_id ?? conversaAtiva.canal_id))
    : null;
  const podePassarPraColega = podeTransferir && !acEncerrada
    && (Boolean(canalDaConversaAtiva?.transferencia_livre_atendente)
      ? Boolean(canalDaConversaAtiva)
      : Boolean(conversaAtiva?.setor_id) && filasVisiveis.length > 0);

  // As três formas de passar a conversa, pro <MenuTransferir>. Cada uma expõe
  // `carregarOpcoes` (o que preencher no seletor) e `confirmar` (o POST). O
  // seletor vive dentro do próprio menu — nada de popup separado.
  const modosTransferir = [
    {
      key: 'setor',
      disponivel: podeMoverFilaAcao,
      icone: 'bi-inboxes',
      titulo: 'Transferir de fila',
      descricao: 'Muda a área — Caixa, Crediário… — no mesmo número.',
      ajuda: `Fila atual: ${conversaAtiva?.setor_nome || 'Pendente'}.`,
      placeholder: null,
      rotuloConfirmar: 'Transferir',
      carregarOpcoes: async () => {
        const atualId = conversaAtiva?.setor_id != null ? Number(conversaAtiva.setor_id) : null;
        const canalDaConversa = conversaAtiva?.canal_id != null ? Number(conversaAtiva.canal_id) : null;
        const opcoes = [];
        filas.filter((f) => f.ativo !== false).forEach((f) => {
          const daOutraLoja = canalDaConversa != null && f.canal_id != null
            && Number(f.canal_id) !== canalDaConversa;
          if (daOutraLoja || Number(f.id) === atualId) return;
          opcoes.push({ value: String(f.id), label: f.nome });
        });
        opcoes.push({ value: '', label: 'Pendente (sem fila)' });
        // Só sobrou uma fila real -> já vem escolhida.
        const reais = opcoes.filter((o) => o.value !== '');
        return { opcoes, padrao: reais.length === 1 ? reais[0].value : undefined };
      },
      confirmar: async (value) => {
        await axios.patch(`${API}/atendimentos/${conversaAtivaId}/setor`, { setor_id: value ? Number(value) : null });
        await carregarLista(false);
        await carregarDetalhe(conversaAtivaId, false);
        Swal.fire({ icon: 'success', title: 'Movido de fila', timer: 1200, showConfirmButton: false });
      },
    },
    // "Passar pra colega": por padrão só entre atendentes da MESMA fila. Com
    // fio_canal.transferencia_livre_atendente ligado, o canal libera passar pra
    // qualquer atendente dele (mesmo sem fila) — a decisão é da loja.
    (() => {
      const livre = Boolean(canalDaConversaAtiva?.transferencia_livre_atendente);
      return {
        key: 'colega',
        disponivel: podePassarPraColega,
        icone: 'bi-person-up',
        titulo: 'Passar pra um colega',
        descricao: livre
          ? 'Entrega pra outra pessoa deste canal.'
          : 'Entrega pra outra pessoa da sua fila.',
        placeholder: 'Escolha o atendente',
        rotuloConfirmar: 'Passar',
        exigeValor: true,
        vazio: livre ? 'Não há outro atendente neste canal.' : 'Não há outro atendente nesta fila.',
        carregarOpcoes: async () => {
          const url = livre
            ? `${API}/canais/${canalDaConversaAtiva.id}/atendentes`
            : `${API}/setores/${conversaAtiva.setor_id}/atendentes`;
          const r = await axios.get(url);
          const ST = { disponivel: '🟢 disponível', ausente: '🟡 ausente', offline: '⚪ offline' };
          const opcoes = (r.data?.data || [])
            .filter((a) => a.login !== user?.usuario)
            .map((a) => ({ value: a.login, label: `${a.nome || a.login} — ${ST[a.status] || a.status}` }));
          return { opcoes };
        },
        confirmar: async (value) => {
          await axios.post(`${API}/atendimentos/${conversaAtivaId}/atribuir`, { atendente: value });
          await carregarLista(false);
          await carregarDetalhe(conversaAtivaId, false);
          Swal.fire({ icon: 'success', title: `Passado pra ${value}`, timer: 1200, showConfirmButton: false });
        },
      };
    })(),
    {
      key: 'canal',
      disponivel: podeTransferirCanalAcao,
      icone: 'bi-arrow-left-right',
      titulo: 'Transferir de número',
      descricao: 'Manda pra outro número/loja — ex.: um vendedor.',
      ajuda: 'As respostas continuam saindo por este número.',
      placeholder: 'Selecione o número de destino',
      rotuloConfirmar: 'Transferir',
      exigeValor: true,
      vazio: 'Não há outro número pra transferir.',
      carregarOpcoes: async () => {
        const opcoes = canais
          .filter((c) => c.id !== conversaAtiva.fila_canal_id)
          .map((c) => ({ value: String(c.id), label: c.nome }));
        return { opcoes, padrao: opcoes.length === 1 ? opcoes[0].value : undefined };
      },
      confirmar: async (value) => {
        await axios.post(`${API}/atendimentos/${conversaAtivaId}/transferir`, { fila_canal_id: Number(value) });
        await carregarLista(false);
        await carregarDetalhe(conversaAtivaId, false);
        Swal.fire({ icon: 'success', title: 'Transferido', timer: 1300, showConfirmButton: false });
      },
    },
  ];
  const acaoMenuItemStyle = {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '12px 14px', background: 'transparent', border: 'none',
    borderBottom: '1px solid var(--portal-border)', color: 'var(--portal-text)',
    fontSize: '0.9rem', textAlign: 'left', cursor: 'pointer', width: '100%',
  };

  // Rascunho por conversa: ao trocar de conversa, guarda o que estava digitado
  // e devolve o rascunho da conversa aberta (igual ao WhatsApp).
  useEffect(() => {
    const prev = prevConvRef.current;
    if (prev != null && prev !== conversaAtivaId) {
      rascunhosRef.current[prev] = texto;
    }
    if (conversaAtivaId !== prev) {
      setTexto(rascunhosRef.current[conversaAtivaId] || '');
    }
    prevConvRef.current = conversaAtivaId;
  }, [conversaAtivaId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Antecipa a intenção: ao abrir uma conversa que dá pra responder, o passo
  // óbvio é digitar — então joga o cursor direto no campo, sem precisar clicar.
  // Espera o detalhe carregar (a conversa vem async, o campo só monta depois) e
  // foca UMA vez por conversa; refoca também logo depois de "Assumir".
  // Inclui a conversa não assumida: quem responde assume automaticamente.
  // Digitar e anexar têm a MESMA regra: poder agir no canal. Conversa de outro
  // atendente ou encerrada não bloqueia — enviar assume/reabre.
  const canDigitar = podeAgir;
  const focoConversaRef = useRef(null);
  useEffect(() => {
    if (!conversaAtivaId) { focoConversaRef.current = null; return undefined; }
    if (focoConversaRef.current === conversaAtivaId) return undefined;
    // No dedo, focar sozinho ESCANCARA o teclado e come metade da tela antes de
    // ele ler qualquer mensagem. No mouse é o oposto: abrir a conversa e já
    // poder digitar economiza um clique. Então o foco automático é só do mouse.
    if (toque) return undefined;
    // só depois que o detalhe da conversa CERTA carregou (senão o campo nem existe)
    if (!conversaAtiva || conversaAtiva.id !== conversaAtivaId) return undefined;
    const t = setTimeout(() => {
      const el = textareaRef.current;
      if (el) {
        el.focus();
        // leva o cursor pro fim do texto (útil quando há rascunho salvo)
        const len = el.value.length;
        try { el.setSelectionRange(len, len); } catch (e) { /* ignore */ }
        focoConversaRef.current = conversaAtivaId;
      }
    }, 60);
    return () => clearTimeout(t);
  }, [conversaAtivaId, conversaAtiva, canDigitar, toque]);

  if (!podeUsar) {
    return (
      <div className="content-shell">
        <div className="feedback-banner feedback-banner--error">
          Você não tem permissão para acessar atendimentos.
        </div>
      </div>
    );
  }

  return (
    <div className="content-shell" style={{ height: isMobile ? (conversaAtivaId ? (areaVisivel ? `${areaVisivel}px` : '100dvh') : 'calc(100dvh - 28px - var(--fio-abas-h) - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))') : 'calc(100vh - 32px)', display: 'flex', flexDirection: 'column' }}>
      {/* No dedo o cabeçalho é o do app: título, o "+" colado nele e a
          disponibilidade na direita. Equipe/Grupos/Contatos saíram daqui — quem
          leva pra lá agora é a barra de abas do rodapé. */}
      {isMobile && !conversaAtivaId && (
      <section className="page-header cabecalho-toque">
        <div className="cabecalho-toque__titulo">
          <h1>Conversas</h1>
          <button
            type="button"
            className="cabecalho-toque__novo"
            onClick={() => setNovaConversaAberta(true)}
            title="Conversa nova"
            aria-label="Conversa nova"
          >
            <i className="bi bi-plus-lg" />
          </button>
          <InfoFluxoAtendimento />
        </div>
        {podeAtenderPresenca && (
          <button
            type="button"
            className="cabecalho-toque__presenca"
            onClick={toggleAusente}
            title={ausente
              ? 'Você está Ausente (não recebe conversa nova). Toque para ficar Disponível.'
              : 'Você está Disponível. Toque para entrar em Ausente (pausa/almoço).'}
          >
            <span
              className="cabecalho-toque__bolinha"
              style={{ background: ausente ? '#f5a623' : presenca === 'disponivel' ? '#2fb344' : '#8a8a8a' }}
            />
            {ausente ? 'Ausente' : presenca === 'disponivel' ? 'Disponível' : 'Offline'}
          </button>
        )}
      </section>
      )}

      {!isMobile && (
      <section className="page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 style={{ marginBottom: 0 }}>Atendimentos</h1>
            <InfoFluxoAtendimento />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setChatInternoAberto(true)}
            title="Chat interno da equipe (não vai pro cliente)"
            style={{ position: 'relative' }}
          >
            <i className="bi bi-people-fill me-1 icone-toque" /> Equipe
            {equipeNaoLidas > 0 && (
              <span style={{
                position: 'absolute', top: -6, right: -6, minWidth: 18, height: 18, padding: '0 5px',
                borderRadius: 9, background: '#e5484d', color: '#fff', fontSize: '0.68rem', fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
                boxShadow: '0 0 0 2px var(--portal-surface)',
              }}>
                {equipeNaoLidas > 99 ? '99+' : equipeNaoLidas}
              </span>
            )}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => navegar('/ti/grupos')}
            title="Grupos de WhatsApp do canal"
            style={{ position: 'relative' }}
          >
            <i className="bi bi-chat-square-text-fill me-1 icone-toque" /> Grupos
            {gruposNaoLidas > 0 && (
              <span style={{
                position: 'absolute', top: -6, right: -6, minWidth: 18, height: 18, padding: '0 5px',
                borderRadius: 9, background: '#e5484d', color: '#fff', fontSize: '0.68rem', fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
                boxShadow: '0 0 0 2px var(--portal-surface)',
              }}>
                {gruposNaoLidas > 99 ? '99+' : gruposNaoLidas}
              </span>
            )}
          </button>
          <a
            href="/ti/contatos"
            className="btn btn-sm btn-outline-secondary"
            title="Gerenciar contatos cadastrados"
          >
            <i className="bi bi-person-vcard me-1 icone-toque" /> Contatos
          </a>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => setNovaConversaAberta(true)}
            style={{ background: '#3D8B37', color: '#fff', border: 'none' }}
          >
            <i className="bi bi-plus-circle me-1 icone-toque" /> Nova conversa
          </button>
        </div>
      </section>
      )}

      {(!isMobile || !conversaAtivaId) && <BannerAtivarAvisos />}

      <NovaConversaModal
        aberto={novaConversaAberta}
        onClose={() => setNovaConversaAberta(false)}
        onCriada={(conversa) => {
          setNovaConversaAberta(false);
          if (conversa?.id) {
            setConversaAtivaId(conversa.id);
            carregarLista(false);
          }
        }}
      />

      <ChatInternoModal
        aberto={chatInternoAberto}
        onClose={() => { setChatInternoAberto(false); recarregarNaoLidas(); }}
        onLido={recarregarNaoLidas}
      />


      <ContatoPickerModal
        aberto={pickerContatoAberto}
        onClose={() => setPickerContatoAberto(false)}
        onEscolher={enviarContatoEscolhido}
      />

      {/* Telefone tocado dentro de uma mensagem. */}
      {numeroTocado && (
        <div
          onClick={() => setNumeroTocado(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 2050, background: 'rgba(8,23,43,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <div className="content-card" onClick={(e) => e.stopPropagation()}
            style={{ width: 'min(360px, 100%)', padding: 18 }}>
            <div style={{ fontSize: '1.05rem', fontWeight: 700 }}>
              {formatarTelefone(numeroTocado.numero)}
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--portal-muted)', marginTop: 4, minHeight: 20 }}>
              {numeroTocado.checando && 'verificando no WhatsApp…'}
              {!numeroTocado.checando && numeroTocado.existe === true
                && (numeroTocado.nome ? `tem WhatsApp — ${numeroTocado.nome}` : 'tem WhatsApp')}
              {!numeroTocado.checando && numeroTocado.existe === false && 'esse número não tem WhatsApp'}
              {!numeroTocado.checando && numeroTocado.existe === null && 'não deu pra verificar agora'}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14 }}>
              <button
                type="button"
                className="btn btn-sm"
                disabled={numeroTocado.checando || numeroTocado.existe === false}
                onClick={() => {
                  const n = numeroTocado.numero;
                  const nome = numeroTocado.nome;
                  setNumeroTocado(null);
                  conversarComContato(n, nome);
                }}
                style={{ background: '#3D8B37', color: '#fff', border: 'none' }}
              >
                <i className="bi bi-chat-dots me-1" /> Conversar
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => {
                  const n = numeroTocado.numero;
                  const nome = numeroTocado.nome;
                  setNumeroTocado(null);
                  salvarContatoNaAgenda(n, nome);
                }}
              >
                <i className="bi bi-person-plus me-1" /> Salvar na agenda
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => {
                  if (navigator.clipboard) {
                    navigator.clipboard.writeText(formatarTelefone(numeroTocado.numero)).catch(() => {});
                  }
                  setNumeroTocado(null);
                }}
              >
                <i className="bi bi-copy me-1" /> Copiar número
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recorte + compressão da foto. Cancelar NÃO anexa nada: quem desistiu
          do ajuste desistiu da foto, e deixar a original entrar pela porta dos
          fundos anularia o motivo de a tela existir. */}
      <EditorImagem
        arquivo={editandoImagem}
        onCancelar={() => setEditandoImagem(null)}
        onPronto={(pronta) => {
          setEditandoImagem(null);
          setArquivoSelecionado(pronta);
          setTimeout(() => textareaRef.current?.focus(), 50);
        }}
      />

      <AcoesMensagemMobile
        mensagem={acaoMensagem}
        ancora={acaoAncora}
        onFechar={() => { setAcaoMensagem(null); setAcaoAncora(null); }}
        onReagir={reagirMsg}
        onResponder={responderMsg}
        onEditar={editarMsgPrompt}
        onApagar={apagarMsg}
        onCopiar={copiarMsg}
        onEncaminhar={encaminharMsg}
        onFixar={fixarMsg}
        onDesafixar={desafixarMsg}
        ehFixada={!!(acaoMensagem && conversaAtiva?.mensagem_fixada_id && Number(conversaAtiva.mensagem_fixada_id) === Number(acaoMensagem.id))}
      />

      {encaminharAlvo && (
        <div onClick={() => !encaminhando && setEncaminharAlvo(null)} style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(420px, 100%)', maxHeight: '80vh', background: 'var(--portal-surface)', borderRadius: 12, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.45)' }}>
            <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--portal-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <strong><i className="bi bi-forward-fill me-2" />Encaminhar para…</strong>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setEncaminharAlvo(null)} disabled={!!encaminhando}><i className="bi bi-x-lg" /></button>
            </div>
            <div style={{ padding: 10 }}>
              <input type="search" autoFocus placeholder="Buscar conversa ou contato da agenda..." value={encaminharBusca} onChange={(e) => setEncaminharBusca(e.target.value)}
                style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--portal-border)', background: 'var(--portal-bg)', color: 'var(--portal-text)', fontSize: '0.85rem' }} />
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {/* 1) Conversas abertas — o destino mais provável. */}
              {encaminharConversas.map((c) => {
                const indo = encaminhando === `conv-${c.id}`;
                return (
                <button key={c.id} type="button" onClick={() => confirmarEncaminhar(c.id)} disabled={!!encaminhando}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '10px 14px', background: indo ? 'rgba(61,139,55,0.10)' : 'transparent', border: 'none', borderBottom: '1px solid var(--portal-border)', cursor: encaminhando ? 'default' : 'pointer', color: 'var(--portal-text)', opacity: encaminhando && !indo ? 0.45 : 1 }}>
                  <Avatar nome={c.push_name} numero={c.numero} fotoUrl={c.profile_pic_url} size={34} />
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.push_name || formatNumero(c.numero) || c.jid}
                    {c.id === conversaAtivaId && <span style={{ color: 'var(--portal-muted)', fontSize: '0.75rem' }}> (atual)</span>}
                    {indo && <span style={{ color: '#3D8B37', fontSize: '0.75rem' }}> · enviando…</span>}
                  </span>
                  {indo
                    ? <div className="screen-loader__spinner" style={{ width: 16, height: 16, borderWidth: 2, flexShrink: 0 }} />
                    : <i className="bi bi-send" style={{ color: 'var(--portal-muted)' }} />}
                </button>
                );
              })}

              {!encaminharBusca.trim() && conversas.length === 0 && (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--portal-muted)' }}>Nenhuma conversa na lista.</div>
              )}

              {/* 2) Agenda — quem ainda não tem conversa. Clicar abre a conversa
                     e encaminha na sequência, num passo só. */}
              {encaminharBusca.trim().length >= 2 && (
                <>
                  <div style={{
                    padding: '8px 14px', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em',
                    textTransform: 'uppercase', color: 'var(--portal-muted)',
                    background: 'var(--portal-bg)', borderBottom: '1px solid var(--portal-border)',
                  }}>
                    Contatos da agenda
                    {encaminharBuscandoAgenda && <span style={{ fontWeight: 400, textTransform: 'none' }}> · procurando…</span>}
                  </div>
                  {!encaminharBuscandoAgenda && encaminharContatosSemConversa.length === 0 && (
                    <div style={{ padding: '12px 14px', fontSize: '0.8rem', color: 'var(--portal-muted)' }}>
                      {encaminharConversas.length
                        ? 'Ninguém a mais na agenda com esse nome.'
                        : 'Nada encontrado nas conversas nem na agenda.'}
                    </div>
                  )}
                  {/* Contato sem WhatsApp fica visível e NÃO clicável: encaminhar
                      pra ele seria escrever à toa. */}
                  {encaminharContatosSemConversa.map((ct) => {
                    const chave = `ag-${ct.id}-${ct.canal_id}`;
                    const indo = encaminhando === chave;
                    // Esta linha demora mais que a de conversa aberta: abre a
                    // conversa e só então encaminha. É justamente onde o
                    // atendente reclicava.
                    return (
                    <button
                      key={chave}
                      type="button"
                      onClick={() => !ct.sem_whatsapp && encaminharParaContato(ct)}
                      disabled={!!ct.sem_whatsapp || !!encaminhando}
                      title={ct.sem_whatsapp ? 'Esse número não tem WhatsApp' : undefined}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '10px 14px', background: indo ? 'rgba(61,139,55,0.10)' : 'transparent', border: 'none', borderBottom: '1px solid var(--portal-border)', cursor: (ct.sem_whatsapp || encaminhando) ? 'default' : 'pointer', color: 'var(--portal-text)', opacity: ct.sem_whatsapp ? 0.55 : (encaminhando && !indo ? 0.45 : 1) }}
                    >
                      <Avatar nome={ct.push_name} numero={ct.numero} fotoUrl={ct.profile_pic_url} size={34} />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontWeight: 600, fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {ct.push_name || formatNumero(ct.numero)}
                        </span>
                        <span style={{ display: 'block', fontSize: '0.74rem', color: ct.sem_whatsapp ? '#dc3545' : (indo ? '#3D8B37' : 'var(--portal-muted)') }}>
                          {indo ? 'abrindo a conversa e enviando…' : (ct.sem_whatsapp ? 'sem WhatsApp' : formatNumero(ct.numero))}
                          {!indo && canais.length > 1 && ct.canal_id
                            ? ` · ${canais.find((k) => k.id === ct.canal_id)?.nome || ''}`
                            : ''}
                        </span>
                      </span>
                      {indo
                        ? <div className="screen-loader__spinner" style={{ width: 16, height: 16, borderWidth: 2, flexShrink: 0 }} />
                        : (
                          <i
                            className={`bi ${ct.sem_whatsapp ? 'bi-x-octagon' : 'bi-chat-dots'}`}
                            style={{ color: ct.sem_whatsapp ? '#dc3545' : '#3D8B37', flexShrink: 0 }}
                          />
                        )}
                    </button>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <ConsultaProdutoModal
        aberto={consultaProdutoAberta}
        conversaId={conversaAtiva?.id}
        onClose={() => setConsultaProdutoAberta(false)}
      />

      <EnviarOrcamentoModal
        aberto={orcamentoAberto}
        conversaId={conversaAtiva?.id}
        nomeDoCliente={conversaAtiva?.push_name || (conversaAtiva?.numero ? formatNumero(conversaAtiva.numero) : null)}
        onClose={() => setOrcamentoAberto(false)}
        onEnviado={() => { carregarDetalhe(conversaAtivaId, false); carregarLista(false); }}
      />

      <HistoricoClienteModal
        aberto={historicoAberto}
        numero={conversaAtiva?.numero}
        nomeContato={conversaAtiva?.push_name}
        onClose={() => setHistoricoAberto(false)}
      />

      <MenuTransferir
        aberto={menuTransferirAberto}
        onFechar={() => setMenuTransferirAberto(false)}
        modos={modosTransferir}
      />

      <ConsultaClienteModal
        aberto={consultaClienteAberta}
        numero={conversaAtiva?.numero}
        nomeContato={conversaAtiva?.push_name}
        conversaId={conversaAtiva?.id}
        onClose={() => setConsultaClienteAberta(false)}
      />

      <EncerrarModal
        aberto={encerrarAberto}
        modos={modosEncerrar}
        onClose={() => setEncerrarAberto(false)}
        onConfirmar={confirmarEncerrar}
      />

      {/* Mesmo modal do encerrar de uma só: o motivo é escolhido UMA vez e vale
          pra todas as marcadas (decisão do Lucas). A mensagem do motivo não vai
          pro cliente em lote — seriam N disparos de uma vez (o backend barra). */}
      <EncerrarModal
        aberto={encerrarLoteAberto}
        modos={modosEncerrar}
        onClose={() => setEncerrarLoteAberto(false)}
        onConfirmar={confirmarEncerrarLote}
      />

      <LightboxImagem url={lightboxUrl} onClose={() => setLightboxUrl(null)} />

      {erro && <div className="feedback-banner feedback-banner--error">{erro}</div>}

      <div
        style={{
          flex: 1,
          display: 'grid',
          // Em tela compacta com a ficha do contato aberta, a LISTA sai de cena
          // e a ficha toma o lugar dela. Mantendo as três colunas, um iPad em
          // pé (820) ficaria com ~150px de conversa: 280 da lista + 300 da
          // ficha + a barra lateral do app não deixam sobrar nada.
          gridTemplateColumns: isMobile
            ? '1fr'
            : (listaEscondidaPelaFicha
              ? '1fr 300px'
              : `minmax(280px, 360px) 1fr${contatoSidebarAberta ? ' 300px' : ''}`),
          gap: isMobile ? 0 : 12,
          minHeight: 0,
        }}
      >
        {/* Lista — no mobile aparece so quando nenhuma conversa esta aberta,
            e em tela compacta cede o lugar pra ficha do contato. */}
        {(!isMobile || !conversaAtivaId) && !listaEscondidaPelaFicha && (
        <aside
          className="content-card"
          style={{ display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}
        >
          <div style={{ padding: 12, borderBottom: '1px solid var(--portal-border)' }}>
            {/* Busca + ⋮. Filtros e Selecionar moraram na linha das abas e não
                cabiam: no computador a coluna é estreita e o último botão saía
                da área visível; no tablet a linha quebrava. São duas ações
                ocasionais — ficam atrás do ⋮, e as abas respiram. */}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', position: 'relative' }}>
              <input
                type="search"
                placeholder="Buscar por nome, número ou texto..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                style={{
                  flex: 1, minWidth: 0, padding: '6px 10px', borderRadius: 6,
                  border: '1px solid var(--portal-border)',
                  background: 'var(--portal-surface)', color: 'var(--portal-text)',
                  fontSize: '0.85rem',
                }}
              />
              <button
                type="button"
                className="botao-toque"
                onClick={() => setMenuListaAberto((v) => !v)}
                title="Filtros e seleção"
                style={{
                  flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: isMobile ? 40 : 32, height: isMobile ? 40 : 30,
                  borderRadius: 8, cursor: 'pointer',
                  border: '1px solid var(--portal-border)',
                  background: (filtrosAbertos || temFiltroLigado) ? 'var(--portal-surface-soft)' : 'transparent',
                  color: temFiltroLigado ? '#3D8B37' : 'var(--portal-muted)',
                  position: 'relative',
                }}
              >
                <i className="bi bi-three-dots-vertical" />
                {/* Bolinha: tem filtro ligado que a lista está obedecendo. */}
                {temFiltroLigado && (
                  <span style={{
                    position: 'absolute', top: 3, right: 3, width: 7, height: 7,
                    borderRadius: '50%', background: '#3D8B37',
                  }} />
                )}
              </button>
              {menuListaAberto && (
                <>
                  <div onClick={() => setMenuListaAberto(false)} style={{ position: 'fixed', inset: 0, zIndex: 1300 }} />
                  <div style={{
                    position: 'absolute', right: 0, top: 'calc(100% + 4px)',
                    background: 'var(--portal-surface)', border: '1px solid var(--portal-border)',
                    borderRadius: 10, boxShadow: '0 10px 28px rgba(0,0,0,0.45)',
                    zIndex: 1301, minWidth: 230, overflow: 'hidden',
                    display: 'flex', flexDirection: 'column',
                  }}>
                    <button
                      type="button"
                      style={acaoMenuItemStyle}
                      onClick={() => { setMenuListaAberto(false); setFiltrosAbertos((v) => !v); }}
                    >
                      <i className="bi bi-sliders" /> {filtrosAbertos ? 'Esconder filtros' : 'Filtros'}
                      {temFiltroLigado && <span style={{ marginLeft: 'auto', color: '#3D8B37', fontSize: '0.75rem' }}>ligado</span>}
                    </button>
                    {!selecionando && conversasOrdenadas.length > 1 && (
                      <button
                        type="button"
                        style={{ ...acaoMenuItemStyle, borderBottom: 'none' }}
                        onClick={() => { setMenuListaAberto(false); entrarSelecao(null); }}
                      >
                        <i className="bi bi-check2-square" /> Selecionar conversas
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Visões principais (sempre): Minha fila · Pendente · Encerradas.
                Filtros e Selecionar saíram daqui pro ⋮ da busca. */}
            <div style={{ display: 'flex', gap: isMobile ? 8 : 4, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {[
                { key: 'minha', l: 'Minha fila', i: 'bi-person-fill', badge: 0, t: 'Conversas atribuídas a você' },
                { key: 'pendente', l: 'Pendente', i: 'bi-inbox-fill', badge: pendenteCount, t: 'Cliente esperando resposta — pegue pra você' },
                { key: 'encerradas', l: 'Encerradas', i: 'bi-check2-all', badge: 0, t: 'Histórico: conversas já encerradas que você enxerga' },
              ].map((t) => {
                const ativa = viewAtual === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    className="botao-toque"
                    onClick={() => aplicarView(t.key)}
                    title={t.t}
                    style={{
                      // No dedo são pílulas que abraçam o texto (como no app):
                      // esticar até a borda deixava três retângulos iguais e
                      // difíceis de distinguir de relance. No mouse continua
                      // dividindo a largura da coluna, que ali é estreita.
                      flex: isMobile ? '0 0 auto' : 1,
                      position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      gap: isMobile ? 7 : 5,
                      fontSize: isMobile ? '0.85rem' : '0.74rem', fontWeight: ativa ? 700 : 500,
                      padding: isMobile ? '9px 16px' : '6px 5px',
                      borderRadius: isMobile ? 999 : 8, cursor: 'pointer',
                      border: '1px solid var(--portal-border)',
                      background: ativa ? '#3D8B37' : 'var(--portal-surface)',
                      color: ativa ? '#fff' : 'var(--portal-text)',
                    }}
                  >
                    {!isMobile && <i className={`bi ${t.i} icone-toque`} />}{t.l}
                    {t.badge > 0 && (
                      <span style={{
                        minWidth: 17, height: 17, padding: '0 5px', borderRadius: 9,
                        background: ativa ? 'rgba(255,255,255,0.3)' : '#e5484d', color: '#fff',
                        fontSize: '0.66rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      }}>{t.badge > 99 ? '99+' : t.badge}</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Ver junto à Minha fila: camadas somáveis (multi-seleção) — Por fora / Time */}
            {viewAtual === 'minha' && mostraGrupo('ver_junto') && (
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: isMobile ? '0.85rem' : '0.68rem', color: 'var(--portal-muted)' }}>
                  <i className="bi bi-layers me-1" />Ver junto:
                </span>
                <OlhoFixar
                  fixado={filtrosFixados.includes('ver_junto')}
                  aoAlternar={() => alternarFixado('ver_junto')}
                  painelAberto={filtrosAbertos}
                />
                {[
                  { on: incluirPorFora, set: setIncluirPorFora, l: 'Por fora', i: 'bi-phone-fill', badge: semDonoCount, t: 'Somar as conversas tocadas por fora (celular/WhatsApp Web) à sua fila' },
                  { on: incluirTime, set: setIncluirTime, l: 'Time', i: 'bi-people-fill', badge: 0, t: 'Somar as conversas dos colegas à sua fila' },
                ].map((t) => (
                  <button
                    key={t.l}
                    type="button"
                    onClick={() => t.set((v) => !v)}
                    title={t.t}
                    style={{
                      fontSize: isMobile ? '0.85rem' : '0.72rem', padding: isMobile ? '5px 12px' : '3px 10px', borderRadius: 12,
                      border: `1px solid ${t.on ? '#3D8B37' : 'var(--portal-border)'}`, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5,
                      background: t.on ? '#3D8B37' : 'transparent', color: t.on ? '#fff' : 'var(--portal-text)',
                    }}
                  >
                    <i className={`bi ${t.on ? 'bi-check-lg' : t.i}`} />{t.l}
                    {t.badge > 0 && (
                      <span style={{ background: t.on ? 'rgba(255,255,255,0.3)' : '#8a6d3b', color: '#fff', fontSize: '0.62rem', fontWeight: 700, borderRadius: 8, padding: '0 5px' }}>{t.badge > 99 ? '99+' : t.badge}</span>
                    )}
                  </button>
                ))}
              </div>
            )}


            {/* Filtro de fila/setor: chips derivados dos setores presentes na lista.
                A visibilidade (quais setores o usuario ve) ja e aplicada no backend;
                aqui e so um estreitamento visual (util para quem ve varios setores). */}
            {filasVisiveis.length > 0 && mostraGrupo('fila') && (
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: isMobile ? '0.85rem' : '0.68rem', color: 'var(--portal-muted)' }}>
                  <i className="bi bi-inboxes me-1" />Fila:
                </span>
                <OlhoFixar
                  fixado={filtrosFixados.includes('fila')}
                  aoAlternar={() => alternarFixado('fila')}
                  painelAberto={filtrosAbertos}
                />
                {[{ v: '', l: 'Todas' }]
                  .concat(filasVisiveis.map((f) => ({ v: String(f.id), l: f.rotulo })))
                  // "Sem fila" só aparece quando existe conversa sem fila — é o
                  // Pendente geral, e some sozinho quando o canal roteia tudo.
                  .concat(setoresPresentes.temPendente
                    ? [{ v: '__pendente__', l: 'Sem fila' }]
                    : [])
                  .map((opt) => {
                    const ativo = setorFiltro === opt.v;
                    const cnt = opt.v === ''
                      ? Object.values(esperandoPorFila).reduce((s, n) => s + n, 0)
                      : (esperandoPorFila[opt.v] || 0);
                    return (
                      <button
                        key={opt.v || 'todas'}
                        type="button"
                        onClick={() => setSetorFiltro(opt.v)}
                        style={{
                          fontSize: isMobile ? '0.85rem' : '0.7rem',
                          padding: isMobile ? '5px 12px' : '3px 10px', borderRadius: 12,
                          border: '1px solid var(--portal-border)',
                          background: ativo ? (opt.v ? '#F5A623' : '#3D8B37') : 'transparent',
                          color: ativo ? '#fff' : 'var(--portal-text)',
                          cursor: 'pointer',
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                        }}
                      >
                        {opt.l}
                        {cnt > 0 && (
                          <span
                            title={`${cnt} esperando resposta`}
                            style={{
                              background: '#E5484D', color: '#fff',
                              fontSize: '0.62rem', fontWeight: 700,
                              borderRadius: 8, padding: '0 5px', lineHeight: '1.4',
                            }}
                          >
                            {cnt}
                          </span>
                        )}
                      </button>
                    );
                  })}
              </div>
            )}

            {/* Filtro de Canal: aparece para quem ve mais de um canal, listando so
                os visiveis. Filtro de Atendente: so o papel de gestao. */}
            {(() => {
              const canaisVisiveis = meuEscopo.verTodos
                ? canais
                : canais.filter((c) => meuEscopo.canaisVer.includes(c.id));
              const mostraCanal = canaisVisiveis.length > 1;
              const mostraAtendente = temPermissao('ti:atendimentos:todos');
              if (!mostraCanal && !mostraAtendente) return null;
              if (!mostraGrupo('canal') && !mostraGrupo('atendente')) return null;
              return (
                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  {mostraCanal && (
                    <MultiSelectDropdown
                      label="Canal"
                      options={canaisVisiveis.map((c) => ({ value: c.id, label: c.nome }))}
                      selected={canaisFiltro}
                      onChange={setCanaisFiltro}
                    />
                  )}
                  {mostraAtendente && (
                    <MultiSelectDropdown
                      label="Atendente"
                      options={atendentesDisponiveis.map((a) => ({ value: a, label: a }))}
                      selected={atendentesFiltro}
                      onChange={setAtendentesFiltro}
                    />
                  )}
                </div>
              );
            })()}

            {/* TAGS — filtro novo, e o motivo de tudo isto virar personalizável:
                pra quem usa etiqueta, é o filtro mais usado do dia; pra quem não
                usa, é ruído. Cada um fixa o que precisa. */}
            {minhasTags.length > 0 && mostraGrupo('tags') && (
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: isMobile ? '0.85rem' : '0.68rem', color: 'var(--portal-muted)' }}>
                  <i className="bi bi-tags me-1" />Tag:
                </span>
                <OlhoFixar
                  fixado={filtrosFixados.includes('tags')}
                  aoAlternar={() => alternarFixado('tags')}
                  painelAberto={filtrosAbertos}
                />
                {[{ id: '', nome: 'Todas', cor: null }, ...minhasTags.filter((t) => t.ativo)].map((t) => {
                  const ativo = String(tagFiltro) === String(t.id);
                  const cor = t.cor || '#3D8B37';
                  return (
                    <button
                      key={t.id || 'todas'}
                      type="button"
                      onClick={() => setTagFiltro(ativo ? '' : String(t.id))}
                      style={{
                        fontSize: isMobile ? '0.85rem' : '0.72rem',
                        padding: isMobile ? '5px 12px' : '3px 10px', borderRadius: 12, cursor: 'pointer',
                        border: `1px solid ${ativo ? cor : 'var(--portal-border)'}`,
                        background: ativo ? cor : 'transparent',
                        color: ativo ? '#fff' : 'var(--portal-text)',
                      }}
                    >
                      {t.nome}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {!meuEscopo.verTodos && meuEscopo.canaisVer.length === 0 && (
            <div style={{ margin: '0 0 8px', padding: 10, borderRadius: 8, background: 'rgba(255,193,7,0.12)', border: '1px solid rgba(255,193,7,0.4)', fontSize: '0.82rem' }}>
              <i className="bi bi-exclamation-triangle me-2" style={{ color: '#f0ad4e' }} />
              Você não está vinculado a um canal. Fale com o administrador para atender.
            </div>
          )}

          {/* Faxina da bandeja: a barra só aparece com a seleção ligada. Pra
              LIGAR, o botão "Selecionar" fica na linha das abas (junto do
              Filtros) — linha própria só pra ele comia altura da lista. */}
          {selecionando && (
            <div style={{
              padding: isMobile ? '10px 12px' : '8px 12px',
              borderBottom: '1px solid var(--portal-border)',
              background: 'var(--portal-surface-soft)',
              display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
            }}>
              <strong style={{ fontSize: isMobile ? '0.9rem' : '0.8rem' }}>
                {selecionadas.size} {selecionadas.size === 1 ? 'selecionada' : 'selecionadas'}
              </strong>
              <button
                type="button"
                onClick={selecionarTodas}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer', color: '#3D8B37',
                  fontSize: isMobile ? '0.85rem' : '0.75rem', fontWeight: 600, padding: '4px 6px',
                }}
              >
                Todas ({conversasOrdenadas.length})
              </button>
              <button
                type="button"
                onClick={() => setSelecionadas(new Set())}
                disabled={!selecionadas.size}
                style={{
                  background: 'transparent', border: 'none',
                  cursor: selecionadas.size ? 'pointer' : 'default',
                  color: 'var(--portal-muted)', fontSize: isMobile ? '0.85rem' : '0.75rem', padding: '4px 6px',
                }}
              >
                Limpar
              </button>
              <div style={{ flex: 1 }} />
              <button
                type="button"
                onClick={encerrarSelecionadas}
                disabled={!selecionadas.size || encerrandoLote}
                className="botao-toque"
                style={{
                  background: selecionadas.size ? '#3D8B37' : 'var(--portal-border)',
                  color: '#fff', border: 'none', borderRadius: 8,
                  padding: isMobile ? '9px 14px' : '6px 12px', fontWeight: 600,
                  fontSize: isMobile ? '0.85rem' : '0.78rem',
                  cursor: selecionadas.size && !encerrandoLote ? 'pointer' : 'default',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
              >
                <i className={`bi ${encerrandoLote ? 'bi-hourglass-split' : 'bi-check2-all'}`} />
                {encerrandoLote ? 'Encerrando…' : `Encerrar${selecionadas.size ? ` (${selecionadas.size})` : ''}`}
              </button>
              <button
                type="button"
                onClick={sairSelecao}
                title="Sair da seleção"
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: 'var(--portal-muted)', fontSize: '1rem', padding: '4px 6px',
                }}
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
          )}

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loadingLista ? (
              <div style={{ padding: 16, textAlign: 'center' }}>
                <div className="screen-loader__spinner" />
              </div>
            ) : (conversasOrdenadas.length === 0 && contatosSemConversa.length === 0) ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--portal-muted)', fontSize: '0.85rem' }}>
                {busca.trim() && buscandoContatos ? 'Procurando…' : 'Nenhuma conversa encontrada.'}
              </div>
            ) : (
              conversasOrdenadas.map((c) => (
                <ConversaItem
                  key={c.id}
                  conversa={c}
                  ativa={c.id === conversaAtivaId}
                  onClick={() => setConversaAtivaId(c.id)}
                  selecionando={selecionando}
                  selecionada={selecionadas.has(c.id)}
                  onAlternarSelecao={alternarSelecao}
                  onSegurar={entrarSelecao}
                  agora={agora}
                  horarios={horarios}
                  horarioAtivo={horarioAtivo}
                  slaTtfrMap={slaTtfrMap}
                  slaTtrMap={slaTtrMap}
                />
              ))
            )}

            {/* Contatos da agenda que casam com a busca. Clicar abre uma conversa
                nova com a pessoa — o mesmo gesto do WhatsApp de procurar o nome e
                começar a falar, sem passar pelo "Nova conversa". */}
            {contatosSemConversa.length > 0 && (
              <>
                <div style={{
                  padding: '10px 14px 6px', fontSize: '0.72rem', fontWeight: 700,
                  color: 'var(--portal-muted)', textTransform: 'uppercase', letterSpacing: 0.4,
                  borderTop: conversasOrdenadas.length > 0 ? '1px solid var(--portal-border)' : 'none',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <i className="bi bi-person-lines-fill" />
                  <span style={{ flex: 1 }}>Contatos — abrir conversa nova</span>
                  {/* Nunca trunca calado: diz quantos casaram no total. */}
                  {contatosBusca[0]?.total_geral > contatosBusca.length && (
                    <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>
                      {contatosBusca.length} de {contatosBusca[0].total_geral}
                    </span>
                  )}
                </div>
                {contatosSemConversa.map((ct) => (
                  <button
                    key={`${ct.id}-${ct.canal_id}`}
                    type="button"
                    onClick={() => abrirConversaComContato(ct)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                      padding: '9px 14px', background: 'transparent', border: 'none',
                      borderBottom: '1px solid var(--portal-border)', cursor: 'pointer',
                      textAlign: 'left', color: 'var(--portal-text)',
                    }}
                  >
                    {/* Avatar já cai nas iniciais quando a foto falha — e as
                        URLs do WhatsApp expiram, então isso acontece mesmo. */}
                    <Avatar nome={ct.push_name} numero={ct.numero} fotoUrl={ct.profile_pic_url} size={34} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{
                        display: 'block', fontWeight: 600, fontSize: '0.88rem',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {ct.push_name || formatNumero(ct.numero)}
                      </span>
                      <span style={{ display: 'block', fontSize: '0.74rem', color: 'var(--portal-muted)' }}>
                        {formatNumero(ct.numero)}
                        {canais.length > 1 && ct.canal_id
                          ? ` · ${canais.find((k) => k.id === ct.canal_id)?.nome || ''}`
                          : ''}
                      </span>
                    </span>
                    <i className="bi bi-chat-dots" style={{ color: '#3D8B37', flexShrink: 0 }} />
                  </button>
                ))}

                {/* Sobrou gente de fora? Ou carrega o resto (o normal), ou —
                    quando nem o teto dá conta — avisa que só afinando a busca.
                    Nada de botão "ver mais 0", que não faria nada ao clicar. */}
                {contatosBusca[0]?.total_geral > contatosBusca.length && (
                  contatosBusca.length >= LIMITE_MAX ? (
                    <div style={{
                      padding: '10px 14px', fontSize: '0.78rem', color: 'var(--portal-muted)',
                      borderBottom: '1px solid var(--portal-border)', textAlign: 'center',
                    }}>
                      Mostrando os {LIMITE_MAX} mais parecidos de {contatosBusca[0].total_geral}.
                      Escreva mais do nome pra achar quem você quer.
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setLimiteContatos(LIMITE_MAX)}
                      disabled={buscandoContatos}
                      style={{
                        width: '100%', padding: '10px 14px', background: 'transparent',
                        border: 'none', borderBottom: '1px solid var(--portal-border)',
                        color: '#3D8B37', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer',
                      }}
                    >
                      {buscandoContatos ? 'Carregando…' : (
                        contatosBusca[0].total_geral > LIMITE_MAX
                          ? `Ver mais ${LIMITE_MAX - contatosBusca.length} (de ${contatosBusca[0].total_geral})`
                          : `Ver todos os ${contatosBusca[0].total_geral}`
                      )}
                    </button>
                  )
                )}
              </>
            )}
          </div>
        </aside>
        )}

        {/* Chat — no mobile aparece so quando tem conversa aberta (tela cheia) */}
        {(!isMobile || conversaAtivaId) && (
        <section
          className="content-card"
          style={{
            display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden',
            minHeight: 0, position: 'relative',
            ...(voltandoX > 0 ? {
              transform: `translateX(${Math.min(voltandoX, window.innerWidth)}px)`,
              opacity: Math.max(0.4, 1 - voltandoX / 400),
            } : null),
          }}
          onTouchStart={bordaInicio}
          onTouchMove={bordaMove}
          onTouchEnd={bordaFim}
          onTouchCancel={bordaFim}
          onDragEnter={aoArrastarEntrar}
          onDragOver={aoArrastarSobre}
          onDragLeave={aoArrastarSair}
          onDrop={aoSoltar}
        >
          {/* Arrastou um arquivo pra cima da conversa: solta e já anexa. */}
          {arrastando && conversaAtivaId && (
            <div style={{
              position: 'absolute', inset: 8, zIndex: 40, borderRadius: 12,
              border: `2px dashed ${podeAgir ? '#3D8B37' : '#E5484D'}`,
              background: podeAgir ? 'rgba(61, 139, 55, 0.14)' : 'rgba(229, 72, 77, 0.12)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 8, pointerEvents: 'none', backdropFilter: 'blur(1px)',
            }}>
              <i
                className={`bi ${podeAgir ? 'bi-file-earmark-arrow-up' : 'bi-slash-circle'}`}
                style={{ fontSize: '2rem', color: podeAgir ? '#3D8B37' : '#E5484D' }}
              />
              <strong style={{ fontSize: '0.95rem' }}>
                {podeAgir ? 'Solte para anexar' : 'Não dá pra anexar aqui'}
              </strong>
              <span style={{ fontSize: '0.78rem', color: 'var(--portal-muted)', textAlign: 'center', padding: '0 16px' }}>
                {podeAgir
                  ? 'Imagem, vídeo ou documento — até 25MB'
                  : 'Este atendimento é de outro canal — só quem atende nele pode enviar'}
              </span>
            </div>
          )}
          {!conversaAtivaId ? (
            <div className="empty-state" style={{ margin: 'auto' }}>
              <strong>Selecione uma conversa</strong>
              <span>Escolha uma conversa na lista para visualizar e responder.</span>
            </div>
          ) : loadingDetalhe && !conversaAtiva ? (
            <div style={{ margin: 'auto', textAlign: 'center' }}>
              <div className="screen-loader__spinner" />
              <p>Carregando conversa...</p>
            </div>
          ) : conversaAtiva ? (
            <>
              <header
                className="conversa-topo"
                style={{
                  display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 12,
                  // No mobile imersivo, o topo vai sob a status bar do iOS — o
                  // safe-area empurra o header pra baixo dela.
                  padding: isMobile ? 'calc(env(safe-area-inset-top, 0px) + 8px) 10px 8px' : '12px 16px',
                  borderBottom: '1px solid var(--portal-border)',
                  flexWrap: 'nowrap',
                }}
              >
                {isMobile && (
                  <button
                    type="button"
                    onClick={() => setConversaAtivaId(null)}
                    title="Voltar para a lista"
                    aria-label="Voltar"
                    style={{
                      background: 'transparent', border: 'none', color: 'var(--portal-text)',
                      fontSize: '1.5rem', lineHeight: 1, padding: '2px 4px', cursor: 'pointer',
                      flexShrink: 0,
                    }}
                  >
                    <i className="bi bi-arrow-left" />
                  </button>
                )}
                <div
                  role="button"
                  onClick={abrirContatoSidebar}
                  title="Editar contato"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    cursor: 'pointer', borderRadius: 8, padding: '2px 6px',
                    transition: 'background 0.15s',
                    // `flex: 1 1 auto` + `minWidth: 0` em TODA largura: é o par
                    // que deixa este bloco encolher até o ellipsis em vez de
                    // empurrar o resto do cabeçalho pra fora da tela.
                    flex: '1 1 auto', minWidth: 0,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <Avatar nome={conversaAtiva.push_name} numero={conversaAtiva.numero} fotoUrl={conversaAtiva.profile_pic_url} size={40} />
                  {/* Uma linha cada, cortando com "…". Sem isso o flex encolhe
                      este bloco até o min-content e o texto desce em coluna. */}
                  <div style={{ minWidth: 0, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                      <strong style={{
                        fontSize: '1rem',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {conversaAtiva.push_name || formatNumero(conversaAtiva.numero) || conversaAtiva.jid}
                      </strong>
                      {/* As tags COLADAS no nome, não escondidas atrás do botão.
                          O contador em cima diz que existe algo; aqui diz o quê
                          — e é o que precisa estar à vista enquanto se atende.
                          Tocar tira a marcação, sem abrir menu. */}
                      {tagsDaConversa.map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => alternarTagDaConversa(t)}
                          title={`${t.nome} — toque pra tirar`}
                          style={{
                            flexShrink: 0, border: 'none', cursor: 'pointer',
                            fontSize: '0.66rem', fontWeight: 700, padding: '2px 8px', borderRadius: 8,
                            background: t.cor || '#3D8B37', color: '#fff',
                            maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}
                        >
                          {t.nome}
                        </button>
                      ))}
                    </div>
                    {clientePresenca ? (
                      <div style={{ fontSize: '0.75rem', color: '#3D8B37', fontWeight: 600 }}>
                        {clientePresenca === 'recording' ? 'gravando áudio…' : 'digitando…'}
                      </div>
                    ) : (!isMobile && (
                      <div
                        title={`${formatNumero(conversaAtiva.numero)} · canal ${conversaAtiva.canal_nome}`
                          + `${conversaAtiva.atendente ? ` · atendente: ${conversaAtiva.atendente_nome || conversaAtiva.atendente}` : ''}`}
                        style={{
                          fontSize: '0.75rem', color: 'var(--portal-muted)',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {formatNumero(conversaAtiva.numero)} - canal {conversaAtiva.canal_nome}
                        {conversaAtiva.atendente && ` - atendente: ${conversaAtiva.atendente_nome || conversaAtiva.atendente}`}
                      </div>
                    ))}
                  </div>
                </div>
                {!isMobile && (
                  // flexShrink 0 pra não ser espremido; em tela apertada ele
                  // encolhe pelo modo `compact`, sem sumir — o SLA continua à
                  // vista no tablet.
                  <div style={{ flexShrink: 0 }}>
                    <CronometroSLA
                      chamadoCreatedAt={conversaAtiva.chamado_created_at}
                      primeiraRespostaEm={conversaAtiva.primeira_resposta_em}
                      chamadoStatus={conversaAtiva.chamado_status}
                      agora={agora}
                      horarios={horarios}
                      horarioAtivo={horarioAtivo}
                      status={conversaAtiva.status}
                      prioridade={conversaAtiva.chamado_prioridade}
                      slaTtfrMap={slaTtfrMap}
                      slaTtrMap={slaTtrMap}
                      compact={isCompacto}
                    />
                  </div>
                )}
                {/* Cabeçalho: as duas ações do dia a dia (transferir de setor e
                    encerrar) ficam a um clique; o resto no menu ⋮. No mobile só
                    o ícone, pra caber junto do nome. */}
                <div style={{ position: 'relative', marginLeft: 'auto', flexShrink: 0, display: 'flex', gap: isMobile ? 'var(--fio-gap-acoes, 4px)' : 6 }}>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setBuscaConversaAberta((v) => !v)} title="Buscar na conversa">
                    <i className="bi bi-search" />
                  </button>
                  {/* Consulta no ERP — só quem tem a permissão (crediário/caixa). */}
                  {podeConsultarCliente && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setConsultaClienteAberta(true)}
                      title="Consultar situação do cliente no Microuniverso"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      <i className="bi bi-person-vcard" />{!isCompacto && ' Consultar cliente'}
                    </button>
                  )}
                  {/* Consulta de produto — vendas, caixa e crediário (a permissão
                      vem da função da fila). */}
                  {podeConsultarProduto && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setConsultaProdutoAberta(true)}
                      title="Consultar produto: preço, estoque e foto — e mandar pro cliente"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      <i className="bi bi-box-seam" />{!isCompacto && ' Produto'}
                    </button>
                  )}
                  {/* Histórico de compras — ferramenta do vendedor. Separado do
                      "Consultar cliente" (crediário) porque o vendedor não vê
                      dívida, e o crediário não precisa deste botão. */}
                  {podeVerHistorico && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setHistoricoAberto(true)}
                      title="Últimas compras do cliente: o que levou e por quanto"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      <i className="bi bi-clock-history" />{!isCompacto && ' Histórico'}
                    </button>
                  )}
                  {/* Orçamento/cotação — ferramenta do vendedor. O número é o
                      único campo: telefone vem da conversa e a identidade de
                      quem está logado. */}
                  {podeEnviarOrcamento && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setOrcamentoAberto(true)}
                      title="Mandar o link do orçamento ou da cotação pro cliente"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      <i className="bi bi-file-earmark-text" />{!isCompacto && ' Orçamento'}
                    </button>
                  )}
                  {/* Tags: no cabeçalho, ao lado das outras ações da conversa.
                      O contador no botão faz a marcação existir mesmo fechada —
                      bater o olho e ver "2" é o que lembra de usar. */}
                  {conversaAtiva.contato_id && (
                    <div style={{ position: 'relative' }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setTagsAberto((v) => !v)}
                        title="Marcar este cliente"
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        <i className="bi bi-tags" />{!isCompacto && ' Tags'}
                        {tagsDaConversa.length > 0 && (
                          <span style={{
                            marginLeft: 5, background: '#3D8B37', color: '#fff', borderRadius: 8,
                            padding: '0 6px', fontSize: '0.68rem', fontWeight: 700,
                          }}>{tagsDaConversa.length}</span>
                        )}
                      </button>
                      {tagsAberto && (
                        <>
                          <div onClick={() => setTagsAberto(false)} style={{ position: 'fixed', inset: 0, zIndex: 39 }} />
                          <div style={{
                            position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 40,
                            width: 250, maxHeight: 300, overflowY: 'auto',
                            background: 'var(--portal-surface)', border: '1px solid var(--portal-border)',
                            borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.25)', padding: 10,
                          }}>
                            {minhasTags.length === 0 && (
                              <div style={{ fontSize: '0.8rem', color: 'var(--portal-muted)', marginBottom: 8 }}>
                                Você ainda não tem tags.
                              </div>
                            )}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              {minhasTags.filter((t) => t.ativo).map((t) => {
                                const marcada = tagsDaConversa.some((x) => x.id === t.id);
                                const cor = t.cor || '#3D8B37';
                                return (
                                  <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => alternarTagDaConversa(t)}
                                    style={{
                                      fontSize: '0.78rem', padding: '4px 10px', borderRadius: 999, cursor: 'pointer',
                                      border: `1px solid ${cor}`,
                                      background: marcada ? cor : 'transparent',
                                      color: marcada ? '#fff' : 'var(--portal-text)',
                                    }}
                                  >
                                    {marcada && <i className="bi bi-check-lg me-1" />}{t.nome}
                                  </button>
                                );
                              })}
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              style={{ marginTop: 10, width: '100%' }}
                              onClick={criarTagNaConversa}
                            >
                              <i className="bi bi-plus-lg me-1" />Nova tag
                            </button>
                            <div style={{ fontSize: '0.7rem', color: 'var(--portal-muted)', marginTop: 6 }}>
                              Suas tags. Outro atendente não vê as marcações que você faz.
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  {(podeMoverFilaAcao || podeTransferirCanalAcao || podePassarPraColega) && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setMenuTransferirAberto(true)}
                      title="Passar esta conversa: outra fila, um colega ou outro número"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      <i className="bi bi-arrow-left-right" />{!isCompacto && ' Transferir'}
                    </button>
                  )}
                  {podeAgir && conversaAtiva.status !== 'encerrada' && (
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={abrirEncerrar}
                      title="Encerrar atendimento"
                      style={{
                        whiteSpace: 'nowrap', background: '#3D8B37', color: '#fff', border: 'none',
                      }}
                    >
                      <i className="bi bi-check2-circle" />{!isCompacto && ' Encerrar'}
                    </button>
                  )}
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setAcoesMenuAberto((v) => !v)} title="Mais ações">
                    <i className="bi bi-three-dots-vertical" />
                  </button>
                  {acoesMenuAberto && (
                    <>
                      <div onClick={() => setAcoesMenuAberto(false)} style={{ position: 'fixed', inset: 0, zIndex: 1300 }} />
                      <div style={{
                        position: 'absolute', right: 0, top: 'calc(100% + 4px)',
                        background: 'var(--portal-surface)', border: '1px solid var(--portal-border)',
                        borderRadius: 10, boxShadow: '0 10px 28px rgba(0,0,0,0.45)',
                        zIndex: 1301, minWidth: 210, overflow: 'hidden',
                        display: 'flex', flexDirection: 'column',
                      }}>
                        <button type="button" style={acaoMenuItemStyle} onClick={() => { setAcoesMenuAberto(false); fixarConversa(); }}>
                          <i className={`bi ${conversaAtiva.fixada ? 'bi-pin-angle' : 'bi-pin-angle-fill'}`} /> {conversaAtiva.fixada ? 'Desafixar' : 'Fixar no topo'}
                        </button>
                        <button type="button" style={acaoMenuItemStyle} onClick={() => { setAcoesMenuAberto(false); marcarNaoLidaConversa(); }}>
                          <i className="bi bi-envelope" /> Marcar como não-lida
                        </button>
                        {podeAssumirAcao && (
                          <button type="button" style={acaoMenuItemStyle} onClick={() => { setAcoesMenuAberto(false); assumir(); }}>
                            <i className="bi bi-person-check" /> Assumir
                          </button>
                        )}
                        {podeAgir && conversaAtiva.status !== 'encerrada' && conversaAtiva.atendente && (
                          <button type="button" style={acaoMenuItemStyle} onClick={() => { setAcoesMenuAberto(false); devolverPendente(); }}>
                            <i className="bi bi-inbox" /> Devolver pro Pendente
                          </button>
                        )}
                        {/* Encerrar virou botão do cabeçalho; aqui fica só o caso
                            residual: conversa já encerrada que ainda tem chamado aberto. */}
                        {podeAgir && conversaAtiva.status === 'encerrada' && chamadosVinculados.length > 0 && (
                          <button type="button" style={{ ...acaoMenuItemStyle, borderBottom: 'none' }} onClick={() => { setAcoesMenuAberto(false); abrirEncerrar(); }}>
                            <i className="bi bi-check2-circle" /> Encerrar chamado
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Chips de chamados em aberto desse contato. Clicar define o
                    "chamado em foco" para o qual mensagens enviadas serao
                    vinculadas via siti_mensagem.chamado_id. */}
                {chamadosVinculados.length > 0 && (
                  <div style={{
                    flexBasis: '100%',
                    display: 'flex',
                    gap: 6,
                    flexWrap: 'wrap',
                    paddingTop: 8,
                    borderTop: '1px dashed var(--portal-border)',
                  }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--portal-muted)', alignSelf: 'center' }}>
                      Chamados em aberto:
                    </span>
                    {chamadosVinculados.map((c) => {
                      const ativo = c.id === chamadoEmFoco;
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => setChamadoEmFoco(ativo ? null : c.id)}
                          title={c.titulo}
                          style={{
                            background: ativo ? '#3D8B37' : 'rgba(61, 139, 55,0.12)',
                            color: ativo ? '#fff' : 'var(--portal-text)',
                            border: `1px solid ${ativo ? '#3D8B37' : 'rgba(61, 139, 55,0.45)'}`,
                            padding: '3px 10px',
                            borderRadius: 14,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            maxWidth: 220,
                          }}
                        >
                          <span>#{c.id}</span>
                          <span style={{
                            fontWeight: 400, opacity: 0.85,
                            overflow: 'hidden', textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap', maxWidth: 160,
                          }}>
                            {c.titulo}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </header>

              {conversaAtiva.mensagem_fixada_id && conversaAtiva.msgfix_id && !conversaAtiva.msgfix_apagada && (
                <div
                  onClick={() => irParaMensagem(conversaAtiva.msgfix_id)}
                  title="Ir para a mensagem fixada"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                    borderBottom: '1px solid var(--portal-border)', background: 'var(--portal-surface)',
                    cursor: 'pointer', flexShrink: 0,
                  }}
                >
                  <i className="bi bi-pin-angle-fill" style={{ color: '#F5A623', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0, borderLeft: '3px solid #F5A623', paddingLeft: 8 }}>
                    <div style={{ fontSize: '0.7rem', color: 'var(--portal-muted)', fontWeight: 600 }}>
                      Mensagem fixada
                    </div>
                    <div style={{
                      fontSize: '0.85rem', color: 'var(--portal-text)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {previewMsgFixada(conversaAtiva)}
                    </div>
                  </div>
                  <button
                    type="button"
                    title="Desafixar"
                    onClick={(e) => { e.stopPropagation(); desafixarMsg(); }}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--portal-muted)', padding: 4 }}
                  >
                    <i className="bi bi-x-lg" />
                  </button>
                </div>
              )}

              {buscaConversaAberta && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                  borderBottom: '1px solid var(--portal-border)', background: 'var(--portal-surface)', flexShrink: 0,
                }}>
                  <i className="bi bi-search" style={{ color: 'var(--portal-muted)' }} />
                  <input
                    type="search"
                    autoFocus
                    value={buscaConversa}
                    onChange={(e) => setBuscaConversa(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') irParaMatch(buscaIdx + (e.shiftKey ? -1 : 1)); }}
                    placeholder="Buscar nesta conversa..."
                    style={{
                      flex: 1, border: '1px solid var(--portal-border)', borderRadius: 6,
                      padding: '5px 9px', background: 'var(--portal-bg)', color: 'var(--portal-text)', fontSize: '0.85rem',
                    }}
                  />
                  <span style={{ fontSize: '0.75rem', color: 'var(--portal-muted)', minWidth: 52, textAlign: 'center' }}>
                    {matchesBusca.length ? `${buscaIdx + 1}/${matchesBusca.length}` : (buscaConversa.trim() ? '0/0' : '')}
                  </span>
                  <button type="button" className="btn btn-sm btn-outline-secondary" disabled={!matchesBusca.length} onClick={() => irParaMatch(buscaIdx - 1)} title="Anterior">
                    <i className="bi bi-chevron-up" />
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" disabled={!matchesBusca.length} onClick={() => irParaMatch(buscaIdx + 1)} title="Próximo">
                    <i className="bi bi-chevron-down" />
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { setBuscaConversaAberta(false); setBuscaConversa(''); }} title="Fechar">
                    <i className="bi bi-x-lg" />
                  </button>
                </div>
              )}

              <div
                ref={mensagensRef}
                style={{
                  flex: 1, overflowY: 'auto', overflowX: 'clip', padding: 16,
                  background: 'var(--portal-chat-bg)',
                }}
              >
                {(conversaAtiva.mensagens || []).length === 0 ? (
                  <div style={{ textAlign: 'center', color: 'var(--portal-muted)', fontSize: '0.85rem' }}>
                    Nenhuma mensagem ainda.
                  </div>
                ) : (
                  conversaAtiva.mensagens.map((m) => (
                    <div
                      key={m.id}
                      id={`msg-${m.id}`}
                      className={destaqueId === m.id ? 'msg-destacada' : undefined}
                      style={matchesBusca.length && m.id === matchesBusca[buscaIdx] ? { background: 'rgba(245,166,35,0.22)', borderRadius: 8, transition: 'background 0.3s' } : undefined}
                    >
                      <MessageBubble
                        mensagem={m}
                        contatoNome={conversaAtiva.push_name}
                        contatoNumero={conversaAtiva.numero}
                        onAbrirImagem={setLightboxUrl}
                        isMobile={isMobile}
                        onAbrirAcoes={(m, retangulo) => { setAcaoMensagem(m); setAcaoAncora(retangulo || null); }}
                        onConversarContato={conversarComContato}
                        onSalvarContato={salvarContatoNaAgenda}
                        nomeNaAgenda={m.tipo === 'contato' ? agendaDosCartoes[numeroDoCartao(m)] : undefined}
                        onResponder={responderMsg}
                        onReagir={reagirMsg}
                        onEditar={editarMsg}
                        onApagar={apagarMsg}
                        onEncaminhar={encaminharMsg}
                        onFixar={fixarMsg}
                        onDesafixar={desafixarMsg}
                        ehFixada={conversaAtiva.mensagem_fixada_id && Number(conversaAtiva.mensagem_fixada_id) === Number(m.id)}
                        onIrParaMensagem={irParaMensagem}
                        onTelefone={tocouNoNumero}
                      />
                    </div>
                  ))
                )}
              </div>

              <form
                onSubmit={enviar}
                className="conversa-barra"
                style={{
                  display: 'flex', flexDirection: 'column', gap: 8,
                  // No mobile, o safe-area evita que o campo fique sob o home indicator.
                  padding: isMobile ? '10px 12px calc(env(safe-area-inset-bottom, 0px) + 10px)' : 12,
                  borderTop: '1px solid var(--portal-border)',
                  background: 'var(--portal-surface)',
                }}
              >
                {/* Número sem WhatsApp: avisa NO LUGAR do campo de digitar, e não
                    depois do envio. A queixa que originou isto foi alguém
                    escrever uma cobrança inteira e só então descobrir que não
                    havia pra quem mandar. */}
                {semWhatsapp ? (
                  <div style={{
                    width: '100%', padding: '10px 14px', borderRadius: 8,
                    background: 'rgba(220,53,69,0.10)',
                    border: '1px solid rgba(220,53,69,0.4)',
                    display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.85rem',
                  }}>
                    <i className="bi bi-x-octagon-fill" style={{ color: '#dc3545', flexShrink: 0 }} />
                    <span>
                      <strong>Esse número não tem WhatsApp.</strong>{' '}
                      {formatNumero(conversaAtiva.numero)} não tem conta — não dá pra enviar
                      mensagem por aqui. Confira o número na ficha do contato ou fale por telefone.
                    </span>
                  </div>
                ) : !podeAgir ? (
                  <div style={{
                    width: '100%', padding: '10px 14px', borderRadius: 8,
                    background: 'rgba(240,173,78,0.10)',
                    border: '1px solid rgba(240,173,78,0.4)',
                    display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.85rem',
                  }}>
                    <i className="bi bi-eye" style={{ color: '#f0ad4e', flexShrink: 0 }} />
                    <span>
                      Visualização: este atendimento é do canal <strong>{conversaAtiva.canal_nome}</strong>.
                      Só atendentes desse canal podem responder ou encerrar.
                    </span>
                  </div>
                ) : (
                  <>
                    {/* Conversa de outro atendente: NÃO tira o campo. Quem pode
                        assumir pode escrever e anexar. E responder aqui NÃO rouba
                        a conversa: o backend só preenche o atendente quando ele
                        está vazio (`conversa.atendente || user`), e nem entra
                        nesse caminho com a conversa em atendimento. É o que
                        permite o gerente intervir sem sumir a conversa da fila de
                        quem está atendendo — só o botão Assumir transfere. */}
                    {outroAssumiu && (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '7px 10px', borderRadius: 8, marginBottom: 4,
                        background: 'rgba(245, 166, 35, 0.12)',
                        border: '1px solid rgba(245, 166, 35, 0.4)',
                        fontSize: '0.8rem',
                      }}>
                        <i className="bi bi-person-fill" style={{ color: '#F5A623', flexShrink: 0 }} />
                        <span style={{ flex: 1 }}>
                          Em atendimento por <strong>{conversaAtiva.atendente_nome || conversaAtiva.atendente}</strong>. Você pode
                          responder e anexar normalmente — a conversa <strong>continua com {conversaAtiva.atendente_nome || conversaAtiva.atendente}</strong>,
                          na fila dela. Só o botão ao lado transfere pra você.
                        </span>
                        <button
                          type="button"
                          onClick={assumir}
                          style={{
                            background: 'transparent', border: '1px solid #F5A623', color: '#F5A623',
                            borderRadius: 6, padding: '3px 10px', fontWeight: 600,
                            fontSize: '0.76rem', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                          }}
                          title={`Transferir esta conversa de ${conversaAtiva.atendente_nome || conversaAtiva.atendente} para você`}
                        >
                          Assumir pra mim
                        </button>
                      </div>
                    )}
                    {ninguemAssumiu && (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        fontSize: '0.75rem', color: 'var(--portal-muted)', padding: '2px 2px 0',
                      }}>
                        <i className="bi bi-info-circle" />
                        Ao responder, você assume esta conversa.
                      </div>
                    )}
                    {!assinaturaAtiva && (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '6px 10px', borderRadius: 8,
                        background: 'rgba(240,173,78,0.10)',
                        border: '1px solid rgba(240,173,78,0.4)',
                        fontSize: '0.8rem',
                      }}>
                        <i className="bi bi-person-dash-fill" style={{ color: '#c98317', flexShrink: 0 }} />
                        <span style={{ flex: 1 }}>
                          <strong>Assinatura desligada.</strong>{' '}
                          O cliente <strong>não vai ver {assinaturaComo}</strong> antes do texto neste envio.
                        </span>
                        <button
                          type="button"
                          onClick={() => salvarAssinatura(true)}
                          style={{
                            background: 'transparent', border: '1px solid #c98317', color: '#c98317',
                            borderRadius: 6, padding: '3px 10px', fontWeight: 600,
                            fontSize: '0.76rem', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                          }}
                          title="Voltar a assinar as próximas mensagens"
                        >
                          Religar
                        </button>
                      </div>
                    )}
                    {replyTo && (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '6px 10px', borderRadius: 8,
                        background: 'rgba(61, 139, 55,0.1)',
                        borderLeft: '3px solid #3D8B37',
                        fontSize: '0.82rem',
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, color: '#3D8B37', marginBottom: 1 }}>
                            <i className="bi bi-reply me-1" />
                            {replyTo.direcao === 'out'
                              ? (replyTo.autor || 'atendente')
                              : (conversaAtiva?.push_name || formatNumero(conversaAtiva?.numero) || 'cliente')}
                          </div>
                          <div style={{ opacity: 0.75, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {replyTo.conteudo ? String(replyTo.conteudo).slice(0, 100) : `[${replyTo.tipo}]`}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setReplyTo(null)}
                          style={{
                            background: 'transparent', border: 'none',
                            color: 'var(--portal-muted)', cursor: 'pointer', padding: 2, flexShrink: 0,
                          }}
                          title="Cancelar resposta"
                        >
                          <i className="bi bi-x-lg" />
                        </button>
                      </div>
                    )}
                    {arquivoSelecionado && (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '6px 10px', borderRadius: 8,
                        background: 'rgba(61, 139, 55,0.12)',
                        border: '1px solid rgba(61, 139, 55,0.4)',
                        fontSize: '0.85rem',
                      }}>
                        <i className="bi bi-paperclip" style={{ color: '#3D8B37' }} />
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {arquivoSelecionado.name}
                        </span>
                        <span style={{ color: 'var(--portal-muted)', fontSize: '0.78rem' }}>
                          {(arquivoSelecionado.size / 1024).toFixed(0)} KB
                        </span>
                        <button
                          type="button"
                          onClick={removerArquivo}
                          style={{
                            background: 'transparent', border: 'none',
                            color: 'var(--portal-muted)', cursor: 'pointer', padding: 2,
                          }}
                          title="Remover anexo"
                        >
                          <i className="bi bi-x-lg" />
                        </button>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        ref={inputArquivoRef}
                        type="file"
                        style={{ display: 'none' }}
                        onChange={escolherArquivo}
                      />
                      {/* `capture="environment"` abre a câmera TRASEIRA direto,
                          sem passar pela galeria — é o que faz o toque virar
                          foto em um passo. Cai no mesmo `escolherArquivo`, então
                          a foto passa pelo editor (recorte e compressão) como
                          qualquer outra. */}
                      <input
                        ref={inputCameraRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        style={{ display: 'none' }}
                        onChange={escolherArquivo}
                      />
                      {/* Documento. O clipe do lado NÃO tem `accept` — o que
                          deveria significar "qualquer arquivo" —, mas no
                          celular o seletor do sistema resolve esse "qualquer"
                          abrindo as FOTOS, e no tablet do balcão não aparecia
                          jeito de achar um PDF.
                          `accept` com os tipos escritos é o que faz o Android
                          abrir o seletor de ARQUIVOS em vez do de fotos. Por
                          isso é uma entrada separada, e não um ajuste no clipe:
                          são dois seletores diferentes do sistema, e o WhatsApp
                          separa pelo mesmo motivo. */}
                      <input
                        ref={inputDocumentoRef}
                        type="file"
                        accept={ACEITA_DOCUMENTO}
                        style={{ display: 'none' }}
                        onChange={escolherArquivo}
                      />
                      {gravando ? (
                        <>
                          <button
                            type="button"
                            onClick={cancelarGravacao}
                            title="Cancelar gravação"
                            style={{
                              background: 'transparent',
                              border: '1px solid var(--portal-border)',
                              color: 'var(--portal-text)',
                              borderRadius: 8, padding: '0 12px', cursor: 'pointer',
                            }}
                          >
                            <i className="bi bi-x-lg" />
                          </button>
                          <div style={{
                            flex: 1, display: 'flex', alignItems: 'center', gap: 10,
                            padding: '0 14px', borderRadius: 8, minHeight: 38,
                            background: 'rgba(231,76,60,0.08)',
                            border: '1px solid rgba(231,76,60,0.4)',
                          }}>
                            <span style={{
                              width: 9, height: 9, borderRadius: '50%',
                              background: '#e74c3c', flexShrink: 0,
                              animation: 'gravacaoPulse 1s ease-in-out infinite',
                            }} />
                            <span style={{ color: '#e74c3c', fontWeight: 600, fontSize: '0.88rem' }}>Gravando</span>
                            <span style={{ fontSize: '0.88rem', opacity: 0.85 }}>{fmtTempoGravacao(tempoGravacao)}</span>
                          </div>
                          <button
                            type="button"
                            onClick={pararEEnviarGravacao}
                            style={{
                              background: 'linear-gradient(135deg, #3D8B37 0%, #2E6A29 100%)',
                              color: '#fff', border: 'none', borderRadius: 8,
                              padding: '0 18px', fontWeight: 600, cursor: 'pointer',
                            }}
                          >
                            <><i className="bi bi-send-fill me-1" />Enviar</>
                          </button>
                        </>
                      ) : (
                        <>
                          <div ref={anexoMenuRef} style={{ position: 'relative', flexShrink: 0 }}>
                            <button
                              type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => setAnexoMenu((v) => !v)}
                              title="Enviar contato ou localização"
                              style={{
                                background: 'transparent', border: '1px solid var(--portal-border)',
                                color: 'var(--portal-text)', borderRadius: 8, padding: '0 12px',
                                cursor: 'pointer', height: 'var(--fio-acao, 38px)',
                              }}
                            >
                              <i className={`bi ${anexoMenu ? 'bi-x-lg' : 'bi-plus-lg'}`} />
                            </button>
                            {anexoMenu && (
                              <div style={{
                                position: 'absolute', bottom: 46, left: 0, zIndex: 30, minWidth: 180,
                                background: 'var(--portal-surface)', border: '1px solid var(--portal-border)',
                                borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.2)', overflow: 'hidden',
                              }}>
                                <button
                                  type="button"
                                  onClick={() => { setAnexoMenu(false); inputDocumentoRef.current?.click(); }}
                                  style={anexoItemStyle}
                                >
                                  <i className="bi bi-file-earmark-text-fill" style={{ color: '#3D8B37', width: 20 }} /> Documento
                                </button>
                                <button type="button" onClick={anexarContato} style={anexoItemStyle}>
                                  <i className="bi bi-person-fill" style={{ color: '#3D8B37', width: 20 }} /> Contato
                                </button>
                                <button type="button" onClick={anexarLocalizacao} style={anexoItemStyle}>
                                  <i className="bi bi-geo-alt-fill" style={{ color: '#3D8B37', width: 20 }} /> Localização
                                </button>
                                {/* A assinatura morava na barra, ocupando um dos
                                    poucos lugares que o polegar alcança — pra
                                    algo que se mexe uma vez a cada tanto. Aqui
                                    dentro ela continua a um toque de distância,
                                    e quando está DESLIGADA o aviso em cima da
                                    barra segue dizendo, com o botão de religar:
                                    o estado não se perde por estar num menu. */}
                                <button
                                  type="button"
                                  onClick={() => { salvarAssinatura(!assinaturaAtiva); setAnexoMenu(false); }}
                                  style={{ ...anexoItemStyle, borderTop: '1px solid var(--portal-border)' }}
                                >
                                  <i
                                    className={`bi ${assinaturaAtiva ? 'bi-person-dash-fill' : 'bi-person-check-fill'}`}
                                    style={{ color: assinaturaAtiva ? '#c98317' : '#3D8B37', width: 20 }}
                                  />
                                  {assinaturaAtiva ? 'Desligar assinatura' : 'Religar assinatura'}
                                </button>
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => inputArquivoRef.current?.click()}
                            title="Anexar arquivo (max 25MB)"
                            style={{
                              background: 'transparent',
                              border: '1px solid var(--portal-border)',
                              color: 'var(--portal-text)',
                              borderRadius: 8, padding: '0 12px', cursor: 'pointer',
                            }}
                          >
                            <i className="bi bi-paperclip" />
                          </button>
                          {/* Câmera, no lugar que era da assinatura. Fotografar
                              o produto na prateleira é o que mais se faz num
                              tablet de balcão; desligar a assinatura, não.
                              Quem está no caminho da mão tem que ser o de todo
                              dia.

                              Só no dedo: `capture` é ignorado no computador, e
                              lá o botão viraria um segundo clipe. */}
                          {isMobile && (
                            <button
                              type="button"
                              onClick={() => inputCameraRef.current?.click()}
                              title="Tirar foto"
                              style={{
                                background: 'transparent',
                                border: '1px solid var(--portal-border)',
                                color: 'var(--portal-text)',
                                borderRadius: 8, padding: '0 12px', cursor: 'pointer',
                                height: 'var(--fio-acao, 38px)',
                              }}
                            >
                              <i className="bi bi-camera-fill" />
                            </button>
                          )}
                          <RespostasRapidas
                            value={texto}
                            inputRef={textareaRef}
                            variaveis={{ nomeCliente: conversaAtiva?.push_name, atendente: user?.nome || user?.usuario }}
                            onInserir={(pronto) => {
                              setTexto((cur) => {
                                const ehTrigger = /^\/\S*$/.test(cur || '');
                                if (!pronto) return ehTrigger ? '' : cur; // Esc limpa o gatilho
                                if (!cur || ehTrigger) return pronto;
                                return `${cur.trimEnd()} ${pronto}`;
                              });
                              setTimeout(() => textareaRef.current?.focus(), 30);
                            }}
                          />
                          <textarea
                            ref={textareaRef}
                            value={texto}
                            onChange={(e) => { setTexto(e.target.value); sinalizarDigitando(); }}
                            placeholder={arquivoSelecionado ? 'Legenda (opcional)...' : ''}
                            rows={1}
                            onPaste={aoColar}
                            onKeyDown={(e) => {
                              // Preferência do usuário: Enter envia ou quebra linha.
                              if (prefs.enterEnvia && e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                enviar(e);
                              }
                            }}
                            style={{
                              flex: 1, resize: 'none', minHeight: 'var(--fio-acao, 38px)', overflowY: 'auto',
                              padding: '8px 12px', borderRadius: 8,
                              border: '1px solid var(--portal-border)',
                              background: 'var(--portal-surface)', color: 'var(--portal-text)',
                              fontSize: '0.9rem',
                            }}
                          />
                          {(texto.trim() || arquivoSelecionado) ? (
                            // Tem conteúdo -> botão de ENVIAR (igual WhatsApp).
                            //
                            // `onMouseDown` segura o foco NO CAMPO. Sem isso, tocar
                            // aqui passava o foco pro botão e o Android fechava o
                            // teclado a cada envio — e o botão ainda some na hora
                            // (vira o do microfone quando o campo esvazia), então o
                            // foco ia pro nada. Pedido do Gabriel: mandar várias
                            // seguidas sem o teclado descer e subir entre elas.
                            // Não força foco depois: teclado fechado continua fechado.
                            <button
                              type="submit"
                              onMouseDown={(e) => e.preventDefault()}
                              title="Enviar"
                              aria-label="Enviar"
                              style={{
                                background: 'linear-gradient(135deg, #3D8B37 0%, #2E6A29 100%)',
                                color: '#fff', border: 'none', borderRadius: '50%',
                                width: 'var(--fio-acao-redondo, 42px)', height: 'var(--fio-acao-redondo, 42px)', minWidth: 'var(--fio-acao-redondo, 42px)', flexShrink: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '1.05rem', cursor: 'pointer',
                              }}
                            >
                              <i className="bi bi-send-fill" />
                            </button>
                          ) : (
                            // Campo vazio -> botão de GRAVAR ÁUDIO no mesmo lugar.
                            <button
                              type="button"
                              onClick={iniciarGravacao}
                              title="Gravar áudio"
                              aria-label="Gravar áudio"
                              style={{
                                background: 'linear-gradient(135deg, #3D8B37 0%, #2E6A29 100%)',
                                color: '#fff', border: 'none', borderRadius: '50%',
                                width: 'var(--fio-acao-redondo, 42px)', height: 'var(--fio-acao-redondo, 42px)', minWidth: 'var(--fio-acao-redondo, 42px)', flexShrink: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '1.05rem', cursor: 'pointer',
                              }}
                            >
                              <i className="bi bi-mic-fill" />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </>
                )}
              </form>
            </>
          ) : (
            <div className="empty-state" style={{ margin: 'auto' }}>
              <strong>Conversa não disponível</strong>
              <span>Selecione outra na lista.</span>
            </div>
          )}
        </section>
        )}

        {/* Sidebar de edição de contato — no mobile vira overlay tela cheia */}
        {contatoSidebarAberta && conversaAtiva && (
          <aside
            className="content-card"
            style={isMobile
              ? { position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden', borderRadius: 0 }
              : { display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}
          >
            <header style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 14px', borderBottom: '1px solid var(--portal-border)',
              flexShrink: 0,
            }}>
              <strong style={{ fontSize: '0.9rem' }}>
                <i className="bi bi-person-lines-fill me-2" />
                Editar contato
              </strong>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setContatoSidebarAberta(false)}
                disabled={contatoSidebarSalvando}
              >
                <i className="bi bi-x-lg" />
              </button>
            </header>

            <div style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Foto do contato (expandida) — clique abre em tela cheia */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <div
                  role={conversaAtiva.profile_pic_url ? 'button' : undefined}
                  onClick={conversaAtiva.profile_pic_url ? () => setLightboxUrl(conversaAtiva.profile_pic_url) : undefined}
                  title={conversaAtiva.profile_pic_url ? 'Ver em tela cheia' : undefined}
                  style={{
                    width: 104, height: 104, borderRadius: '50%', overflow: 'hidden',
                    background: 'linear-gradient(135deg, #3D8B37 0%, #2E6A29 100%)',
                    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: '2rem', flexShrink: 0,
                    cursor: conversaAtiva.profile_pic_url ? 'zoom-in' : 'default',
                  }}
                >
                  {conversaAtiva.profile_pic_url ? (
                    <img src={conversaAtiva.profile_pic_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : iniciaisDe(conversaAtiva.push_name, conversaAtiva.numero)}
                </div>
                {conversaAtiva.profile_pic_url ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setLightboxUrl(conversaAtiva.profile_pic_url)}
                  >
                    <i className="bi bi-arrows-fullscreen me-1" /> Ver foto em tela cheia
                  </button>
                ) : (
                  <span style={{ fontSize: '0.75rem', color: 'var(--portal-muted)' }}>Sem foto no WhatsApp</span>
                )}
              </div>

              {contatoSidebarErro && (
                <div className="feedback-banner feedback-banner--error" style={{ fontSize: '0.82rem' }}>
                  {contatoSidebarErro}
                </div>
              )}

              <div>
                <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)', display: 'block', marginBottom: 4 }}>
                  Nome <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={contatoSidebarForm.push_name}
                  onChange={(e) => setContatoSidebarForm((f) => ({ ...f, push_name: e.target.value }))}
                  placeholder="Como o contato deve aparecer"
                  autoFocus
                />
              </div>

              <div>
                <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)', display: 'block', marginBottom: 4 }}>
                  Filial (opcional)
                </label>
                <select
                  className="form-select form-select-sm"
                  value={contatoSidebarForm.filial}
                  onChange={(e) => setContatoSidebarForm((f) => ({ ...f, filial: e.target.value }))}
                >
                  <option value="">Não informar</option>
                  {contatoSidebarForm.filial && !contatoSidebarFiliais.some((f) => f.nome === contatoSidebarForm.filial) && (
                    <option value={contatoSidebarForm.filial}>{contatoSidebarForm.filial}</option>
                  )}
                  {contatoSidebarFiliais.map((f) => (
                    <option key={f.codigo || f.nome} value={f.nome}>
                      {f.codigo ? `${f.codigo} - ${f.nome}` : f.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)', display: 'block', marginBottom: 4 }}>
                  Setor (opcional)
                </label>
                <select
                  className="form-select form-select-sm"
                  value={contatoSidebarForm.setor}
                  onChange={(e) => setContatoSidebarForm((f) => ({ ...f, setor: e.target.value }))}
                >
                  <option value="">Não informar</option>
                  {contatoSidebarForm.setor && !contatoSidebarSetores.some((s) => s.nome === contatoSidebarForm.setor) && (
                    <option value={contatoSidebarForm.setor}>{contatoSidebarForm.setor}</option>
                  )}
                  {contatoSidebarSetores.map((s) => (
                    <option key={s.id || s.nome} value={s.nome}>{s.nome}</option>
                  ))}
                </select>
              </div>

              <div style={{ fontSize: '0.75rem', color: 'var(--portal-muted)' }}>
                <i className="bi bi-telephone me-1" />
                {formatNumero(conversaAtiva.numero)}
                <span style={{ margin: '0 6px' }}>·</span>
                <i className="bi bi-whatsapp me-1" />
                {conversaAtiva.canal_nome}
              </div>
            </div>

            <footer style={{
              padding: '10px 14px', borderTop: '1px solid var(--portal-border)',
              display: 'flex', gap: 8, justifyContent: 'flex-end', flexShrink: 0,
            }}>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setContatoSidebarAberta(false)}
                disabled={contatoSidebarSalvando}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-sm"
                onClick={salvarContatoSidebar}
                disabled={contatoSidebarSalvando}
                style={{ background: '#3D8B37', color: '#fff', border: 'none' }}
              >
                <i className="bi bi-check2 me-1" />
                {contatoSidebarSalvando ? 'Salvando...' : 'Salvar'}
              </button>
            </footer>
          </aside>
        )}
      </div>
    </div>
  );
}
